import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { Card, Input, Button, message, Table, Modal, Spin, Select, InputNumber, DatePicker, Tag, Descriptions, Tooltip, Space, Typography, Divider, Radio, Switch, Tabs, Alert, Row, Col, AutoComplete } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import adminAxios from './adminAxios';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import LockInspector from './Management/LockInspector';
import ManifestInspector from './Management/ManifestInspector';
import { formatDuration, formatLockDateTime } from './Management/utils';
import type {
  Transaction,
  TranslationLockDetails,
  BookOption,
  BookSearchResult,
  NextExpiringBatch,
  SubscriptionCreditBatch,
  SubscriptionBatchExpiryRecomputeAllResult,
  SubscriptionBatchSummary,
  UserInfo,
  CreditSnapshot,
  SubscriptionPlanDetails,
  WebhookPlanRecord,
  TabKey,
  SupportEventRecord,
  LockExpireResponse,
  ProcessingStateBackfillResult,
  PendingTransactionLogEntry,
  PauseStatusSummary,
} from './Management/types';
import './AdminManagementTab.css';

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;
const API_BASE = resolveFrontendApiBase();
const ON_BREAK_PRODUCT_ID = 'prod_UPpK4JaS2kr0NM';
const ON_BREAK_PRICE_ID = 'price_1TUWTvEj6HHcTsCPlloksVKd';

interface ManagementTabProps {
  usersSharedState: any;
  mode?: ManagementMode;
  allowedTabs?: TabKey[];
  defaultTab?: TabKey;
  hideTabsWhenSingle?: boolean;
  operationPanels?: Partial<OperationPanelVisibility>;
  forcedPretranslationModelId?: string;
  forcedTestProofreadModel1Id?: string;
  forcedTestProofreadModel2Id?: string;
  forcedTestProofreadModel3Id?: string;
  forcedTranslationTenseGuardModelId?: string;
  forcedProofreadChangeAuditModelId?: string;
}

type ManagementMode = 'admin' | 'support';

type OperationPanelVisibility = {
  adminTools: boolean;
  lockSupport: boolean;
  lockInspector: boolean;
  manifestInspector: boolean;
  aiModelRoles: boolean;
};

type UserDetailTabKey = 'overview' | 'credits' | 'holds' | 'referrals';

type AIModel = {
  id: string;
  displayName?: string;
  provider?: string;
  isActive?: boolean;
  testingOnly?: boolean;
  inputCostPerMTokens?: number;
  outputCostPerMTokens?: number;
};

type SimpleAIModelSummary = Pick<
  AIModel,
  'id' | 'displayName' | 'provider' | 'isActive' | 'testingOnly' | 'inputCostPerMTokens' | 'outputCostPerMTokens'
>;

interface AILogCostInfo {
  inputCostPerMTokens?: number;
  outputCostPerMTokens?: number;
  currency?: string;
  actualCostUsd?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  reasoningTokens?: number;
  cachedTokens?: number;
}

interface AILogReplacement {
  from?: string;
  to?: string;
}

interface AILogEntry {
  timestamp?: string;
  stage: string;
  provider?: string;
  model?: string;
  status?: string;
  bookId?: string;
  chapterGuid?: string;
  chunkId?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  task?: string;
  snippet?: string;
  reply?: string;
  replacements: AILogReplacement[];
  length?: number;
  cost?: AILogCostInfo;
  tokenCounts?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    reasoningTokens?: number;
    cachedTokens?: number;
  };
}

interface AIModelUsageRole {
  id: string;
  description: string;
}

interface AIModelUsageResponse {
  assignments: Record<string, string>;
  translationCanary?: TranslationCanarySettings;
  metadata?: {
    updatedAt?: string;
    updatedBy?: string;
  };
  roles: AIModelUsageRole[];
}

interface TranslationCanarySettings {
  modelId?: string | null;
  percentage: number;
  cohortVersion: string;
}

interface UncategorizedUsageBackfillSample {
  doc_id?: string;
  provider?: string;
  model?: string;
  previous_usage_category?: string;
  suggested_usage_category?: string;
  process?: string;
  subprocess?: string;
  call_type?: string;
}

interface UncategorizedUsageBackfillResponse {
  window?: {
    start?: string;
    end?: string;
  };
  filters?: {
    provider?: string;
    modality?: string;
    model?: string;
    limit?: number;
    dry_run?: boolean;
  };
  scanned?: number;
  uncategorized_candidates?: number;
  reclassified?: number;
  unchanged?: number;
  applied?: number;
  errors?: number;
  hit_limit?: boolean;
  samples?: UncategorizedUsageBackfillSample[];
}

type ManagementTabDefinition = {
  key: TabKey;
  label: string;
  modes: ManagementMode[];
  requiresAdmin?: boolean;
};

const HOLD_POSITIVE_STATUSES = new Set(['released', 'expired']);
const TERMINAL_LOCK_STATUSES = new Set([
  'completed',
  'failed',
  'error',
  'expired',
  'interrupted',
  'released',
  'cancelled',
  'canceled',
  'aborted',
]);
const isTerminalLockStatus = (status?: string | null) =>
  status ? TERMINAL_LOCK_STATUSES.has(status.toLowerCase()) : false;

const sentenceCase = (value?: string) => {
  if (!value) return '';
  const normalized = value.replace(/_/g, ' ').trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatHoldTimestamp = (iso?: string) => {
  if (!iso) return null;
  return dayjs(iso).format('MMM D, YYYY h:mm A');
};

const formatEpochSeconds = (value?: number | null) => {
  if (value === null || value === undefined || value <= 0) {
    return '—';
  }
  const parsed = dayjs.unix(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : '—';
};

const formatOptionalNumber = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return value.toLocaleString();
};

const normalizeNullableString = (value?: string | null): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (lower === 'none' || lower === 'null' || lower === 'undefined') {
    return null;
  }
  return trimmed;
};

const isOnBreakIdentifier = (value?: string | null): boolean => {
  const normalized = normalizeNullableString(value);
  return normalized === ON_BREAK_PRODUCT_ID || normalized === ON_BREAK_PRICE_ID || normalized === 'on_break';
};

const isOnBreakPlanDetails = (plan?: SubscriptionPlanDetails | null): boolean => {
  if (!plan) {
    return false;
  }
  const metadata = plan.metadata && typeof plan.metadata === 'object' ? plan.metadata : {};
  return Boolean(
    metadata.isBreakPlan ||
      isOnBreakIdentifier((plan as any).id) ||
      isOnBreakIdentifier((plan as any).planKey) ||
      isOnBreakIdentifier(plan.stripePriceID) ||
      isOnBreakIdentifier(plan.stripeProductID)
  );
};

const formatStoredPlanName = (
  storedPlanName: string | undefined,
  resolvedPlan: SubscriptionPlanDetails | null
): string => {
  const normalized = normalizeNullableString(storedPlanName);
  if (normalized && normalized.toLowerCase() !== 'unknown') {
    return normalized;
  }
  return resolvedPlan?.planName || '—';
};

const formatSubscriptionInterval = (billingInterval?: string, intervalMonths?: number): string => {
  const interval = (billingInterval || '').toLowerCase();
  if (interval === 'monthly' || interval === 'month') {
    return 'Monthly';
  }
  if (interval === 'annual' || interval === 'yearly' || interval === 'year') {
    return 'Annual';
  }
  if (typeof intervalMonths === 'number' && intervalMonths > 0) {
    if (intervalMonths === 1) {
      return 'Monthly';
    }
    if (intervalMonths === 12) {
      return 'Annual';
    }
    return `Every ${intervalMonths} months`;
  }
  return '—';
};

const formatSubscriptionPrice = (price?: number, currency?: string): string => {
  if (typeof price !== 'number' || Number.isNaN(price)) {
    return '—';
  }
  const normalizedCurrency = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  } catch (error) {
    return `${normalizedCurrency} ${price.toFixed(2)}`;
  }
};

const formatSubscriptionLevel = (plan: SubscriptionPlanDetails | null): string => {
  if (!plan) {
    return '—';
  }
  const parts: string[] = [];
  if (plan.isLegacy) {
    parts.push('Legacy');
  }
  const intervalLabel = formatSubscriptionInterval(plan.billingInterval, plan.intervalMonths);
  if (intervalLabel !== '—') {
    parts.push(intervalLabel);
  }
  return parts.length ? parts.join(' · ') : '—';
};

const formatCreditsPerInterval = (plan: SubscriptionPlanDetails | null): string => {
  if (!plan || typeof plan.creditsPerInterval !== 'number' || Number.isNaN(plan.creditsPerInterval)) {
    return '—';
  }
  const intervalLabel = formatSubscriptionInterval(plan.billingInterval, plan.intervalMonths);
  if (intervalLabel === 'Monthly') {
    return `${plan.creditsPerInterval.toLocaleString()} / month`;
  }
  if (intervalLabel === 'Annual') {
    return `${plan.creditsPerInterval.toLocaleString()} / year`;
  }
  return `${plan.creditsPerInterval.toLocaleString()} / interval`;
};

const formatRolloverPeriod = (plan: SubscriptionPlanDetails | null): string => {
  if (!plan?.metadata || typeof plan.metadata !== 'object') {
    return '—';
  }
  const rawRollover = plan.metadata.rolloverDays;
  const rolloverDays =
    typeof rawRollover === 'number'
      ? rawRollover
      : Number(rawRollover ?? NaN);
  if (!Number.isFinite(rolloverDays) || rolloverDays <= 0) {
    return '—';
  }
  return `${rolloverDays.toLocaleString()} days`;
};

const formatPauseStatusLabel = (pauseStatus?: PauseStatusSummary | null) => {
  if (!pauseStatus || pauseStatus.isPaused === null || pauseStatus.isPaused === undefined) {
    return '—';
  }
  return pauseStatus.isPaused ? 'Paused' : 'Active';
};

const formatPauseLengthLabel = (pauseStatus?: PauseStatusSummary | null) => {
  if (!pauseStatus) {
    return '—';
  }
  const start = pauseStatus.pauseStartedAt;
  const end = pauseStatus.pauseResumesAt;
  if (typeof start === 'number' && typeof end === 'number' && end >= start) {
    return formatDuration(end - start);
  }
  if (typeof pauseStatus.pauseRequestedDurationDays === 'number') {
    return `${pauseStatus.pauseRequestedDurationDays} day${pauseStatus.pauseRequestedDurationDays === 1 ? '' : 's'}`;
  }
  if (typeof pauseStatus.pauseRequestedSkipCount === 'number') {
    return `${pauseStatus.pauseRequestedSkipCount} renewal${pauseStatus.pauseRequestedSkipCount === 1 ? '' : 's'}`;
  }
  return '—';
};

const buildHoldTooltip = (tx: Transaction) => {
  if (tx.type !== 'hold') return undefined;
  const bits: string[] = [];
  if (tx.hold_release_reason) {
    bits.push(`Reason: ${tx.hold_release_reason}`);
  }
  const timings: string[] = [];
  const releasedAt = formatHoldTimestamp(tx.hold_released_at);
  const committedAt = formatHoldTimestamp(tx.hold_committed_at);
  const expiredAt = formatHoldTimestamp(tx.hold_expired_at);
  const expiresAt = formatHoldTimestamp(tx.hold_expires_at);
  if (releasedAt) timings.push(`Released: ${releasedAt}`);
  if (committedAt) timings.push(`Committed: ${committedAt}`);
  if (expiredAt) timings.push(`Expired: ${expiredAt}`);
  if (!releasedAt && !committedAt && expiresAt) timings.push(`Expires: ${expiresAt}`);
  if (timings.length) {
    bits.push(timings.join(' • '));
  }
  return bits.length ? bits.join(' | ') : undefined;
};

const getCreditDeltaDisplay = (record: Transaction) => {
  const baseCredits = Number(record.credits ?? 0);
  if (!Number.isFinite(baseCredits)) {
    return { value: 0, color: 'inherit' };
  }
  if (record.type === 'deduction') {
    return { value: -Math.abs(baseCredits), color: 'red' };
  }
  if (record.type === 'hold') {
    const status = (record.hold_status || 'held').toLowerCase();
    if (HOLD_POSITIVE_STATUSES.has(status)) {
      return { value: Math.abs(baseCredits), color: '#52c41a' };
    }
    return { value: -Math.abs(baseCredits), color: '#faad14' };
  }
  if (record.type === 'refund') {
    return { value: Math.abs(baseCredits), color: '#52c41a' };
  }
  return { value: baseCredits >= 0 ? Math.abs(baseCredits) : baseCredits, color: baseCredits >= 0 ? '#52c41a' : 'red' };
};

const getTransactionTypeLabel = (record: Transaction): string => {
  if (record.type === 'hold') {
    const statusLabel = sentenceCase(record.hold_status || 'held');
    return statusLabel ? `Hold · ${statusLabel}` : 'Hold';
  }
  if (record.type === 'hold_release') {
    return 'Hold Release';
  }
  return sentenceCase(record.type) || 'Other';
};

const getDeductionSourceBreakdown = (
  record: Transaction
): { label: string; monthly: number; forever: number; rollover: number; unknown: boolean; isDeduction: boolean } => {
  if (record.type !== 'deduction') {
    return { label: '—', monthly: 0, forever: 0, rollover: 0, unknown: false, isDeduction: false };
  }

  const hasRawSourceValues =
    record.deducted_monthly !== undefined ||
    record.deducted_forever !== undefined ||
    record.deducted_rollover !== undefined;

  if (record.deduction_source_unknown || !hasRawSourceValues) {
    return { label: 'Unknown', monthly: 0, forever: 0, rollover: 0, unknown: true, isDeduction: true };
  }

  const monthly = Math.max(0, Number(record.deducted_monthly ?? 0));
  const forever = Math.max(0, Number(record.deducted_forever ?? 0));
  const rollover = Math.max(0, Number(record.deducted_rollover ?? 0));

  if (monthly === 0 && forever === 0 && rollover === 0) {
    return { label: 'None', monthly: 0, forever: 0, rollover: 0, unknown: false, isDeduction: true };
  }

  const labels: string[] = [];
  if (monthly > 0) labels.push('Monthly');
  if (forever > 0) labels.push('Forever');
  if (rollover > 0) labels.push('Rollover');

  return {
    label: labels.join(' + '),
    monthly,
    forever,
    rollover,
    unknown: false,
    isDeduction: true,
  };
};

const getDeductionSourceCsvFields = (record: Transaction): { label: string; monthly: string; forever: string; rollover: string } => {
  const breakdown = getDeductionSourceBreakdown(record);
  if (!breakdown.isDeduction || breakdown.unknown) {
    return {
      label: breakdown.label,
      monthly: '',
      forever: '',
      rollover: '',
    };
  }
  return {
    label: breakdown.label,
    monthly: String(breakdown.monthly),
    forever: String(breakdown.forever),
    rollover: String(breakdown.rollover),
  };
};

const renderDeductionSourceCell = (record: Transaction) => {
  const breakdown = getDeductionSourceBreakdown(record);
  if (breakdown.label === '—' || breakdown.label === 'Unknown' || breakdown.label === 'None') {
    return breakdown.label;
  }

  const parts: string[] = [];
  if (breakdown.monthly > 0) parts.push(`M: ${breakdown.monthly.toLocaleString()}`);
  if (breakdown.forever > 0) parts.push(`F: ${breakdown.forever.toLocaleString()}`);
  if (breakdown.rollover > 0) parts.push(`R: ${breakdown.rollover.toLocaleString()}`);
  const compact = parts.join(' • ');
  const charged = Math.max(0, Number(record.credits ?? 0)).toLocaleString();

  return (
    <Tooltip
      title={
        <div>
          <div>Monthly: {breakdown.monthly.toLocaleString()}</div>
          <div>Forever: {breakdown.forever.toLocaleString()}</div>
          <div>Rollover: {breakdown.rollover.toLocaleString()}</div>
          <div>Total Charged: {charged}</div>
        </div>
      }
    >
      <div>
        <div>{breakdown.label}</div>
        {compact ? (
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {compact}
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

const normalizeNextExpiringBatch = (raw: any): NextExpiringBatch | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const expiresAtValue =
    typeof raw.expiresAt === 'number'
      ? raw.expiresAt
      : typeof raw.expiresAt === 'string'
      ? Number(raw.expiresAt)
      : typeof raw.expires_at === 'number'
      ? raw.expires_at
      : null;
  const remainingValue =
    typeof raw.remainingCredits === 'number'
      ? raw.remainingCredits
      : typeof raw.remaining_credits === 'number'
      ? raw.remaining_credits
      : Number(raw.remainingCredits ?? raw.remaining_credits ?? 0);
  return {
    batchId: raw.batchId ?? raw.batch_id ?? null,
    remainingCredits: Number.isFinite(remainingValue) ? remainingValue : 0,
    expiresAt: Number.isFinite(expiresAtValue) ? Number(expiresAtValue) : null,
    expiresAtISO: raw.expiresAtISO ?? raw.expires_at_iso ?? null,
    planId: raw.planId ?? raw.plan_id ?? null,
    source: raw.source ?? null,
    invoiceId: raw.invoiceId ?? raw.invoice_id ?? null,
    subscriptionId: raw.subscriptionId ?? raw.subscription_id ?? null,
  };
};
const sanitizeReferralCodeInput = (value: string): string =>
  value
    ? value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 15)
    : '';

const getTimestampValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object') {
    if (typeof value._seconds === 'number') return value._seconds * 1000;
    if (typeof value.seconds === 'number') return value.seconds * 1000;
  }
  return 0;
};

const compareStrings = (a: any, b: any): number =>
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' });

const MANAGEMENT_TAB_DEFINITIONS: ManagementTabDefinition[] = [
  { key: 'user', label: 'User Management', modes: ['admin', 'support'] },
  { key: 'content', label: 'Content Tools', modes: ['admin', 'support'] },
  { key: 'operations', label: 'Operations', modes: ['admin', 'support'] },
  { key: 'monitoring', label: 'Monitoring', modes: ['admin'], requiresAdmin: true },
  { key: 'ai-logs', label: 'AI Logs', modes: ['admin'], requiresAdmin: true },
];
const PRETRANSLATION_ROLE_ID = 'dialogue_normalizer.pretranslation_en_fr';
const TEST_PROOFREAD_ROLE_1 = 'proofreader.test_model_1';
const TEST_PROOFREAD_ROLE_2 = 'proofreader.test_model_2';
const TEST_PROOFREAD_ROLE_3 = 'proofreader.test_model_3';
const PROOFREAD_CHANGE_AUDIT_ROLE_ID = 'proofreader.change_audit';
const TRANSLATION_TENSE_GUARD_ROLE_ID = 'translation.tense_guard';
const TRANSLATION_TENSE_GUARD_CONFIRMATION_ROLE_ID = 'translation.tense_guard_confirmation';
const FRENCH_TONE_DETECTION_ROLE_ID = 'french_tone_detection.ai_detect';
const TRANSLATION_QUALITY_ECONOMY_ROLE_ID = 'translation_quality.economy';
const TRANSLATION_QUALITY_ESCALATION_ROLE_ID = 'translation_quality.escalation';
const CHARACTER_ANALYSIS_RELATIONSHIPS_ROLE_ID = 'character_analysis.relationships';
const TERM_SCAN_ROLE_IDS = new Set(['autotermscanai', 'autotermsscanaifallback']);

type AIModelRoleSection = {
  key: string;
  title: string;
  description: string;
  roles: AIModelUsageRole[];
};

const AI_ROLE_LABEL_OVERRIDES: Record<string, string> = {
  'validation.untranslated_primary': 'Validator: untranslated/source mismatch (primary)',
  'validation.untranslated_fallback': 'Validator: untranslated/source mismatch (fallback)',
  'validation.tense_check': 'Validator: tense consistency check',
  'translation.preferred': 'Translation: recommended setup',
  'translation.tense_guard': 'Translation: chunk tense guard',
  'translation.tense_guard_confirmation': 'Translation: tense guard confirmation',
  'translation.rejection_fallback': 'Translation: rejection fallback #1',
  'translation.rejection_fallback_2': 'Translation: rejection fallback #2',
  'translation.rejection_fallback_3': 'Translation: rejection fallback #3',
  'translation_quality.economy': 'Quality routing: economy reviewer',
  'translation_quality.escalation': 'Quality routing: escalation reviewer',
  'dialogue_normalizer.pretranslation_en_fr':
    'Pre-translation dialogue normalizer (English structural prep)',
  'dialogue_normalizer.pretranslation_en_fr_fallback':
    'Pre-translation dialogue normalizer fallback',
  'dialogue_normalizer.french': 'Post-translation French dialogue normalizer',
  'dialogue_normalizer.french_fallback': 'Post-translation French dialogue normalizer fallback',
  'proofreader.formality': 'Formality pass',
  'proofreader.general': 'General proofreader (limited mechanical cleanup)',
  'proofreader.friction_detector': 'Friction detector (label native-reader friction spans)',
  'proofreader.friction_rewriter': 'Friction rewriter (fix flagged friction spans)',
  'proofreader.change_audit': 'Change audit (keep/remove proposal review)',
  'proofreader.alignment': 'Final alignment checker',
  'proofreader.alignment_fallback': 'Final alignment checker fallback',
  'proofreader.alignment_repair': 'French→English localized alignment repair',
  'proofreader.test_model_1': 'Experimental post-translation test model #1',
  'proofreader.test_model_2': 'Experimental post-translation test model #2',
  'proofreader.test_model_3': 'Experimental post-translation test model #3',
  'marketing.inspiration_extractor': 'Marketing: inspiration extractor',
  'metadata.options': 'Metadata title/series options',
  'metadata.options_fallback': 'Metadata title/series options fallback',
  'metadata.autofill': 'Metadata autofill',
  'french_tone_detection.ai_detect': 'French narrative tone AI Detect',
  'character_analysis.relationships': 'Character and relationship scan',
  autotermscanai: 'Term scan primary',
  autotermsscanaifallback: 'Term scan fallback',
};

const AI_ROLE_SECTION_ORDER: Array<{
  key: string;
  title: string;
  description: string;
  matcher: (roleId: string) => boolean;
}> = [
  {
    key: 'translation',
    title: 'Translation',
    description: 'Main translation model selection and rejection fallbacks.',
    matcher: (roleId) => roleId.startsWith('translation.'),
  },
  {
    key: 'translation_quality',
    title: 'Translation Quality Routing',
    description:
      'Candidate economy and escalation models used by observe-mode cost projections. Observe mode does not change live translation output.',
    matcher: (roleId) => roleId.startsWith('translation_quality.'),
  },
  {
    key: 'dialogue',
    title: 'Dialogue Structure',
    description: 'Pre/post dialogue structure normalization roles.',
    matcher: (roleId) => roleId.startsWith('dialogue_normalizer.'),
  },
  {
    key: 'proofreading',
    title: 'Proofreading and Friction',
    description:
      'Formality, general cleanup, friction detector/rewriter, alignment checks, and experimental test-model cycle slots.',
    matcher: (roleId) => roleId.startsWith('proofreader.'),
  },
  {
    key: 'validation',
    title: 'Validation',
    description: 'Validation and untranslated-content checks.',
    matcher: (roleId) => roleId.startsWith('validation.'),
  },
  {
    key: 'marketing',
    title: 'Marketing',
    description: 'Marketing-specific extraction and inspiration-generation roles.',
    matcher: (roleId) => roleId.startsWith('marketing.'),
  },
  {
    key: 'character_analysis',
    title: 'Character Analysis',
    description:
      'Primary model used to identify characters, aliases, genders, and relationships. The default is ChatGPT-6 Luna.',
    matcher: (roleId) => roleId.startsWith('character_analysis.'),
  },
  {
    key: 'term_scan',
    title: 'Term Scan',
    description: 'Automatic terminology scan roles.',
    matcher: (roleId) => TERM_SCAN_ROLE_IDS.has(roleId) || roleId.startsWith('autotermscan'),
  },
  {
    key: 'metadata',
    title: 'Metadata',
    description: 'Localized title and series option generation.',
    matcher: (roleId) => roleId.startsWith('metadata.'),
  },
  {
    key: 'french_tone_detection',
    title: 'French Tone Detection',
    description: 'AI Detect model for French narrative tense and style recommendations.',
    matcher: (roleId) => roleId.startsWith('french_tone_detection.'),
  },
];

const getAiRoleDisplayLabel = (role: AIModelUsageRole): string =>
  AI_ROLE_LABEL_OVERRIDES[role.id] || role.description || role.id;

const ManagementTab: React.FC<ManagementTabProps> = ({
  usersSharedState,
  mode = 'admin',
  allowedTabs,
  defaultTab,
  hideTabsWhenSingle = false,
  operationPanels,
  forcedPretranslationModelId,
  forcedTestProofreadModel1Id,
  forcedTestProofreadModel2Id,
  forcedTestProofreadModel3Id,
  forcedTranslationTenseGuardModelId,
  forcedProofreadChangeAuditModelId,
}) => {
  const [bookGuidInput, setBookGuidInput] = useState('');
  const [formatRepairBookGuidInput, setFormatRepairBookGuidInput] = useState('');
  const [formatRepairDryRun, setFormatRepairDryRun] = useState(false);
  const [formatRepairResult, setFormatRepairResult] = useState<any | null>(null);
  const [copySeriesData, setCopySeriesData] = useState(true);
  const adminUserId = usersSharedState?.userID as string | undefined;
  const isAdmin =
    Array.isArray(usersSharedState?.roles) && usersSharedState.roles.includes('admin');
  const [email, setEmail] = useState('');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [books, setBooks] = useState<any[]>([]);
  const [isBookModalVisible, setIsBookModalVisible] = useState(false);
  const [isTransactionsModalVisible, setIsTransactionsModalVisible] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [referralAdminInfo, setReferralAdminInfo] = useState<any | null>(null);
  const [referralAdminLoading, setReferralAdminLoading] = useState(false);
  const [referralAdminError, setReferralAdminError] = useState<string | null>(null);
  const [referralActionLoading, setReferralActionLoading] = useState(false);
  const [creditsToAdd, setCreditsToAdd] = useState<number | null>(null);
  const [creditReason, setCreditReason] = useState('');
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [userIdSearch, setUserIdSearch] = useState('');
  const [affiliateCodeSearch, setAffiliateCodeSearch] = useState('');
  const [stripeCustomerIdSearch, setStripeCustomerIdSearch] = useState('');
  const [referralCodeSearchResult, setReferralCodeSearchResult] = useState<any | null>(null);
  const [referralCodeSearchLoading, setReferralCodeSearchLoading] = useState(false);
  const [referralCodeSearchError, setReferralCodeSearchError] = useState<string | null>(null);
  const [creditAction, setCreditAction] = useState<'add' | 'deduct'>('add');
  const foreverCreditReasonOptions = useMemo(
    () => [
      { value: 'Customer Support' },
      { value: 'Restore credits after failed job' },
      { value: 'Promotion' },
      { value: 'Manual reconciliation' },
      { value: 'Legacy Balance' },
    ],
    []
  );
  const [seriesFilter, setSeriesFilter] = useState('');
  const [bookNameFilter, setBookNameFilter] = useState('');
  const [filteredBooks, setFilteredBooks] = useState<any[]>([]);
  const [bookTitleSearchTerm, setBookTitleSearchTerm] = useState('');
  const [bookTitleSearchResults, setBookTitleSearchResults] = useState<BookSearchResult[]>([]);
  const [bookTitleSearchLoading, setBookTitleSearchLoading] = useState(false);
  const titleSearchRequestId = useRef(0);
  // Admin: verify email state
  const [verifyEmailInput, setVerifyEmailInput] = useState('');
  const [verifyEmailLoading, setVerifyEmailLoading] = useState(false);
  const [userDetailTab, setUserDetailTab] = useState<UserDetailTabKey>('overview');
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [deleteUserConfirmId, setDeleteUserConfirmId] = useState('');
  const [deleteUserConfirmEmail, setDeleteUserConfirmEmail] = useState('');
  const [deleteUserSubmitting, setDeleteUserSubmitting] = useState(false);
  const [deleteUserPreview, setDeleteUserPreview] = useState<any | null>(null);
  const [deleteUserPreviewLoading, setDeleteUserPreviewLoading] = useState(false);

  const [isCoverMigrationModalVisible, setIsCoverMigrationModalVisible] = useState(false);
  const [coverMigrationLoading, setCoverMigrationLoading] = useState(false);
  const [coverMigrationResult, setCoverMigrationResult] = useState<{ processed: number; changed: number; docs: Array<{ id: string; set_keys: string[]; delete_keys: string[] }> } | null>(null);
  // Backfill results state
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState<any | null>(null);
  const [usageBackfillLoading, setUsageBackfillLoading] = useState(false);
  const [, setUsageBackfillResult] = useState<any | null>(null);
  const [subscriptionExpiryRecomputeLoading, setSubscriptionExpiryRecomputeLoading] = useState<boolean>(false);
  const [subscriptionExpiryRecomputeResult, setSubscriptionExpiryRecomputeResult] =
    useState<SubscriptionBatchExpiryRecomputeAllResult | null>(null);
  const [subscriptionExpiryRecomputeLimit, setSubscriptionExpiryRecomputeLimit] = useState<number>(1000);
  const [subscriptionExpiryRecomputeCursor, setSubscriptionExpiryRecomputeCursor] = useState<string>('');
  const [lockStateBackfillLimit, setLockStateBackfillLimit] = useState<number>(500);
  const [lockStateBackfillLoading, setLockStateBackfillLoading] = useState<boolean>(false);
  const [lockStateBackfillResult, setLockStateBackfillResult] = useState<ProcessingStateBackfillResult | null>(null);
  const [uncategorizedUsageDays, setUncategorizedUsageDays] = useState<number>(365);
  const [uncategorizedUsageLimit, setUncategorizedUsageLimit] = useState<number>(20000);
  const [uncategorizedUsageProvider, setUncategorizedUsageProvider] = useState<string>('');
  const [uncategorizedUsageModel, setUncategorizedUsageModel] = useState<string>('');
  const [uncategorizedUsageBackfillLoading, setUncategorizedUsageBackfillLoading] = useState<boolean>(false);
  const [uncategorizedUsageBackfillResult, setUncategorizedUsageBackfillResult] =
    useState<UncategorizedUsageBackfillResponse | null>(null);
  const [transactionQueueUserId, setTransactionQueueUserId] = useState('');
  const [transactionQueueEntries, setTransactionQueueEntries] = useState<PendingTransactionLogEntry[]>([]);
  const [transactionQueueLoading, setTransactionQueueLoading] = useState(false);
  const [transactionQueueFlushing, setTransactionQueueFlushing] = useState(false);
  const [transactionQueueError, setTransactionQueueError] = useState<string | null>(null);
  const [transactionQueueSelectedIds, setTransactionQueueSelectedIds] = useState<string[]>([]);
  const [transactionQueueLimit, setTransactionQueueLimit] = useState<number>(25);
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab || allowedTabs?.[0] || 'user');
  const allowedTabSet = useMemo(
    () => (allowedTabs?.length ? new Set<TabKey>(allowedTabs) : null),
    [allowedTabs]
  );
  const availableTabs = useMemo(
    () =>
      MANAGEMENT_TAB_DEFINITIONS.filter(
        (tab) =>
          (!allowedTabSet || allowedTabSet.has(tab.key)) &&
          tab.modes.includes(mode) &&
          (!tab.requiresAdmin || isAdmin)
      ),
    [allowedTabSet, isAdmin, mode]
  );
  const availableTabKeys = useMemo(
    () => new Set(availableTabs.map((tab) => tab.key)),
    [availableTabs]
  );
  const [aiLogs, setAiLogs] = useState<AILogEntry[]>([]);
  const [aiLogsResultCount, setAiLogsResultCount] = useState(0);
  const [aiLogsLoading, setAiLogsLoading] = useState(false);
  const [aiLogsError, setAiLogsError] = useState<string | null>(null);
  const [aiLogsUnavailable, setAiLogsUnavailable] = useState(false);
  const [aiLogStage, setAiLogStage] = useState<'validator' | 'proofreader'>('validator');
  const [aiLogBookId, setAiLogBookId] = useState('');
  const [aiLogChapterGuid, setAiLogChapterGuid] = useState('');
  const [aiLogModelId, setAiLogModelId] = useState<string | undefined>(undefined);
  const [aiLogLimit, setAiLogLimit] = useState<number>(50);
  const [aiLogSince, setAiLogSince] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!availableTabKeys.has(activeTab)) {
      setActiveTab(defaultTab && availableTabKeys.has(defaultTab) ? defaultTab : availableTabs[0]?.key ?? 'user');
    }
  }, [activeTab, availableTabKeys, availableTabs, defaultTab]);
  const visibleOperationPanels = useMemo<OperationPanelVisibility>(() => {
    const defaults: OperationPanelVisibility = {
      adminTools: isAdmin && mode === 'admin',
      lockSupport: true,
      lockInspector: isAdmin && mode === 'admin',
      manifestInspector: isAdmin && mode === 'admin',
      aiModelRoles: isAdmin && mode === 'admin',
    };
    return { ...defaults, ...operationPanels };
  }, [isAdmin, mode, operationPanels]);
  const [aiModelUsageLoading, setAiModelUsageLoading] = useState(false);
  const [aiModelUsageSaving, setAiModelUsageSaving] = useState(false);
  const [aiModelAssignments, setAiModelAssignments] = useState<Record<string, string>>({});
  const [aiModelUsageMetadata, setAiModelUsageMetadata] = useState<{ updatedAt?: string; updatedBy?: string } | null>(null);
  const [aiModelUsageRoles, setAiModelUsageRoles] = useState<AIModelUsageRole[]>([]);
  const [aiModelUsageError, setAiModelUsageError] = useState<string | null>(null);
  const [aiModelUsageInitialized, setAiModelUsageInitialized] = useState(false);
  const [aiModelUsageDirty, setAiModelUsageDirty] = useState(false);
  const [aiModelOptions, setAiModelOptions] = useState<SimpleAIModelSummary[]>([]);
  const [translationCanary, setTranslationCanary] = useState<TranslationCanarySettings>({
    modelId: null,
    percentage: 0,
    cohortVersion: 'v1',
  });
  
  const [lockLookupCode, setLockLookupCode] = useState('');
  const [lockLookupLoading, setLockLookupLoading] = useState(false);
  const [lockLookupResult, setLockLookupResult] = useState<TranslationLockDetails | null>(null);
  const [lockLookupError, setLockLookupError] = useState<string | null>(null);
  const [lockExpireModalVisible, setLockExpireModalVisible] = useState(false);
  const [lockExpireTarget, setLockExpireTarget] = useState<TranslationLockDetails | null>(null);
  const [lockExpireReason, setLockExpireReason] = useState('Expired via admin dashboard');
  const [lockExpireSubmitting, setLockExpireSubmitting] = useState(false);
  const [supportLookupCode, setSupportLookupCode] = useState('');
  const [supportLookupLoading, setSupportLookupLoading] = useState(false);
  const [supportLookupResult, setSupportLookupResult] = useState<SupportEventRecord | null>(null);
  const [supportLookupError, setSupportLookupError] = useState<string | null>(null);
  const [isCopyingAccountRules, setIsCopyingAccountRules] = useState(false);

  // Transaction filtering
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [bookFilter, setBookFilter] = useState<string | undefined>();
  const [languageFilter, setLanguageFilter] = useState<string | undefined>();
  const [descriptionFilter, setDescriptionFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [bookOptions, setBookOptions] = useState<BookOption[]>([]);
  const [languageOptions, setLanguageOptions] = useState<string[]>([]);
  const [descriptionOptions, setDescriptionOptions] = useState<string[]>([]);
  // Credit snapshot + holds state
  const [creditStats, setCreditStats] = useState<CreditSnapshot | null>(null);
  const [nextExpiringBatch, setNextExpiringBatch] = useState<NextExpiringBatch | null>(null);
  const [subscriptionPlanDetails, setSubscriptionPlanDetails] = useState<SubscriptionPlanDetails | null>(null);
  const [subscriptionPlanLoading, setSubscriptionPlanLoading] = useState<boolean>(false);
  const [subscriptionPlanError, setSubscriptionPlanError] = useState<string | null>(null);
  const [subscriptionBatches, setSubscriptionBatches] = useState<SubscriptionCreditBatch[]>([]);
  const [expiredSubscriptionBatches, setExpiredSubscriptionBatches] = useState<SubscriptionCreditBatch[]>([]);
  const [subscriptionBatchSummary, setSubscriptionBatchSummary] = useState<SubscriptionBatchSummary>({ total_active_credits: 0, total_granted_credits: 0, total_expired_credits: 0 });
  const [subscriptionBatchesLoading, setSubscriptionBatchesLoading] = useState<boolean>(false);
  const [selectedBatch, setSelectedBatch] = useState<SubscriptionCreditBatch | null>(null);
  const [isBatchAdjustmentModalVisible, setIsBatchAdjustmentModalVisible] = useState<boolean>(false);
  const [batchAdjustmentAction, setBatchAdjustmentAction] = useState<'add' | 'subtract'>('add');
  const [batchAdjustmentAmount, setBatchAdjustmentAmount] = useState<number | null>(null);
  const [batchAdjustmentReason, setBatchAdjustmentReason] = useState<string>('');
  const [batchAdjustmentNewExpiry, setBatchAdjustmentNewExpiry] = useState<Dayjs | null>(null);
  const [batchOperationLoading, setBatchOperationLoading] = useState<boolean>(false);
  const [isCreateBatchModalVisible, setIsCreateBatchModalVisible] = useState<boolean>(false);
  const [newBatchAmount, setNewBatchAmount] = useState<number | null>(null);
  const [newBatchExpiresAt, setNewBatchExpiresAt] = useState<Dayjs | null>(null);
  const [newBatchReason, setNewBatchReason] = useState<string>('');
  const [newBatchPlanId, setNewBatchPlanId] = useState<string>('');
  const [newBatchSource, setNewBatchSource] = useState<string>('admin_manual');
  const [holds, setHolds] = useState<any[]>([]);
  const [holdsLoading, setHoldsLoading] = useState(false);
  const [holdsStatusFilter, setHoldsStatusFilter] = useState<string | undefined>(undefined);
  const [holdsPage, setHoldsPage] = useState<number>(1);
  const [holdsPageSize, setHoldsPageSize] = useState<number>(10);
  const releasableStatuses = useMemo(
    () => new Set(['held', 'partial_committed', 'partial_released']),
    []
  );
  const [docsExportLoading, setDocsExportLoading] = useState(false);
  const hasReleasableHolds = useMemo(
    () =>
      holds.some(
        (hold) =>
          hold &&
          releasableStatuses.has(hold.status) &&
          Number(hold.remaining_amount ?? hold.remainingAmount ?? 0) > 0
      ),
    [holds, releasableStatuses]
  );
  const aiModelOptionSet = useMemo(() => new Set(aiModelOptions.map((model) => model.id)), [aiModelOptions]);
  const aiModelSelectOptions = useMemo(() => {
    const formatPrice = (value?: number) => {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
      }
      if (value >= 1) {
        return value.toFixed(2);
      }
      if (value >= 0.01) {
        return value.toFixed(3);
      }
      return value.toFixed(4);
    };
    const options = aiModelOptions.map((model) => {
      const display = model.displayName || model.id || '';
      const baseLabel = `${display} (${model.provider || 'unknown'})`;
      const pricingBits: string[] = [];
      const inputCost = formatPrice(model.inputCostPerMTokens);
      const outputCost = formatPrice(model.outputCostPerMTokens);
      if (inputCost) {
        pricingBits.push(`in $${inputCost}/1M`);
      }
      if (outputCost) {
        pricingBits.push(`out $${outputCost}/1M`);
      }
      const label = [
        baseLabel,
        pricingBits.length ? ` · ${pricingBits.join(' · ')}` : '',
        model.isActive === false ? ' [inactive]' : '',
      ].join('');
      return {
        value: model.id,
        label,
        sortKey: display.toLowerCase(),
      };
    });
    return options
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(({ sortKey, ...rest }) => rest);
  }, [aiModelOptions]);
  const aiModelUsageUpdatedLabel = useMemo(() => {
    if (!aiModelUsageMetadata?.updatedAt) {
      return null;
    }
    try {
      return dayjs(aiModelUsageMetadata.updatedAt).format('MMM D, YYYY h:mm A');
    } catch (err) {
      return aiModelUsageMetadata.updatedAt;
    }
  }, [aiModelUsageMetadata]);
  const translationCanaryModelOptions = useMemo(() => {
    const availableModelIds = new Set(
      aiModelOptions
        .filter(
          (model) =>
            model.isActive !== false &&
            !model.testingOnly &&
            model.id !== aiModelAssignments['translation.preferred']
        )
        .map((model) => model.id)
    );
    return aiModelSelectOptions.filter((option) => availableModelIds.has(option.value));
  }, [aiModelAssignments, aiModelOptions, aiModelSelectOptions]);
  const aiModelUsageRoleSections = useMemo<AIModelRoleSection[]>(() => {
    if (!aiModelUsageRoles.length) {
      return [];
    }
    const sortedRoles = [...aiModelUsageRoles].sort((a, b) =>
      getAiRoleDisplayLabel(a).localeCompare(getAiRoleDisplayLabel(b), undefined, {
        sensitivity: 'base',
      })
    );
    const sections: AIModelRoleSection[] = AI_ROLE_SECTION_ORDER.map((entry) => ({
      key: entry.key,
      title: entry.title,
      description: entry.description,
      roles: sortedRoles.filter((role) => entry.matcher(role.id)),
    })).filter((entry) => entry.roles.length > 0);
    const assignedRoleIds = new Set(
      sections.flatMap((section) => section.roles.map((role) => role.id))
    );
    const ungroupedRoles = sortedRoles.filter((role) => !assignedRoleIds.has(role.id));
    if (ungroupedRoles.length > 0) {
      sections.push({
        key: 'other',
        title: 'Other',
        description: 'Roles without an explicit category.',
        roles: ungroupedRoles,
      });
    }
    return sections;
  }, [aiModelUsageRoles]);

  const applyLoadedUser = async (loadedUser: UserInfo) => {
    setUserInfo(loadedUser);
    setEmail(loadedUser.email || '');
    setUserIdSearch(loadedUser.userId || '');
    setUserDetailTab('overview');
    setDateRange(null);
    setBookFilter(undefined);
    setLanguageFilter(undefined);
    setDescriptionFilter(undefined);
    setPage(1);
    const [creditSnapshot] = await Promise.all([
      loadCreditSnapshot(loadedUser.userId),
      loadSubscriptionBatches(loadedUser.userId),
      loadReferralAdminStatus(loadedUser.userId),
    ]);
    await loadSubscriptionPlanDetails(loadedUser, creditSnapshot);
  };

  const formatLockValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') {
      return '--';
    }
    return String(value);
  };

  const normalizeBookGuid = (value: string) => value.trim();

  const formatCount = (value?: number | null) => Number(value ?? 0).toLocaleString();

  const getCurrencyPrefix = (currency?: string) =>
    currency && currency.toUpperCase() !== 'USD' ? `${currency.toUpperCase()} ` : '$';

  const formatAiLogTimestamp = (value?: string) => {
    if (!value) return '—';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm:ss A') : value;
  };

  const truncateForDisplay = (value?: string, limit = 160) => {
    if (!value) {
      return '';
    }
    return value.length > limit ? `${value.slice(0, limit)}…` : value;
  };

  const formatCostInfo = (cost?: AILogCostInfo) => {
    if (!cost) {
      return '—';
    }
    const prefix = getCurrencyPrefix(cost.currency);
    const segments: string[] = [];
    if (typeof cost.actualCostUsd === 'number') {
      segments.push(`${prefix}${cost.actualCostUsd.toFixed(4)}`);
    }
    const tokenSegments: string[] = [];
    if (typeof cost.inputTokens === 'number') {
      tokenSegments.push(`in ${cost.inputTokens.toLocaleString()}`);
    }
    if (typeof cost.outputTokens === 'number') {
      tokenSegments.push(`out ${cost.outputTokens.toLocaleString()}`);
    }
    if (typeof cost.totalTokens === 'number') {
      tokenSegments.push(`total ${cost.totalTokens.toLocaleString()}`);
    }
    if (tokenSegments.length) {
      segments.push(tokenSegments.join(', '));
    }
    const rateSegments: string[] = [];
    if (typeof cost.inputCostPerMTokens === 'number') {
      rateSegments.push(`in ${prefix}${cost.inputCostPerMTokens.toFixed(4)}/1M`);
    }
    if (typeof cost.outputCostPerMTokens === 'number') {
      rateSegments.push(`out ${prefix}${cost.outputCostPerMTokens.toFixed(4)}/1M`);
    }
    if (rateSegments.length) {
      segments.push(rateSegments.join(', '));
    }
    return segments.join(' · ') || '—';
  };

  const extractFilenameFromDisposition = (header?: string | null): string | null => {
    if (!header) {
      return null;
    }
    const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch (err) {
        console.warn('Failed to decode UTF-8 filename from header', err);
        return utf8Match[1];
      }
    }
    const asciiMatch = header.match(/filename\s*=\s*\"?([^\";]+)\"?/i);
    return asciiMatch && asciiMatch[1] ? asciiMatch[1] : null;
  };

  const loadAiModelUsageData = useCallback(async () => {
    setAiModelUsageLoading(true);
    setAiModelUsageError(null);
    const ensureRole = (roles: AIModelUsageRole[], id: string, description: string): AIModelUsageRole[] => {
      const exists = roles.some(role => role.id === id);
      return exists ? roles : [...roles, { id, description }];
    };
    try {
      const [usageResp, modelsResp] = await Promise.all([
        adminAxios.get<AIModelUsageResponse>(`${API_BASE}/api/ai-model-usage`),
        adminAxios.get<SimpleAIModelSummary[]>(`${API_BASE}/api/ai-models?includeInactive=true&bypass_cache=true`),
      ]);
      const roles = usageResp.data.roles || [];
      let enrichedRoles = roles;
      enrichedRoles = ensureRole(enrichedRoles, 'autotermscanai', 'Terms scan primary');
      enrichedRoles = ensureRole(enrichedRoles, 'autotermsscanaifallback', 'Terms scan fallback');
      enrichedRoles = ensureRole(
        enrichedRoles,
        TRANSLATION_TENSE_GUARD_ROLE_ID,
        'Translation chunk tense guard check'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        TRANSLATION_TENSE_GUARD_CONFIRMATION_ROLE_ID,
        'Translation tense guard confirmation check'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        FRENCH_TONE_DETECTION_ROLE_ID,
        'French narrative tone AI Detect'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        TEST_PROOFREAD_ROLE_1,
        'Experimental post-translation test model #1'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        TEST_PROOFREAD_ROLE_2,
        'Experimental post-translation test model #2'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        TEST_PROOFREAD_ROLE_3,
        'Experimental post-translation test model #3'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        'proofreader.friction_detector',
        'Friction detector (native-reader issue labeling)'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        'proofreader.friction_rewriter',
        'Friction rewriter (fixes flagged spans)'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        'proofreader.alignment',
        'Alignment signal checker'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        'proofreader.alignment_repair',
        'French-to-English localized alignment repair'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        PROOFREAD_CHANGE_AUDIT_ROLE_ID,
        'Proofread change audit (keep/remove review)'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        TRANSLATION_QUALITY_ECONOMY_ROLE_ID,
        'Translation quality economy reviewer (observe projection)'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        TRANSLATION_QUALITY_ESCALATION_ROLE_ID,
        'Translation quality escalation reviewer (high-risk projection)'
      );
      enrichedRoles = ensureRole(
        enrichedRoles,
        CHARACTER_ANALYSIS_RELATIONSHIPS_ROLE_ID,
        'Character and relationship analysis'
      );
      const fullyEnrichedRoles = ensureRole(
        enrichedRoles,
        'proofreader.alignment_fallback',
        'Alignment signal checker fallback'
      ).sort((a, b) => a.id.localeCompare(b.id));
      const persistedAssignments = usageResp.data.assignments || {};
      const forcedModel = normalizeNullableString(forcedPretranslationModelId);
      const forcedTestModel1 = normalizeNullableString(forcedTestProofreadModel1Id);
      const forcedTestModel2 = normalizeNullableString(forcedTestProofreadModel2Id);
      const forcedTestModel3 = normalizeNullableString(forcedTestProofreadModel3Id);
      const forcedTenseGuardModel = normalizeNullableString(forcedTranslationTenseGuardModelId);
      const forcedChangeAuditModel = normalizeNullableString(forcedProofreadChangeAuditModelId);
      const effectiveAssignments = { ...persistedAssignments };
      if (forcedModel) {
        effectiveAssignments[PRETRANSLATION_ROLE_ID] = forcedModel;
      }
      if (forcedTestModel1) {
        effectiveAssignments[TEST_PROOFREAD_ROLE_1] = forcedTestModel1;
      }
      if (forcedTestModel2) {
        effectiveAssignments[TEST_PROOFREAD_ROLE_2] = forcedTestModel2;
      }
      if (forcedTestModel3) {
        effectiveAssignments[TEST_PROOFREAD_ROLE_3] = forcedTestModel3;
      }
      if (forcedTenseGuardModel) {
        effectiveAssignments[TRANSLATION_TENSE_GUARD_ROLE_ID] = forcedTenseGuardModel;
      }
      if (forcedChangeAuditModel) {
        effectiveAssignments[PROOFREAD_CHANGE_AUDIT_ROLE_ID] = forcedChangeAuditModel;
      }
      setAiModelAssignments(effectiveAssignments);
      setTranslationCanary({
        modelId: usageResp.data.translationCanary?.modelId || null,
        percentage: usageResp.data.translationCanary?.percentage || 0,
        cohortVersion: usageResp.data.translationCanary?.cohortVersion || 'v1',
      });
      setAiModelUsageRoles(fullyEnrichedRoles);
      setAiModelUsageMetadata(usageResp.data.metadata || null);
      setAiModelOptions(modelsResp.data || []);
      setAiModelUsageDirty(false);
      setAiModelUsageInitialized(true);
      const hasForcedModelMismatch =
        (forcedModel && persistedAssignments[PRETRANSLATION_ROLE_ID] !== forcedModel) ||
        (forcedTestModel1 && persistedAssignments[TEST_PROOFREAD_ROLE_1] !== forcedTestModel1) ||
        (forcedTestModel2 && persistedAssignments[TEST_PROOFREAD_ROLE_2] !== forcedTestModel2) ||
        (forcedTestModel3 && persistedAssignments[TEST_PROOFREAD_ROLE_3] !== forcedTestModel3) ||
        (forcedTenseGuardModel && persistedAssignments[TRANSLATION_TENSE_GUARD_ROLE_ID] !== forcedTenseGuardModel) ||
        (forcedChangeAuditModel && persistedAssignments[PROOFREAD_CHANGE_AUDIT_ROLE_ID] !== forcedChangeAuditModel);
      if (
        hasForcedModelMismatch
      ) {
        try {
          const { data: persisted } = await adminAxios.put<AIModelUsageResponse>(
            `${API_BASE}/api/ai-model-usage`,
            {
              assignments: effectiveAssignments,
              updatedBy: adminUserId,
            }
          );
          setAiModelAssignments(persisted.assignments || effectiveAssignments);
          setAiModelUsageMetadata(persisted.metadata || usageResp.data.metadata || null);
          setAiModelUsageDirty(false);
        } catch (persistError) {
          console.error('Failed to persist forced model assignment from AdminDashboard.tsx', persistError);
          setAiModelUsageDirty(true);
          message.warning(
            'A forced model override from AdminDashboard.tsx could not be persisted. Use Save Changes.'
          );
        }
      }
    } catch (error) {
      console.error('Failed to load AI model usage settings', error);
      setAiModelUsageError('Failed to load AI model role assignments. Please try again.');
    } finally {
      setAiModelUsageLoading(false);
    }
  }, [
    adminUserId,
    forcedPretranslationModelId,
    forcedTestProofreadModel1Id,
    forcedTestProofreadModel2Id,
    forcedTestProofreadModel3Id,
    forcedTranslationTenseGuardModelId,
    forcedProofreadChangeAuditModelId,
  ]);

  const handleAiModelAssignmentChange = useCallback((roleId: string, modelId?: string) => {
    setAiModelAssignments((prev) => {
      const next = { ...prev };
      if (!modelId) {
        delete next[roleId];
      } else {
        next[roleId] = modelId;
      }
      return next;
    });
    setAiModelUsageDirty(true);
  }, []);

  const saveAiModelUsageAssignments = useCallback(async () => {
    if (!aiModelUsageDirty || aiModelUsageSaving) {
      return;
    }
    setAiModelUsageSaving(true);
    try {
      const { data } = await adminAxios.put<AIModelUsageResponse>(`${API_BASE}/api/ai-model-usage`, {
        assignments: aiModelAssignments,
        translationCanary,
        updatedBy: adminUserId,
      });
      setAiModelAssignments(data.assignments || {});
      setTranslationCanary(data.translationCanary || translationCanary);
      setAiModelUsageMetadata(data.metadata || null);
      setAiModelUsageDirty(false);
      message.success('AI model role assignments and canary settings saved.');
    } catch (error) {
      console.error('Failed to save AI model assignments', error);
      message.error('Failed to save AI model role assignments');
    } finally {
      setAiModelUsageSaving(false);
    }
  }, [aiModelAssignments, aiModelUsageDirty, aiModelUsageSaving, adminUserId, translationCanary]);

  useEffect(() => {
    if (
      isAdmin &&
      (activeTab === 'operations' || activeTab === 'ai-logs') &&
      !aiModelUsageInitialized
    ) {
      loadAiModelUsageData();
    }
  }, [activeTab, aiModelUsageInitialized, loadAiModelUsageData, isAdmin]);

  const renderStripeCustomerIDs = (customerIDs: string | string[]) => {
    const idsArray =
      userInfo?.stripeCustomerIDs ||
      (Array.isArray(customerIDs) ? customerIDs : [customerIDs]);

    if (idsArray.length === 0 || (idsArray.length === 1 && idsArray[0] === 'None')) {
      return 'None';
    }

    if (idsArray.length === 1) {
      return idsArray[0];
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
        {idsArray.map((id, index) => (
          <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
            {id}
          </Tag>
        ))}
      </div>
    );
  };

  const handleVerifyEmail = async () => {
    const emailToVerify = verifyEmailInput.trim();
    if (!emailToVerify) {
      message.warning('Enter an email to verify');
      return;
    }
    setVerifyEmailLoading(true);
    try {
      const resp = await adminAxios.post(`${API_BASE}/admin/verify-email`, {
        email: emailToVerify,
      });
      if (resp.data?.status === 'success') {
        message.success(`Marked verified: ${emailToVerify}`);
        setVerifyEmailInput('');
      } else {
        message.info('Request completed, but no success flag returned');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      message.error(`Failed to verify: ${detail}`);
    } finally {
      setVerifyEmailLoading(false);
    }
  };

  const resetDeleteUserModal = () => {
    setIsDeleteUserModalOpen(false);
    setDeleteUserConfirmId('');
    setDeleteUserConfirmEmail('');
    setDeleteUserPreview(null);
  };

  const handleDeleteUserData = async () => {
    if (!userInfo?.userId) {
      message.warning('Select a user before deleting data.');
      return;
    }
    const confirmId = deleteUserConfirmId.trim();
    const confirmEmail = deleteUserConfirmEmail.trim();
    const targetEmail = (userInfo.email || '').trim();
    if (confirmId !== userInfo.userId) {
      message.error('Confirmation user ID does not match.');
      return;
    }
    if (targetEmail && confirmEmail.toLowerCase() !== targetEmail.toLowerCase()) {
      message.error('Confirmation email does not match.');
      return;
    }
    setDeleteUserSubmitting(true);
    try {
      await adminAxios.post(`${API_BASE}/admin/delete-user-data`, {
        user_id: userInfo.userId,
        confirm_user_id: confirmId,
        confirm_email: confirmEmail || undefined,
        delete_auth_user: true,
      });
      message.success(`Deleted user data for ${userInfo.email || userInfo.userId}.`);
      setUserInfo(null);
      setBooks([]);
      setFilteredBooks([]);
      setTransactions([]);
      setReferralAdminInfo(null);
      resetDeleteUserModal();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length
          ? error.response.data.errors.join(', ')
          : error?.message) ||
        'Delete failed';
      message.error(detail);
    } finally {
      setDeleteUserSubmitting(false);
    }
  };

  const handlePreviewDeleteUserData = async () => {
    if (!userInfo?.userId) {
      message.warning('Select a user before previewing.');
      return;
    }
    if (!deleteConfirmReady) {
      message.error('Confirm user ID and email before previewing.');
      return;
    }
    setDeleteUserPreviewLoading(true);
    try {
      const response = await adminAxios.post(`${API_BASE}/admin/delete-user-data`, {
        user_id: userInfo.userId,
        confirm_user_id: deleteUserConfirmId.trim(),
        confirm_email: deleteUserConfirmEmail.trim() || undefined,
        delete_auth_user: true,
        dry_run: true,
      });
      setDeleteUserPreview(response.data);
      message.success('Preview ready.');
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length
          ? error.response.data.errors.join(', ')
          : error?.message) ||
        'Preview failed';
      message.error(detail);
    } finally {
      setDeleteUserPreviewLoading(false);
    }
  };

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'Series', dataIndex: 'series', key: 'series' },
    { title: 'Language', dataIndex: 'language', key: 'language' },
    { title: 'Upload Type', dataIndex: 'uploadType', key: 'uploadType' },
    { title: 'Book ID', dataIndex: 'bookGuid', key: 'bookGuid' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: string, record: any) => (
        <Space>
          <Button onClick={() => handleDownloadOriginalBook(record.bookGuid)}>
            Download Original File
          </Button>
          <Button onClick={() => handleCopyBookToAdmin(record.bookGuid)}>
            Copy to Admin
          </Button>
        </Space>
      ),
    },
  ];

  const titleSearchColumns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'Book ID', dataIndex: 'bookGuid', key: 'bookGuid' },
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: BookSearchResult) => (
        <Button onClick={() => handleCopyBookToAdmin(record.bookGuid)}>Copy to Admin</Button>
      ),
    },
  ];

  const downloadBase64File = (base64Data: string, mimeType: string | undefined, filename: string) => {
    try {
      const byteCharacters = window.atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error('Failed to decode base64 download payload', error);
      message.error('Failed to decode download payload.');
    }
  };

  const handleDownloadOriginalBook = async (bookGuid: string) => {
    const trimmedBookGuid = normalizeBookGuid(bookGuid);
    if (!trimmedBookGuid) {
      message.error('Book GUID is missing.');
      return;
    }
    setIsGlobalLoading(true);
    try {
      const response = await adminAxios.get(`${API_BASE}/admin/books/${trimmedBookGuid}/download-original`);
      const { signed_url: signedUrl, content_base64: contentBase64, filename, mime_type: mimeType } = response.data || {};
      if (signedUrl) {
        const downloadWindow = window.open(signedUrl, '_blank', 'noopener');
        if (!downloadWindow) {
          message.warning('Pop-up blocked. Please allow pop-ups to download the file.');
        } else {
          message.success('Download started.');
        }
      } else if (contentBase64 && filename) {
        downloadBase64File(contentBase64, mimeType, filename);
      } else {
        message.error('Download link was not provided by the server.');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      message.error(`Failed to download original file: ${detail}`);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const copyBookToAdmin = async (
    bookGuid: string,
    options?: { clearInput?: boolean }
  ) => {
    const trimmedBookGuid = normalizeBookGuid(bookGuid);

    if (!trimmedBookGuid) {
      message.error('Please enter a Book GUID.');
      return;
    }

    setIsGlobalLoading(true);
    try {
      const response = await adminAxios.post(`${API_BASE}/copy-to-admin`, {
        book_guid: trimmedBookGuid,
        copy_series_data: copySeriesData,
      });
      const status = response?.data?.status;
      const detail = response?.data?.message;
      const newBookGuid = response?.data?.new_book_guid;
      const copiedSeriesId = response?.data?.copied_series_id;
      const seriesCopyStatus = response?.data?.series_copy_status;

      if (status && status !== 'success') {
        message.error(detail || 'Error occurred while copying the book.');
        return;
      }

      if (!status) {
        message.info('Copy request completed, but no success flag returned.');
      } else {
        const suffix = newBookGuid ? ` New book ID: ${newBookGuid}` : '';
        const seriesSuffix =
          copySeriesData && seriesCopyStatus === 'created' && copiedSeriesId
            ? ` Series copied as: ${copiedSeriesId}`
            : copySeriesData && seriesCopyStatus === 'reused' && copiedSeriesId
            ? ` Series reused: ${copiedSeriesId}`
            : copySeriesData && seriesCopyStatus === 'not_available'
            ? ' Source book has no series record to copy.'
            : copySeriesData && seriesCopyStatus === 'skipped'
            ? ' Series record could not be found, so only the book was copied.'
            : '';
        message.success(`Book copied successfully!${suffix}${seriesSuffix}`);
      }

      if (options?.clearInput) {
        setBookGuidInput('');
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error';
      message.error(`Error occurred while copying the book: ${detail}`);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleRepairTranslatedFormatting = async (bookGuid: string) => {
    const trimmedBookGuid = normalizeBookGuid(bookGuid);
    if (!trimmedBookGuid) {
      message.error('Please enter a Book GUID.');
      return;
    }

    setFormatRepairBookGuidInput(trimmedBookGuid);
    setIsGlobalLoading(true);
    try {
      const response = await adminAxios.post(
        `${API_BASE}/admin/books/${trimmedBookGuid}/repair-translated-html-formatting`,
        { dry_run: formatRepairDryRun }
      );
      const result = response?.data || {};
      setFormatRepairResult(result);
      const scanned = Number(result.documents_scanned || 0);
      const changed = Number(result.documents_changed || 0);
      const failed = Number(result.failed_documents || 0);
      const runLabel = formatRepairDryRun ? 'Dry run complete' : 'Repair complete';
      if (failed > 0) {
        message.warning(`${runLabel}: scanned ${scanned}, changed ${changed}, failed ${failed}.`);
      } else {
        message.success(`${runLabel}: scanned ${scanned}, changed ${changed}.`);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error';
      message.error(`Failed to repair translated formatting: ${detail}`);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.email) {
      setVerifyEmailInput(userInfo.email);
    }
  }, [userInfo?.email]);

  useEffect(() => {
    if (!userInfo) {
      setUserDetailTab('overview');
      setSubscriptionPlanDetails(null);
      setSubscriptionPlanError(null);
      setSubscriptionPlanLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    const normalizedSeries = seriesFilter.trim().toLowerCase();
    const normalizedTitle = bookNameFilter.trim().toLowerCase();

    if (!normalizedSeries && !normalizedTitle) {
      setFilteredBooks(books);
      return;
    }

    const filtered = books.filter((book: any) => {
      const matchesSeries = normalizedSeries
        ? (book.series || '').toLowerCase().includes(normalizedSeries)
        : true;
      const matchesTitle = normalizedTitle
        ? (book.title || '').toLowerCase().includes(normalizedTitle)
        : true;
      return matchesSeries && matchesTitle;
    });
    setFilteredBooks(filtered);
  }, [books, seriesFilter, bookNameFilter]);

  const loadCreditSnapshot = async (targetUserId: string): Promise<CreditSnapshot | null> => {
    try {
      const resp = await adminAxios.get<CreditSnapshot>(`${API_BASE}/admin/check-credit-balance`, { params: { userID: targetUserId } });
      setCreditStats(resp.data);
      setNextExpiringBatch(normalizeNextExpiringBatch(resp.data.next_expiring_subscription_batch));
      return resp.data;
    } catch (error) {
      setCreditStats(null);
      setNextExpiringBatch(null);
      return null;
    }
  };

  const loadSubscriptionPlanDetails = async (
    loadedUser: UserInfo,
    creditSnapshot?: CreditSnapshot | null
  ): Promise<SubscriptionPlanDetails | null> => {
    const subscriptionId = normalizeNullableString(loadedUser?.stripeSubscriptionID);
    const planPriceID = normalizeNullableString(
      creditSnapshot?.planPriceID ?? (subscriptionId ? loadedUser?.stripePriceID : null)
    );
    const planProductID = normalizeNullableString(
      creditSnapshot?.planProductID ?? (subscriptionId ? loadedUser?.stripeProductID : null)
    );
    const planLookupID = planPriceID || planProductID;

    if (isOnBreakIdentifier(planPriceID) || isOnBreakIdentifier(planProductID)) {
      setSubscriptionPlanLoading(true);
      setSubscriptionPlanError(null);
      try {
        const response = await adminAxios.get(
          `${API_BASE}/admin/subscription-plans`,
          { params: { includeLegacy: true, includeInactive: true } }
        );
        const plans = Array.isArray(response.data?.subscriptionPlans)
          ? response.data.subscriptionPlans
          : Array.isArray(response.data?.plans)
          ? response.data.plans
          : [];
        const onBreakPlan = plans.find((plan: SubscriptionPlanDetails) => isOnBreakPlanDetails(plan)) || null;
        setSubscriptionPlanDetails(onBreakPlan);
        setSubscriptionPlanError(null);
        return onBreakPlan;
      } catch (error: any) {
        setSubscriptionPlanDetails(null);
        if (error?.response?.status === 404) {
          setSubscriptionPlanError(null);
        } else {
          const detail = error?.response?.data?.detail;
          setSubscriptionPlanError(
            typeof detail === 'string' && detail
              ? detail
              : 'Unable to load On Break plan details.'
          );
        }
        return null;
      } finally {
        setSubscriptionPlanLoading(false);
      }
    }

    if (!planLookupID) {
      setSubscriptionPlanDetails(null);
      setSubscriptionPlanError(null);
      return null;
    }

    setSubscriptionPlanLoading(true);
    setSubscriptionPlanError(null);
    try {
      const response = await adminAxios.get<SubscriptionPlanDetails>(
        `${API_BASE}/get_plan_details/${encodeURIComponent(planLookupID)}`,
        { params: planPriceID ? { priceId: planPriceID } : undefined }
      );
      setSubscriptionPlanDetails(response.data || null);
      return response.data || null;
    } catch (error: any) {
      setSubscriptionPlanDetails(null);
      if (error?.response?.status === 404) {
        setSubscriptionPlanError(null);
      } else {
        const detail = error?.response?.data?.detail;
        setSubscriptionPlanError(
          typeof detail === 'string' && detail
            ? detail
            : 'Failed to load subscription plan details.'
        );
      }
      return null;
    } finally {
      setSubscriptionPlanLoading(false);
    }
  };

  const loadSubscriptionBatches = async (targetUserId: string) => {
    setSubscriptionBatchesLoading(true);
    try {
      const resp = await adminAxios.get<{
        batches: SubscriptionCreditBatch[];
        expired_batches?: SubscriptionCreditBatch[];
        total_active_credits: number;
        total_granted_credits: number;
        total_expired_credits?: number;
      }>(`${API_BASE}/admin/subscription-credit-batches/${targetUserId}`);
      const list = (resp.data?.batches || []).map((batch, index) => ({
        ...batch,
        key: batch.batchId || `${index}-${batch.expiresAtISO || ''}`
      }));
      const expiredList = (resp.data?.expired_batches || []).map((batch, index) => ({
        ...batch,
        key: batch.batchId || `${index}-${batch.expiredAtISO || batch.expiresAtISO || ''}`
      }));
      setSubscriptionBatches(list);
      setExpiredSubscriptionBatches(expiredList);
      setSubscriptionBatchSummary({
        total_active_credits: resp.data?.total_active_credits ?? 0,
        total_granted_credits: resp.data?.total_granted_credits ?? 0,
        total_expired_credits: resp.data?.total_expired_credits ?? 0,
      });
    } catch (error: any) {
      message.error(`Failed to load credit batches: ${error?.response?.data?.detail || error?.message || error}`);
      setSubscriptionBatches([]);
      setExpiredSubscriptionBatches([]);
      setSubscriptionBatchSummary({ total_active_credits: 0, total_granted_credits: 0, total_expired_credits: 0 });
    } finally {
      setSubscriptionBatchesLoading(false);
    }
  };

  const loadReferralAdminStatus = useCallback(async (targetUserId: string) => {
    if (!targetUserId) {
      setReferralAdminInfo(null);
      return null;
    }
    setReferralAdminLoading(true);
    setReferralAdminError(null);
    try {
      const { data } = await adminAxios.get(`${API_BASE}/referrals/admin/status`, {
        params: { user_id: targetUserId },
      });
      setReferralAdminInfo(data || null);
      return data || null;
    } catch (error: any) {
      console.error('Failed to load referral admin status:', error);
      const detail = error?.response?.data?.detail;
      setReferralAdminInfo(null);
      setReferralAdminError(detail || 'Failed to load referral status.');
      return null;
    } finally {
      setReferralAdminLoading(false);
    }
  }, []);

  const openBatchAdjustmentModal = useCallback((batch: SubscriptionCreditBatch, action: 'add' | 'subtract') => {
    setSelectedBatch(batch);
    setBatchAdjustmentAction(action);
    setBatchAdjustmentAmount(null);
    setBatchAdjustmentReason('');
    setBatchAdjustmentNewExpiry(batch.expiresAtISO ? dayjs(batch.expiresAtISO) : null);
    setIsBatchAdjustmentModalVisible(true);
  }, []);

  const subscriptionBatchColumns = useMemo(() => [
    {
      title: 'Batch ID',
      dataIndex: 'batchId',
      key: 'batchId',
      ellipsis: true,
      render: (value?: string | null) => value || '�',
    },
    {
      title: 'Remaining',
      dataIndex: 'remainingCredits',
      key: 'remainingCredits',
      render: (value?: number | null) => Number(value ?? 0).toLocaleString(),
    },
    {
      title: 'Granted',
      dataIndex: 'grantedCredits',
      key: 'grantedCredits',
      render: (value?: number | null) => Number(value ?? 0).toLocaleString(),
    },
    {
      title: 'Issued',
      key: 'grantedAtISO',
      render: (_: unknown, record: SubscriptionCreditBatch) => {
        const issued = record.grantedAtISO || record.grantedAt;
        return issued ? dayjs(issued).format('MMM D, YYYY h:mm A') : '—';
      },
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAtISO',
      key: 'expiresAtISO',
      render: (value?: string | null) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '�'),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (value?: string | null) => value || '�',
    },
    {
      title: 'Plan',
      dataIndex: 'planId',
      key: 'planId',
      render: (value?: string | null) => value || '�',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: SubscriptionCreditBatch) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openBatchAdjustmentModal(record, 'add')}>
            Add
          </Button>
          <Button type="link" size="small" danger onClick={() => openBatchAdjustmentModal(record, 'subtract')}>
            Subtract
          </Button>
        </Space>
      ),
    },
  ], [openBatchAdjustmentModal]);

  const webhookSummaryBatchColumns: ColumnsType<SubscriptionCreditBatch> = useMemo(
    () => [
      {
        title: 'Granted',
        dataIndex: 'grantedCredits',
        key: 'grantedCredits',
        width: 90,
        render: (value?: number | null) => Number(value ?? 0).toLocaleString(),
      },
      {
        title: 'Remaining',
        dataIndex: 'remainingCredits',
        key: 'remainingCredits',
        width: 90,
        render: (value?: number | null) => Number(value ?? 0).toLocaleString(),
      },
      {
        title: 'Expires',
        dataIndex: 'expiresAtISO',
        key: 'expiresAtISO',
        width: 170,
        render: (value?: string | null) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—'),
      },
      {
        title: 'Source / Plan',
        key: 'source',
        render: (_: unknown, record: SubscriptionCreditBatch) => (
          <div>
            <div>{record.source || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.planId || '—'}</div>
          </div>
        ),
      },
    ],
    []
  );

  const expiredSubscriptionBatchColumns: ColumnsType<SubscriptionCreditBatch> = useMemo(
    () => [
      {
        title: 'Expired',
        dataIndex: 'expiredCredits',
        key: 'expiredCredits',
        width: 110,
        render: (value?: number | null, record?: SubscriptionCreditBatch) =>
          Number(value ?? record?.grantedCredits ?? 0).toLocaleString(),
      },
      {
        title: 'Granted',
        key: 'grantedAtISO',
        width: 170,
        render: (_: unknown, record: SubscriptionCreditBatch) =>
          record.grantedAtISO ? dayjs(record.grantedAtISO).format('MMM D, YYYY h:mm A') : '—',
      },
      {
        title: 'Batch Expiry',
        dataIndex: 'expiresAtISO',
        key: 'expiresAtISO',
        width: 170,
        render: (value?: string | null) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—'),
      },
      {
        title: 'Logged',
        dataIndex: 'expiredAtISO',
        key: 'expiredAtISO',
        width: 170,
        render: (value?: string | null) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—'),
      },
      {
        title: 'Source / Plan',
        key: 'source',
        render: (_: unknown, record: SubscriptionCreditBatch) => (
          <div>
            <div>{record.source || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.planId || '—'}</div>
          </div>
        ),
      },
    ],
    []
  );

  const recentSubscriptionBatches = useMemo(() => {
    if (!subscriptionBatches?.length) {
      return [];
    }
    const sorted = [...subscriptionBatches].sort((a, b) => {
      const aTs = a.grantedAtISO
        ? dayjs(a.grantedAtISO).valueOf()
        : a.expiresAtISO
        ? dayjs(a.expiresAtISO).valueOf()
        : 0;
      const bTs = b.grantedAtISO
        ? dayjs(b.grantedAtISO).valueOf()
        : b.expiresAtISO
        ? dayjs(b.expiresAtISO).valueOf()
        : 0;
      return bTs - aTs;
    });
    return sorted.slice(0, 5);
  }, [subscriptionBatches]);

  const submitBatchAdjustment = async () => {
    if (!userInfo?.userId || !selectedBatch) {
      message.error('Select a batch to adjust');
      return;
    }
    if (!batchAdjustmentAmount || batchAdjustmentAmount <= 0) {
      message.error('Enter a positive credit amount');
      return;
    }
    if (!batchAdjustmentReason.trim()) {
      message.error('Reason is required');
      return;
    }
    const amountDelta = batchAdjustmentAction === 'add' ? batchAdjustmentAmount : -batchAdjustmentAmount;
    try {
      setBatchOperationLoading(true);
      await adminAxios.post(`${API_BASE}/admin/subscription-credit-batches/${userInfo.userId}/adjust`, {
        batch_id: selectedBatch.batchId,
        amount_delta: amountDelta,
        reason: batchAdjustmentReason,
        expires_at: batchAdjustmentNewExpiry ? batchAdjustmentNewExpiry.toDate().toISOString() : null,
      });
      message.success('Batch updated');
      await Promise.all([
        loadSubscriptionBatches(userInfo.userId),
        loadCreditSnapshot(userInfo.userId),
      ]);
      setIsBatchAdjustmentModalVisible(false);
    } catch (error: any) {
      message.error(`Failed to adjust batch: ${error?.response?.data?.detail || error?.message || error}`);
    } finally {
      setBatchOperationLoading(false);
    }
  };

  const submitCreateBatch = async () => {
    if (!userInfo?.userId) {
      message.error('User not loaded');
      return;
    }
    if (!newBatchAmount || newBatchAmount <= 0) {
      message.error('Enter a positive credit amount');
      return;
    }
    if (!newBatchExpiresAt) {
      message.error('Select an expiration date');
      return;
    }
    if (!newBatchReason.trim()) {
      message.error('Reason is required');
      return;
    }
    try {
      setBatchOperationLoading(true);
      await adminAxios.post(`${API_BASE}/admin/subscription-credit-batches/${userInfo.userId}/create`, {
        amount: Math.floor(newBatchAmount),
        expires_at: newBatchExpiresAt.toDate().toISOString(),
        reason: newBatchReason,
        plan_id: newBatchPlanId || undefined,
        source: newBatchSource || 'admin_manual',
      });
      message.success('Batch created');
      setIsCreateBatchModalVisible(false);
      setNewBatchAmount(null);
      setNewBatchExpiresAt(null);
      setNewBatchReason('');
      await Promise.all([
        loadSubscriptionBatches(userInfo.userId),
        loadCreditSnapshot(userInfo.userId),
      ]);
    } catch (error: any) {
      message.error(`Failed to create batch: ${error?.response?.data?.detail || error?.message || error}`);
    } finally {
      setBatchOperationLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    setIsGlobalLoading(true);
    try {
      const cleanedEmail = email.trim().replace(/\s+/g, '');
      const response = await adminAxios.get<UserInfo>(
        `${API_BASE}/user-info/${cleanedEmail}`,
      );
      await applyLoadedUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      message.error('Failed to fetch user info');
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const cloneWordList = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((item): item is string => typeof item === 'string');
  };

  const copyTranslationRulesFromUser = async (sourceUserId: string, sourceEmail?: string) => {
    if (!sourceUserId) {
      message.error('Missing source user ID.');
      return;
    }

    const adminUserId = usersSharedState?.userID;
    if (!adminUserId) {
      message.error('Missing admin user ID.');
      return;
    }

    setIsCopyingAccountRules(true);
    try {
      const [
        adminAlwaysResponse,
        adminNeverResponse,
        userAlwaysResponse,
        userNeverResponse,
      ] = await Promise.all([
        adminAxios.get(`${API_BASE}/get-account-always-translate`, {
          params: { userID: adminUserId },
        }),
        adminAxios.get(`${API_BASE}/get-account-never-translate`, {
          params: { userID: adminUserId },
        }),
        adminAxios.get(`${API_BASE}/get-account-always-translate`, {
          params: { userID: sourceUserId },
        }),
        adminAxios.get(`${API_BASE}/get-account-never-translate`, {
          params: { userID: sourceUserId },
        }),
      ]);

      const adminAlwaysWords = adminAlwaysResponse.data?.alwaysTranslateWords ?? {};
      const adminNeverWords = adminNeverResponse.data?.neverTranslateWords ?? {};
      const userAlwaysWords = userAlwaysResponse.data?.alwaysTranslateWords ?? {};
      const userNeverWords = userNeverResponse.data?.neverTranslateWords ?? {};

      const clearRequests = [
        ...Object.keys(adminAlwaysWords).map((languageKey) =>
          adminAxios.post(`${API_BASE}/update-account-always-translate`, {
            userID: adminUserId,
            language: languageKey,
            words: [],
          })
        ),
        ...Object.keys(adminNeverWords).map((languageKey) =>
          adminAxios.post(`${API_BASE}/update-account-never-translate`, {
            userID: adminUserId,
            language: languageKey,
            words: [],
          })
        ),
      ];

      if (clearRequests.length) {
        await Promise.all(clearRequests);
      }

      const copyRequests = [
        ...Object.entries(userAlwaysWords).map(([languageKey, words]) =>
          adminAxios.post(`${API_BASE}/update-account-always-translate`, {
            userID: adminUserId,
            language: languageKey,
            words: cloneWordList(words),
          })
        ),
        ...Object.entries(userNeverWords).map(([languageKey, words]) =>
          adminAxios.post(`${API_BASE}/update-account-never-translate`, {
            userID: adminUserId,
            language: languageKey,
            words: cloneWordList(words),
          })
        ),
      ];

      if (copyRequests.length) {
        await Promise.all(copyRequests);
      }

      const sourceLabel = sourceEmail || sourceUserId;
      message.success(`Account translation rules copied from ${sourceLabel} to the admin user.`);
    } catch (error) {
      console.error('Failed to copy account translation rules:', error);
      message.error('Failed to copy account translation rules.');
    } finally {
      setIsCopyingAccountRules(false);
    }
  };

  const copyAccountTranslationRulesToAdmin = async () => {
    if (!userInfo?.userId) {
      message.error('Load a user before copying translation rules.');
      return;
    }

    await copyTranslationRulesFromUser(userInfo.userId, userInfo.email);
  };

  const handleCopyTranslationRulesFromLock = async () => {
    if (!lockLookupResult?.userId) {
      message.error('No user associated with this lock.');
      return;
    }
    await copyTranslationRulesFromUser(lockLookupResult.userId, lockLookupResult.userEmail);
  };

  const handleHoldsTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      if (pagination.pageSize && pagination.pageSize !== holdsPageSize) {
        setHoldsPageSize(pagination.pageSize);
        setHoldsPage(1);
        return;
      }
      if (pagination.current && pagination.current !== holdsPage) {
        setHoldsPage(pagination.current);
      }
    },
    [holdsPageSize, holdsPage]
  );

  const fetchHolds = async (status?: string) => {
    setActiveTab('user');
    if (!userInfo?.userId) return;
    setHoldsLoading(true);
    try {
      const params: any = { userID: userInfo.userId };
      if (status) params.status = status;
      const resp = await adminAxios.get<{ holds: any[] }>(`${API_BASE}/admin/list_credit_holds`, { params });
      setHolds(resp.data?.holds || []);
      setHoldsPage(1);
      setHoldsStatusFilter(status);
      setUserDetailTab('holds');
    } catch (e: any) {
      message.error(`Failed to load credit holds: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setHoldsLoading(false);
    }
  };

  const expireStaleHolds = async () => {
    Modal.confirm({
      title: 'Expire stale holds?',
      content: 'Mark held credit holds older than 48 hours as expired and release pending.',
      okText: 'Expire',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setIsGlobalLoading(true);
          await adminAxios.post(`${API_BASE}/admin/expire_stale_credit_holds`, null, {
            params: { older_than_hours: 48 },
          });
          message.success('Requested expiration of stale holds');
          if (userDetailTab === 'holds') fetchHolds(holdsStatusFilter);
        } catch (e: any) {
          message.error(`Failed to expire holds: ${e?.response?.data?.detail || e.message}`);
        } finally {
          setIsGlobalLoading(false);
        }
      },
    });
  };

  const releaseAllCreditHoldsGlobal = () => {
    Modal.confirm({
      title: 'Release all credit holds?',
      content: 'This will release eligible credit holds for every user. Continue?',
      okText: 'Release All',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setIsGlobalLoading(true);
          const resp = await adminAxios.post(`${API_BASE}/admin/release_all_credit_holds`, null, {
            params: { batch_limit: 500 },
          });
          const processed = resp.data?.processed ?? 0;
          const released = resp.data?.released ?? 0;
          const errorsCount = Array.isArray(resp.data?.errors) ? resp.data.errors.length : 0;
          const summary = `Requested release for ${processed} hold${processed === 1 ? '' : 's'} (${released} released).`;
          if (errorsCount > 0) {
            message.warning(`${summary} ${errorsCount} error${errorsCount === 1 ? '' : 's'} logged.`);
          } else {
            message.success(summary);
          }
        } catch (e: any) {
          message.error(`Failed to release holds: ${e?.response?.data?.detail || e.message}`);
        } finally {
          setIsGlobalLoading(false);
        }
      },
    });
  };

  const releaseHold = async (holdId: string, amount?: number) => {
    if (!userInfo?.userId) return;
    try {
      setHoldsLoading(true);
      await adminAxios.post(`${API_BASE}/admin/release_credit_hold`, null, {
        params: { userID: userInfo.userId, holdId, amount },
      });
      message.success('Hold released');
      fetchHolds(holdsStatusFilter);
    } catch (e: any) {
      message.error(`Release failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setHoldsLoading(false);
    }
  };

  const releaseAllHolds = () => {
    if (!userInfo?.userId || holds.length === 0) return;
    const actionableHolds = holds.filter(
      (hold) =>
        hold &&
        releasableStatuses.has(hold.status) &&
        Number(hold.remaining_amount ?? hold.remainingAmount ?? 0) > 0
    );

    if (actionableHolds.length === 0) {
      message.info('No releasable holds found.');
      return;
    }

    Modal.confirm({
      title: `Release ${actionableHolds.length} hold${actionableHolds.length === 1 ? '' : 's'}?`,
      content: 'This will return the selected holds to the user balance.',
      okText: 'Release',
      okButtonProps: { danger: true },
      onOk: async () => {
        setHoldsLoading(true);
        try {
          const results = await Promise.allSettled(
            actionableHolds.map((hold) =>
              adminAxios.post(`${API_BASE}/admin/release_credit_hold`, null, {
                params: { userID: userInfo.userId, holdId: hold.id },
              })
            )
          );
          const failed = results.filter((res) => res.status === 'rejected');
          const successCount = actionableHolds.length - failed.length;

          if (successCount > 0) {
            message.success(`Released ${successCount} hold${successCount === 1 ? '' : 's'}.`);
          }
          if (failed.length > 0) {
            message.error(`Failed to release ${failed.length} hold${failed.length === 1 ? '' : 's'}.`);
          }

          fetchHolds(holdsStatusFilter);
        } catch (e: any) {
          message.error(`Failed to release holds: ${e?.response?.data?.detail || e.message}`);
        } finally {
          setHoldsLoading(false);
        }
      },
    });
  };

  const commitHold = async (holdId: string, amount?: number) => {
    if (!userInfo?.userId) return;
    Modal.confirm({
      title: 'Commit hold?',
      content: 'This will convert the hold into a real deduction.',
      okText: 'Commit',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setHoldsLoading(true);
          await adminAxios.post(`${API_BASE}/admin/commit_credit_hold`, null, {
            params: { userID: userInfo.userId, holdId, amount },
          });
          message.success('Hold committed');
          fetchHolds(holdsStatusFilter);
        } catch (e: any) {
          message.error(`Commit failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
          setHoldsLoading(false);
        }
      }
    });
  };

  const handleResetAIModelCache = async (): Promise<void> => {
    setIsGlobalLoading(true);
    try {
      const resp = await adminAxios.post(
        `${API_BASE}/api/reset-ai-model-cache`
      );
      if (resp.data.success) {
        message.success(resp.data.message);
      } else {
        message.error(`Error: ${resp.data.error || 'Unknown'}`);
      }
    } catch (error: any) {
      console.error('Error resetting AI model cache:', error);
      message.error('Failed to reset AI model cache');
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handlePreviewCoverKeyMigration = async () => {
    // Removed legacy cover key migration
    message.info('Cover key migration tools have been removed.');
  };

  const fetchUserInfoByAffiliate = async () => {
    const sanitizedCode = sanitizeReferralCodeInput(affiliateCodeSearch);
    if (!sanitizedCode) {
      message.error('Enter a referral code to look up.');
      return;
    }
    setAffiliateCodeSearch(sanitizedCode);
    setIsGlobalLoading(true);
    try {
      const response = await adminAxios.get<UserInfo>(
        `${API_BASE}/user-info-by-affiliate/${sanitizedCode}`,
      );
      await applyLoadedUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info by affiliate:', error);
      message.error('Failed to fetch user info by affiliate');
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const fetchUserInfoById = async () => {
    const trimmedUserId = userIdSearch.trim();
    if (!trimmedUserId) {
      message.error('Enter a user ID to look up.');
      return;
    }
    setUserIdSearch(trimmedUserId);
    setIsGlobalLoading(true);
    try {
      const response = await adminAxios.get<UserInfo>(
        `${API_BASE}/user-info-by-id/${encodeURIComponent(trimmedUserId)}`,
      );
      await applyLoadedUser(response.data);
    } catch (error: any) {
      console.error('Failed to fetch user info by user ID:', error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail) {
        message.error(detail);
      } else {
        message.error('Failed to fetch user info by user ID');
      }
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const fetchUserInfoByStripeCustomer = async () => {
    const trimmedId = stripeCustomerIdSearch.trim();
    if (!trimmedId) {
      message.error('Enter a Stripe customer ID to look up.');
      return;
    }
    setStripeCustomerIdSearch(trimmedId);
    setIsGlobalLoading(true);
    try {
      const response = await adminAxios.get<UserInfo>(
        `${API_BASE}/user-info-by-stripe-customer/${encodeURIComponent(trimmedId)}`,
      );
      await applyLoadedUser(response.data);
    } catch (error: any) {
      console.error('Failed to fetch user info by Stripe customer ID:', error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail) {
        message.error(detail);
      } else {
        message.error('Failed to fetch user info by Stripe customer ID');
      }
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleReferralCodeLookup = async () => {
    const sanitizedCode = sanitizeReferralCodeInput(affiliateCodeSearch);
    if (!sanitizedCode) {
      message.error('Enter a referral code to search.');
      return;
    }
    setReferralCodeSearchLoading(true);
    setReferralCodeSearchError(null);
    setReferralCodeSearchResult(null);
    try {
      const { data } = await adminAxios.get(`${API_BASE}/referrals/admin/lookup-code`, {
        params: { code: sanitizedCode },
      });
      setReferralCodeSearchResult(data || null);
      if (!data) {
        message.info('No referral code record found.');
      }
    } catch (error: any) {
      console.error('Referral code lookup failed:', error);
      const detail = error?.response?.data?.detail;
      setReferralCodeSearchError(detail || 'Referral code not found.');
    } finally {
      setReferralCodeSearchLoading(false);
    }
  };

  const handleClearReferralRelationship = useCallback(() => {
    if (!userInfo?.userId) return;
    Modal.confirm({
      title: 'Clear applied affiliate code?',
      content: 'This will remove the applied affiliate code and reset referral payout flags.',
      okText: 'Clear',
      onOk: async () => {
        try {
          setReferralActionLoading(true);
          const { data } = await adminAxios.post(`${API_BASE}/referrals/admin/clear`, {
            user_id: userInfo.userId,
          });
          message.success('Referral relationship cleared.');
          const status = data?.status || (await loadReferralAdminStatus(userInfo.userId));
          if (status) {
            setReferralAdminInfo(status);
            setUserInfo((prev) => (prev ? { ...prev, affiliateCode: status.affiliateCode ?? prev.affiliateCode } : prev));
          }
        } catch (error: any) {
          const detail = error?.response?.data?.detail;
          console.error('Failed to clear referral relationship:', error);
          message.error(detail || 'Failed to clear referral relationship');
        } finally {
          setReferralActionLoading(false);
        }
      },
    });
  }, [userInfo?.userId, loadReferralAdminStatus]);

  const handleRegenerateReferralCode = useCallback(
    (mode: 'random' | 'custom') => {
      if (!userInfo?.userId) return;
      if (mode === 'random') {
        Modal.confirm({
          title: 'Regenerate referral code?',
          content: 'This will replace the current referral code with a new random code.',
          okText: 'Regenerate',
          onOk: async () => {
          try {
            setReferralActionLoading(true);
            const { data } = await adminAxios.post(`${API_BASE}/referrals/admin/regenerate`, {
              user_id: userInfo.userId,
            });
            message.success('Referral code regenerated.');
            const status = data?.status || (await loadReferralAdminStatus(userInfo.userId));
            if (status) {
              setReferralAdminInfo(status);
              setUserInfo((prev) => (prev ? { ...prev, affiliateCode: status.affiliateCode ?? prev.affiliateCode } : prev));
            }
          } catch (error: any) {
            const detail = error?.response?.data?.detail;
            console.error('Failed to regenerate referral code:', error);
            message.error(detail || 'Failed to regenerate referral code');
          } finally {
              setReferralActionLoading(false);
            }
          },
        });
        return;
      }

      const inputId = `admin-custom-referral-${Date.now()}`;
      Modal.confirm({
        title: 'Regenerate referral code (custom)',
        content: (
          <div>
            <p>Enter a custom code (letters and numbers only, up to 15 characters).</p>
            <Input id={inputId} maxLength={15} placeholder="Custom code" />
          </div>
        ),
        okText: 'Regenerate',
        onOk: async () => {
          const el = document.getElementById(inputId) as HTMLInputElement | null;
          const sanitized = sanitizeReferralCodeInput(el?.value || '');
          if (!sanitized) {
            message.error('Enter a custom code to continue.');
            return Promise.reject();
          }
          try {
            setReferralActionLoading(true);
            const { data } = await adminAxios.post(`${API_BASE}/referrals/admin/regenerate`, {
              user_id: userInfo.userId,
              customCode: sanitized,
            });
            message.success('Referral code regenerated.');
            const status = data?.status || (await loadReferralAdminStatus(userInfo.userId));
            if (status) {
              setReferralAdminInfo(status);
              setUserInfo((prev) => (prev ? { ...prev, affiliateCode: status.affiliateCode ?? prev.affiliateCode } : prev));
            }
          } catch (error: any) {
            const detail = error?.response?.data?.detail;
            console.error('Failed to regenerate referral code:', error);
            message.error(detail || 'Failed to regenerate referral code');
            return Promise.reject();
          } finally {
            setReferralActionLoading(false);
          }
          return Promise.resolve();
        },
      });
    },
    [loadReferralAdminStatus, userInfo?.userId]
  );

  const fetchBooks = async () => {
    setIsGlobalLoading(true);
    setSeriesFilter('');
    setBookNameFilter('');
    try {
      const response = await adminAxios.get(
        `${API_BASE}/uploadedbooks/admin/${email}`
      );
      setBooks(response.data.books);
      setIsBookModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch books:', error);
      message.error('Failed to fetch books');
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleBookTitleSearch = async (value?: string) => {
    const rawValue = value ?? bookTitleSearchTerm;
    if (value !== undefined) {
      setBookTitleSearchTerm(value);
    }
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      titleSearchRequestId.current += 1;
      setBookTitleSearchResults([]);
      setBookTitleSearchLoading(false);
      return;
    }

    const requestId = titleSearchRequestId.current + 1;
    titleSearchRequestId.current = requestId;

    setBookTitleSearchLoading(true);
    try {
      const response = await adminAxios.get<{ books: BookSearchResult[] }>(
        `${API_BASE}/uploadedbooks/search-by-title`,
        { params: { q: trimmedValue, limit: 25 } }
      );
      if (titleSearchRequestId.current !== requestId) {
        return;
      }
      setBookTitleSearchResults(response.data.books || []);
    } catch (error) {
      console.error('Failed to search books by title:', error);
      if (titleSearchRequestId.current === requestId) {
        message.error('Failed to search books by title');
      }
    } finally {
      if (titleSearchRequestId.current === requestId) {
        setBookTitleSearchLoading(false);
      }
    }
  };

  const fetchTransactionsAndFilters = async (
    pageNumber: number,
    size: number,
    currentBookFilter?: string,
    currentLanguageFilter?: string,
    currentDescriptionFilter?: string,
    targetUserId?: string,
  ) => {
    const resolvedUserId = targetUserId ?? userInfo?.userId;
    if (!resolvedUserId) return;

    setTransactionLoading(true);

    try {
      const baseParams: any = {
        userID: resolvedUserId,
        page: pageNumber,
        page_size: size,
        include_filters: true,
      };

      if (dateRange) {
        baseParams.start_date = dateRange[0].toISOString();
        baseParams.end_date = dateRange[1].toISOString();
      }

      if (currentBookFilter) baseParams.book_guid = currentBookFilter;
      if (currentLanguageFilter) baseParams.language = currentLanguageFilter;
      if (currentDescriptionFilter) baseParams.description = currentDescriptionFilter;

      const response = await adminAxios.get<{
        transactions: Transaction[];
        total: number;
        filters: {
          books: BookOption[];
          languages: string[];
          descriptions: string[];
        };
      }>(`${API_BASE}/admin/credit-history`, { params: baseParams });

      setTransactions(response.data.transactions);
      setTotal(response.data.total);
      setBookOptions(response.data.filters.books);
      setLanguageOptions(response.data.filters.languages);
      setDescriptionOptions(response.data.filters.descriptions);

      if (currentLanguageFilter && !response.data.filters.languages.includes(currentLanguageFilter)) {
        setLanguageFilter(undefined);
      }
      if (currentDescriptionFilter && !response.data.filters.descriptions.includes(currentDescriptionFilter)) {
        setDescriptionFilter(undefined);
      }
      if (currentBookFilter && !response.data.filters.books.find((b) => b.guid === currentBookFilter)) {
        setBookFilter(undefined);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      message.error('Failed to fetch transactions');
      setTransactions([]);
      setTotal(0);
    } finally {
      setTransactionLoading(false);
    }
  };

  const fetchAllTransactions = async (): Promise<Transaction[]> => {
    if (!userInfo?.userId) {
      console.error('No userInfo.userId');
      return [];
    }

    const params: Record<string, any> = {
      userID: userInfo.userId,
      include_filters: false,
      page_size: pageSize,
    };

    if (dateRange) {
      params.start_date = dateRange[0].toISOString();
      params.end_date = dateRange[1].toISOString();
    }
    if (bookFilter) params.book_guid = bookFilter;
    if (languageFilter) params.language = languageFilter;
    if (descriptionFilter) params.description = descriptionFilter;

    try {
      const firstPage = await adminAxios.get<{ transactions: Transaction[]; total: number }>(
        `${API_BASE}/admin/credit-history`,
        { params: { ...params, page: 1 } }
      );

      const allTx = [...firstPage.data.transactions];
      const totalPages = Math.ceil(firstPage.data.total / pageSize);

      if (totalPages > 1) {
        const pageRequests = [];
        for (let p = 2; p <= totalPages; p++) {
          pageRequests.push(
            adminAxios.get<{ transactions: Transaction[] }>(
              `${API_BASE}/admin/credit-history`,
              { params: { ...params, page: p } }
            )
          );
        }
        const responses = await Promise.all(pageRequests);
        responses.forEach((res) => {
          allTx.push(...res.data.transactions);
        });
      }

      return allTx;
    } catch (err: any) {
      console.error('⛔︎ fetchAllTransactions failed:', err);
      throw new Error(err?.message || 'Unknown error fetching transactions');
    }
  };

  const handleViewTransactionQueueEntry = useCallback((entry: PendingTransactionLogEntry) => {
    Modal.info({
      title: `Queued Transaction ${entry.id}`,
      width: 640,
      content: (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(entry.entry, null, 2)}</pre>
        </div>
      ),
    });
  }, []);

  const fetchTransactionQueue = useCallback(async () => {
    const trimmedUserId = transactionQueueUserId.trim();
    if (!trimmedUserId) {
      message.error('Enter a user ID to inspect the queue.');
      return;
    }
    setTransactionQueueLoading(true);
    setTransactionQueueError(null);
    try {
      const { data } = await adminAxios.get<{ entries: PendingTransactionLogEntry[] }>(
        `${API_BASE}/admin/credit-transactions/queue/${encodeURIComponent(trimmedUserId)}`,
        {
          params: { limit: transactionQueueLimit },
        }
      );
      setTransactionQueueEntries(data?.entries ?? []);
      if (!data?.entries?.length) {
        message.info('No queued entries for this user.');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to load queue';
      setTransactionQueueError(String(detail));
      message.error(`Failed to load queue: ${detail}`);
    } finally {
      setTransactionQueueLoading(false);
    }
  }, [transactionQueueLimit, transactionQueueUserId]);

  const flushTransactionQueueEntries = useCallback(
    async (entryIds?: string[], options?: { limit?: number }) => {
      const trimmedUserId = transactionQueueUserId.trim();
      if (!trimmedUserId) {
        message.error('Enter a user ID before flushing the queue.');
        return;
      }
      const payload: Record<string, any> = {};
      if (entryIds?.length) {
        payload.entry_ids = entryIds;
      } else {
        const nextLimit = typeof options?.limit === 'number' ? options.limit : transactionQueueLimit;
        if (!nextLimit || nextLimit <= 0) {
          message.error('Provide a positive limit for bulk flush operations.');
          return;
        }
        payload.limit = nextLimit;
      }

      setTransactionQueueFlushing(true);
      try {
        const { data } = await adminAxios.post<{
          processed?: number;
          errors?: Array<{ docId?: string; error?: string }>;
        }>(
          `${API_BASE}/admin/credit-transactions/queue/${encodeURIComponent(trimmedUserId)}/flush`,
          payload
        );
        const processed = Number(data?.processed ?? 0);
        if (processed > 0) {
          message.success(`Flushed ${processed} queued entr${processed === 1 ? 'y' : 'ies'}.`);
        } else {
          message.info('No queued entries were flushed.');
        }
        if (data?.errors?.length) {
          const sampleError = data.errors[data.errors.length - 1];
          message.warning(
            `Encountered ${data.errors.length} error${data.errors.length === 1 ? '' : 's'} while flushing: ${
              sampleError?.error || 'unknown error'
            }`
          );
        }
        setTransactionQueueSelectedIds([]);
        await fetchTransactionQueue();
      } catch (error: any) {
        const detail = error?.response?.data?.detail || error?.message || 'Failed to flush queue';
        message.error(`Failed to flush queue: ${detail}`);
      } finally {
        setTransactionQueueFlushing(false);
      }
    },
    [fetchTransactionQueue, transactionQueueLimit, transactionQueueUserId]
  );

  const clearTransactionQueueView = useCallback(() => {
    setTransactionQueueEntries([]);
    setTransactionQueueSelectedIds([]);
    setTransactionQueueError(null);
  }, []);

  const exportFullToCSV = async (): Promise<void> => {
    setTransactionLoading(true);

    let allTx: Transaction[];
    try {
      allTx = await fetchAllTransactions();
      if (allTx.length === 0) {
        message.error('No transactions to export');
        return;
      }
    } catch (err: any) {
      message.error(`Failed to fetch transactions: ${err.message}`);
      setTransactionLoading(false);
      return;
    }

    try {
      const escapeField = (f: any): string => {
        if (f === null || f === undefined) {
          return '""';
        }
        const s = typeof f === 'string' ? f : String(f);
        return `"${s.replace(/"/g, '')}"`;
      };

      const header = [
        'Date',
        'Description',
        'Book',
        'Chapter',
        'Language',
        'Credits',
        'Type',
        'Deducted From',
        'Deducted Monthly',
        'Deducted Forever',
        'Deducted Rollover',
        'Credits Before',
        'Credits After',
      ].join(',');

      const rows = allTx.map((t: Transaction) => {
        const date = escapeField(new Date(t.timestamp).toLocaleString());
        const desc = escapeField(t.description);
        const book = escapeField(t.book);
        const chap = escapeField(t.chapter);
        const lang = escapeField(t.language);
        const { value: creditDelta } = getCreditDeltaDisplay(t);
        const credits = escapeField(String(creditDelta));
        const typ = escapeField(getTransactionTypeLabel(t));
        const deductionSource = getDeductionSourceCsvFields(t);
        const deductedFrom = escapeField(deductionSource.label);
        const deductedMonthly = escapeField(deductionSource.monthly);
        const deductedForever = escapeField(deductionSource.forever);
        const deductedRollover = escapeField(deductionSource.rollover);
        const before = escapeField(String(t.credits_before ?? ''));
        const after = escapeField(String(t.credits_after ?? ''));

        return [
          date,
          desc,
          book,
          chap,
          lang,
          credits,
          typ,
          deductedFrom,
          deductedMonthly,
          deductedForever,
          deductedRollover,
          before,
          after,
        ].join(',');
      });

      const csvContent = [header, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.style.display = 'none';
      a.href = url;
      a.download = `transactions_${userInfo?.email || 'user'}.csv`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 500);

      message.success('CSV download should begin shortly');
    } catch (err: any) {
      console.error('⛔︎ CSV generation/download failed:', err);
      message.error(`Failed to generate CSV: ${err.message}`);
    } finally {
      setTransactionLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setDateRange(null);
    setBookFilter(undefined);
    setLanguageFilter(undefined);
    setDescriptionFilter(undefined);
    setPage(1);
    setIsTransactionsModalVisible(true);
    await fetchTransactionsAndFilters(1, pageSize);
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(null);
    }
    setBookFilter(undefined);
    setLanguageFilter(undefined);
    setDescriptionFilter(undefined);
    setPage(1);
    fetchTransactionsAndFilters(1, pageSize);
  };

  const handleBookFilterChange = (value: string | undefined) => {
    setBookFilter(value);
    setPage(1);
    fetchTransactionsAndFilters(1, pageSize, value, languageFilter, descriptionFilter);
  };

  const handleLanguageFilterChange = (value: string | undefined) => {
    setLanguageFilter(value);
    setPage(1);
    fetchTransactionsAndFilters(1, pageSize, bookFilter, value, descriptionFilter);
  };

  const handleDescriptionFilterChange = (value: string | undefined) => {
    setDescriptionFilter(value);
    setPage(1);
    fetchTransactionsAndFilters(1, pageSize, bookFilter, languageFilter, value);
  };

  const clearAllFilters = () => {
    setDateRange(null);
    setBookFilter(undefined);
    setLanguageFilter(undefined);
    setDescriptionFilter(undefined);
    setPage(1);
    fetchTransactionsAndFilters(1, pageSize);
  };

  const handlePaginationChange = (newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(1);
      fetchTransactionsAndFilters(1, newPageSize, bookFilter, languageFilter, descriptionFilter);
    } else if (newPage !== page) {
      setPage(newPage);
      fetchTransactionsAndFilters(newPage, pageSize, bookFilter, languageFilter, descriptionFilter);
    }
  };

  const exportToCSV = () => {
    if (transactions.length === 0) {
      message.error('No transactions to export');
      return;
    }

    const escapeField = (field: any): string => {
      if (field === undefined || field === null) {
        return '""';
      }
      const stringField = typeof field === 'string' ? field : String(field);
      const escapedField = stringField.replace(/"/g, '');
      return `"${escapedField}"`;
    };

    const csvContent = [
      [
        'Date',
        'Description',
        'Book',
        'Chapter',
        'Language',
        'Credits',
        'Type',
        'Deducted From',
        'Deducted Monthly',
        'Deducted Forever',
        'Deducted Rollover',
        'Credits Before',
        'Credits After',
      ],
      ...transactions.map((t) => {
        const deductionSource = getDeductionSourceCsvFields(t);
        return [
          escapeField(new Date(t.timestamp).toLocaleString()),
          escapeField(t.description),
          escapeField(t.book),
          escapeField(t.chapter),
          escapeField(t.language),
          escapeField((t.type === 'deduction' ? -t.credits : t.credits).toString()),
          escapeField(t.type),
          escapeField(deductionSource.label),
          escapeField(deductionSource.monthly),
          escapeField(deductionSource.forever),
          escapeField(deductionSource.rollover),
          escapeField(t.credits_before.toString()),
          escapeField(t.credits_after.toString()),
        ];
      }),
    ]
      .map((e) => e.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${userInfo?.email || 'user'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyBookToAdmin = async (bookGuid: string) => {
    await copyBookToAdmin(bookGuid);
  };

  const handleAddCredits = () => {
    if (userInfo?.userId) {
      loadSubscriptionBatches(userInfo.userId);
      loadCreditSnapshot(userInfo.userId);
    }
    setUserDetailTab('credits');
  };

  const submitCreditTransaction = async () => {
    if (!creditsToAdd || !creditReason) {
      message.error('Please fill in all fields');
      return;
    }

    if (creditsToAdd <= 0) {
      message.error('Number of credits must be greater than 0');
      return;
    }

    setIsGlobalLoading(true);
    try {
      await adminAxios.post(`${API_BASE}/add-credits`, {
        user_id: userInfo?.userId,
        credit_type: 'forever',
        credits: creditsToAdd,
        reason: creditReason,
        action: creditAction,
      });
      message.success(`Credits ${creditAction === 'add' ? 'added' : 'deducted'} successfully`);
      if (userInfo?.userId) {
        await Promise.all([
          loadCreditSnapshot(userInfo.userId),
          loadSubscriptionBatches(userInfo.userId),
        ]);
      }
    } catch (error) {
      console.error(`Failed to ${creditAction} credits:`, error);
      message.error(`Failed to ${creditAction} credits`);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleDeleteOldTranslationLocks = () => {
    setIsGlobalLoading(true);
    adminAxios
      .post(`${API_BASE}/delete-old-translation-locks`)
      .then((response) => {
        if (response.data.success) {
          message.success(`${response.data.message}`);
        } else {
          message.error(`Error: ${response.data.error}`);
        }
      })
      .catch((error) => {
        console.error('Error deleting old translation locks:', error);
        message.error('Error occurred while deleting old translation locks.');
      })
      .finally(() => setIsGlobalLoading(false));
  };

  const handleLookupTranslationLock = async () => {
    const code = lockLookupCode.trim();
    if (!code) {
      message.warning('Enter a support code to lookup');
      return;
    }
    setLockLookupLoading(true);
    setLockLookupError(null);
    setLockLookupResult(null);
    try {
      const upper = code.toUpperCase();
      const isSupportCode = /^RC-[A-Z0-9]{3,}$/.test(upper);
      const params: Record<string, string | undefined> = {
        user_id: usersSharedState.userID,
        lock_guid: isSupportCode ? undefined : code,
        support_code: isSupportCode ? upper : undefined,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await adminAxios.get<{ lock: TranslationLockDetails }>(`${API_BASE}/admin/translation-lock`, {
        params,
      });
      if (response.data.lock) {
        setLockLookupResult(response.data.lock);
      } else {
        setLockLookupError('No translation lock data returned.');
      }
    } catch (error: any) {
      console.error('Failed to lookup translation lock', error);
      const detail = (error?.response?.data?.detail as string) || (error?.response?.data?.error as string) || (error?.message as string) || 'Lookup failed';
      setLockLookupError(detail);
      message.error('Failed to lookup translation lock');
    } finally {
      setLockLookupLoading(false);
    }
  };

  const resetLockLookup = () => {
    setLockLookupCode('');
    setLockLookupResult(null);
    setLockLookupError(null);
    setLockLookupLoading(false);
  };

  const handleLockCodeChange = (value: string) => {
    setLockLookupCode(value);
    if (lockLookupError) {
      setLockLookupError(null);
    }
  };

  const openLockExpireModal = (lock: TranslationLockDetails) => {
    setLockExpireTarget(lock);
    setLockExpireReason('Expired via admin dashboard');
    setLockExpireModalVisible(true);
  };

  const closeLockExpireModal = () => {
    setLockExpireModalVisible(false);
    setLockExpireTarget(null);
    setLockExpireReason('Expired via admin dashboard');
  };

  const submitLockExpire = async () => {
    if (!lockExpireTarget) {
      return;
    }
    setLockExpireSubmitting(true);
    try {
      const payload = {
        lock_guid: lockExpireTarget.lockGuid,
        document_id: lockExpireTarget.documentId,
        reason: lockExpireReason.trim() || undefined,
      };
      const response = await adminAxios.post<LockExpireResponse>(`${API_BASE}/admin/lock-expire`, payload);
      setLockLookupResult(response.data.lock);
      message.success('Lock expired and credits released when applicable.');
      closeLockExpireModal();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to expire lock';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLockExpireSubmitting(false);
    }
  };

  const handleLookupSupportEvent = async () => {
    const code = supportLookupCode.trim();
    if (!code) {
      message.warning('Enter a support code to lookup');
      return;
    }
    setSupportLookupLoading(true);
    setSupportLookupError(null);
    setSupportLookupResult(null);
    try {
      const response = await adminAxios.get<{ event: SupportEventRecord }>(
        `${API_BASE}/admin/support-events/${encodeURIComponent(code)}`,
        {
          params: {
            user_id: usersSharedState.userID,
          },
        }
      );
      if (response.data.event) {
        setSupportLookupResult(response.data.event);
      } else {
        setSupportLookupError('No support event returned.');
      }
    } catch (error: any) {
      console.error('Failed to lookup support event', error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Lookup failed';
      setSupportLookupError(
        typeof detail === 'string' ? detail : JSON.stringify(detail)
      );
      message.error('Failed to lookup support event');
    } finally {
      setSupportLookupLoading(false);
    }
  };

  const resetSupportLookup = () => {
    setSupportLookupCode('');
    setSupportLookupResult(null);
    setSupportLookupError(null);
    setSupportLookupLoading(false);
  };

  const handleSupportCodeChange = (value: string) => {
    setSupportLookupCode(value);
    if (supportLookupError) {
      setSupportLookupError(null);
    }
  };

  const totalCreditsOnPage = transactions.reduce((sum, tx) => {
    return tx.type === 'deduction' ? sum - tx.credits : sum + tx.credits;
  }, 0);

  const hasActiveFilters = dateRange !== null || bookFilter || languageFilter || descriptionFilter;

  // Removed legacy Brevo sync

  // Removed legacy Stripe sync
  const [isWebhookModalVisible, setIsWebhookModalVisible] = useState(false);
  const [webhookScenario, setWebhookScenario] = useState<'subscription_create' | 'subscription_renewal' | 'subscription_update' | 'subscription_cancel'>('subscription_create');
  const [webhookAmount, setWebhookAmount] = useState<number | null>(null);
  const [webhookCurrency, setWebhookCurrency] = useState<string>('');
  const [webhookInvoiceId, setWebhookInvoiceId] = useState<string>('');
  const [webhookReferralCode, setWebhookReferralCode] = useState<string>('');
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookResult, setWebhookResult] = useState<any>(null);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [webhookPlanRecords, setWebhookPlanRecords] = useState<WebhookPlanRecord[]>([]);
  const [webhookPlanId, setWebhookPlanId] = useState<string | null>(null);
  const [webhookPlansLoading, setWebhookPlansLoading] = useState(false);
  const [webhookPlansError, setWebhookPlansError] = useState<string | null>(null);
  const webhookPlanManuallySelected = useRef(false);

  const fetchWebhookPlans = useCallback(async () => {
    setWebhookPlansLoading(true);
    setWebhookPlansError(null);
    try {
      const response = await adminAxios.get(`${API_BASE}/get-subscription-plans`, {
        params: { includeLegacy: true, includeInactive: true },
      });
      const plans = Array.isArray(response.data?.subscriptionPlans) ? response.data.subscriptionPlans : [];
      const normalizedPlans: WebhookPlanRecord[] = plans
        .map((plan: any) => {
          const rawId = typeof plan?.id === 'string' ? plan.id.trim() : '';
          if (!rawId) {
            return null;
          }
          const priceValue = Number(plan?.price ?? plan?.amount ?? 0);
          const intervalMonthsRaw =
            typeof plan?.intervalMonths === 'number'
              ? plan.intervalMonths
              : Number(plan?.intervalMonths ?? 0);
          const monthlyEquivalentRaw =
            typeof plan?.monthlyEquivalentCredits === 'number'
              ? plan.monthlyEquivalentCredits
              : Number(plan?.monthlyEquivalentCredits ?? plan?.monthlyCredits ?? 0);
          const stripeProductID =
            typeof plan?.stripeProductID === 'string'
              ? plan.stripeProductID
              : typeof plan?.stripeProductId === 'string'
              ? plan.stripeProductId
              : undefined;
          const stripePriceID =
            typeof plan?.stripePriceID === 'string'
              ? plan.stripePriceID
              : typeof plan?.stripePriceId === 'string'
              ? plan.stripePriceId
              : undefined;

          return {
            id: rawId,
            planName: String(plan?.planName || plan?.displayName || plan?.planKey || rawId),
            billingInterval: typeof plan?.billingInterval === 'string' ? plan.billingInterval : undefined,
            intervalMonths: Number.isFinite(intervalMonthsRaw) && intervalMonthsRaw > 0 ? intervalMonthsRaw : undefined,
            price: Number.isFinite(priceValue) ? priceValue : undefined,
            currency:
              typeof plan?.currency === 'string' && plan.currency.trim()
                ? plan.currency.trim().toLowerCase()
                : undefined,
            isLegacy: Boolean(plan?.isLegacy),
            isActive: plan?.isActive !== false,
            monthlyEquivalentCredits: Number.isFinite(monthlyEquivalentRaw) && monthlyEquivalentRaw > 0 ? monthlyEquivalentRaw : undefined,
            stripeProductID,
            stripePriceID,
          } as WebhookPlanRecord;
        })
        .filter((plan: WebhookPlanRecord | null): plan is WebhookPlanRecord => Boolean(plan));

      normalizedPlans.sort((a, b) => a.planName.localeCompare(b.planName));

      setWebhookPlanRecords(normalizedPlans);
    } catch (error: any) {
      console.error('Failed to load subscription plans for webhook simulation:', error);
      const detail =
        (error?.response?.data?.detail as string) ||
        (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length
          ? error.response.data.errors.join(', ')
          : error?.message) ||
        'Failed to load subscription plans';
      setWebhookPlansError(detail);
      message.error(detail);
    } finally {
      setWebhookPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isWebhookModalVisible) {
      return;
    }
    if (webhookPlansLoading || webhookPlanRecords.length) {
      return;
    }
    fetchWebhookPlans();
  }, [fetchWebhookPlans, isWebhookModalVisible, webhookPlanRecords.length, webhookPlansLoading]);

  useEffect(() => {
    webhookPlanManuallySelected.current = false;
    setWebhookPlanId(null);
  }, [userInfo?.userId]);

  useEffect(() => {
    if (!webhookPlanRecords.length) {
      if (!webhookPlanManuallySelected.current && webhookPlanId !== null) {
        setWebhookPlanId(null);
      }
      return;
    }
    if (webhookPlanManuallySelected.current) {
      return;
    }
    const currentProductId =
      typeof userInfo?.stripeProductID === 'string' && userInfo.stripeProductID.trim()
        ? userInfo.stripeProductID.trim()
        : undefined;
    const currentPriceId =
      typeof userInfo?.stripePriceID === 'string' && userInfo.stripePriceID.trim()
        ? userInfo.stripePriceID.trim()
        : undefined;
    const desiredPlan =
      webhookPlanRecords.find((plan) => {
        if (currentPriceId && plan.stripePriceID === currentPriceId) {
          return true;
        }
        return currentProductId ? plan.stripeProductID === currentProductId : false;
      }) || null;
    const desiredPlanId = desiredPlan ? desiredPlan.id : null;
    if (desiredPlanId !== webhookPlanId) {
      setWebhookPlanId(desiredPlanId);
    }
  }, [webhookPlanRecords, userInfo?.stripeProductID, userInfo?.stripePriceID, webhookPlanId]);

  const webhookPlanOptions = useMemo(() => {
    if (!webhookPlanRecords.length) {
      return [];
    }
    const formatterCache = new Map<string, Intl.NumberFormat>();
    const currentProductId =
      typeof userInfo?.stripeProductID === 'string' && userInfo.stripeProductID.trim()
        ? userInfo.stripeProductID.trim()
        : undefined;
    const currentPriceId =
      typeof userInfo?.stripePriceID === 'string' && userInfo.stripePriceID.trim()
        ? userInfo.stripePriceID.trim()
        : undefined;

    const formatPrice = (price: number | undefined, currencyCode?: string): string => {
      if (typeof price !== 'number' || !Number.isFinite(price)) {
        return 'Custom pricing';
      }
      const resolvedCurrency = (currencyCode || 'USD').toUpperCase();
      try {
        if (!formatterCache.has(resolvedCurrency)) {
          formatterCache.set(
            resolvedCurrency,
            new Intl.NumberFormat('en-US', { style: 'currency', currency: resolvedCurrency })
          );
        }
        return formatterCache.get(resolvedCurrency)!.format(price);
      } catch (err) {
        return `${price.toFixed(2)} ${resolvedCurrency}`;
      }
    };

    const intervalLabelFor = (plan: WebhookPlanRecord): string => {
      const interval = (plan.billingInterval || '').toLowerCase();
      const intervalMonths = plan.intervalMonths;
      if (interval === 'monthly' || intervalMonths === 1) {
        return 'Monthly';
      }
      if (interval === 'yearly' || interval === 'annual' || intervalMonths === 12) {
        return 'Annual';
      }
      if (interval === 'weekly' || interval === 'week') {
        return 'Weekly';
      }
      if (interval === 'daily' || interval === 'day') {
        return 'Daily';
      }
      if (intervalMonths && intervalMonths > 1) {
        return `${intervalMonths}-month`;
      }
      if (interval) {
        return interval.charAt(0).toUpperCase() + interval.slice(1);
      }
      return 'Custom interval';
    };

    return webhookPlanRecords.map((plan) => {
      const intervalLabel = intervalLabelFor(plan);
      const priceLabel = formatPrice(plan.price, plan.currency);
      const creditsLabel = plan.monthlyEquivalentCredits
        ? `${plan.monthlyEquivalentCredits.toLocaleString()} credits/month`
        : '';
      const tags: Array<{ label: string; color: string }> = [];
      if (plan.isLegacy) {
        tags.push({ label: 'Legacy', color: 'purple' });
      }
      if (plan.isActive === false) {
        tags.push({ label: 'Inactive', color: 'red' });
      }
      if (
        (currentPriceId && plan.stripePriceID === currentPriceId) ||
        (currentProductId && plan.stripeProductID === currentProductId)
      ) {
        tags.push({ label: 'Current', color: 'green' });
      }

      const subtitleParts: string[] = [];
      if (creditsLabel) {
        subtitleParts.push(creditsLabel);
      }
      if (plan.currency && plan.currency !== 'usd') {
        subtitleParts.push(plan.currency.toUpperCase());
      }

      const searchLabelParts = [plan.planName, intervalLabel, priceLabel, creditsLabel, ...tags.map((tag) => tag.label)];

      return {
        value: plan.id,
        title: `${plan.planName} • ${intervalLabel} • ${priceLabel}`,
        subtitle: subtitleParts.filter(Boolean).join(' • '),
        tags,
        searchLabel: searchLabelParts.filter(Boolean).join(' '),
      };
    });
  }, [webhookPlanRecords, userInfo?.stripeProductID, userInfo?.stripePriceID]);

  const selectedWebhookPlan = useMemo(
    () => webhookPlanRecords.find((plan) => plan.id === webhookPlanId) || null,
    [webhookPlanRecords, webhookPlanId]
  );

  const handleWebhookPlanChange = (value?: string) => {
    if (typeof value === 'string' && value) {
      webhookPlanManuallySelected.current = true;
      setWebhookPlanId(value);
    } else {
      webhookPlanManuallySelected.current = false;
      setWebhookPlanId(null);
    }
  };

  const openWebhookTestModal = () => {
    setWebhookError(null);
    setWebhookResult(null);
    if (!webhookPlanRecords.length && !webhookPlansLoading) {
      fetchWebhookPlans();
    }
    setIsWebhookModalVisible(true);
  };

  const closeWebhookTestModal = () => {
    if (!webhookLoading) {
      setIsWebhookModalVisible(false);
    }
  };

  const runWebhookSimulation = async () => {
    if (!userInfo?.userId) {
      message.error('Load a user before running a simulation.');
      return;
    }
    const payload: Record<string, unknown> = {
      target_user_id: userInfo.userId,
      scenario: webhookScenario,
    };

    if (webhookScenario !== 'subscription_cancel' && typeof webhookAmount === 'number') {
      payload.price_override_cents = Math.round(webhookAmount * 100);
    }
    if (webhookInvoiceId.trim()) {
      payload.invoice_id = webhookInvoiceId.trim();
    }
    if (webhookCurrency.trim()) {
      payload.currency = webhookCurrency.trim();
    }
    if (webhookPlanId) {
      payload.plan_id = webhookPlanId;
    }
    if (webhookReferralCode.trim()) {
      payload.referral_code = webhookReferralCode.trim();
    }

    setWebhookLoading(true);
    setWebhookError(null);
    try {
      const response = await adminAxios.post(`${API_BASE}/admin/simulate-stripe-webhook`, payload);
      setWebhookResult(response.data);
      message.success('Simulated webhook dispatched.');
      if (selectedWebhookPlan && webhookScenario !== 'subscription_cancel') {
        setUserInfo((prev) =>
          prev
            ? {
                ...prev,
                planName: selectedWebhookPlan.planName || prev.planName,
                stripeProductID: selectedWebhookPlan.stripeProductID || prev.stripeProductID,
                stripePriceID: selectedWebhookPlan.stripePriceID || prev.stripePriceID,
                monthlyCredits:
                  selectedWebhookPlan.monthlyEquivalentCredits ?? prev.monthlyCredits,
              }
            : prev
        );
      }
      if (webhookScenario === 'subscription_cancel') {
        setUserInfo((prev) => (prev ? { ...prev, stripeSubscriptionID: '' } : prev));
      }
      const [creditSnapshot] = await Promise.all([
        loadCreditSnapshot(userInfo.userId),
        loadSubscriptionBatches(userInfo.userId),
        loadReferralAdminStatus(userInfo.userId),
      ]);
      await loadSubscriptionPlanDetails(userInfo, creditSnapshot);
    } catch (error: any) {
      const detail =
        (error?.response?.data?.detail as string) ||
        (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length
          ? error.response.data.errors.join(', ')
          : error?.message) ||
        'Webhook simulation failed';
      setWebhookError(detail);
      message.error(detail);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleDeleteGeminiBatchUsage = () => {
    Modal.confirm({
      title: 'Delete Batch Token Data (Gemini + ChatGPT)',
      content: 'This will permanently delete model_usage entries for Gemini 2.5 batch jobs and ChatGPT batch jobs. Continue?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setIsGlobalLoading(true);
          const targets = [
            { provider: 'gemini', model: 'gemini-2.5-pro', label: 'Gemini' },
            { provider: 'openai', model: 'gpt-5-2025-08-07', label: 'ChatGPT' },
          ];
          const responses = await Promise.all(
            targets.map((target) =>
              adminAxios.post(`${API_BASE}/delete-gemini-batch-usage`, {
                provider: target.provider,
                model: target.model,
                batch_only: true,
                days: 3650,
              })
            )
          );
          const summary = responses.map((resp, idx) => ({
            label: targets[idx].label,
            count: Number(resp.data?.deleted_count || 0),
          }));
          const totalDeleted = summary.reduce((sum, item) => sum + item.count, 0);
          const detail = summary.map((item) => `${item.label}: ${item.count}`).join(', ');
          message.success(`Deleted ${totalDeleted} batch usage entries (${detail}).`);
        } catch (e) {
          console.error('Delete batch usage cleanup failed', e);
          message.error('Failed to delete batch usage entries');
        } finally {
          setIsGlobalLoading(false);
        }
      },
    });
  };

  const handleBackfillWordCounts = async (dryRun: boolean) => {
    const currentUserId = usersSharedState.userID;
    setBackfillLoading(true);
    try {
      const response = await adminAxios.post(`${API_BASE}/admin/backfill-original-wordcounts`, {
        dry_run: dryRun,
      });
      const data = response.data;
      setBackfillResult(data);
      Modal.info({
        title: dryRun ? 'Backfill Word Counts (Dry Run)' : 'Backfill Word Counts (Executed)',
        width: 800,
        content: (
          <div>
            <p><strong>Processed Books:</strong> {data.processed_books}</p>
            <p><strong>Processed Chapters:</strong> {data.processed_chapters}</p>
            <p><strong>Chapters {dryRun ? 'that would change' : 'changed'}:</strong> {data.changed_chapters}</p>
            {Array.isArray(data.examples) && data.examples.length > 0 && (
              <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 10 }}>
                <strong>Examples:</strong>
                {data.examples.map((ex: any, idx: number) => (
                  <div key={idx} style={{ fontSize: 12, marginTop: 6 }}>
                    <div>Book: {ex.book_id}</div>
                    <div>Chapter: {ex.chapter_guid}</div>
                    <div>Old → New: {ex.old} → {ex.new} (Δ {ex.delta})</div>
                    <hr />
                  </div>
                ))}
              </div>
            )}
          </div>
        ),
      });
    } catch (err) {
      console.error('Backfill error:', err);
      message.error('Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleBackfillUsageCounters = async (dryRun: boolean) => {
    setUsageBackfillLoading(true);
    try {
      const response = await adminAxios.post(`${API_BASE}/admin/backfill-user-book-counters`, {
        dry_run: dryRun,
      });

      const data = response.data ?? {};
      const toNumber = (value: any) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };
      const totalUsers = toNumber(data.total_users);
      const totalBooksUploaded = toNumber(data.total_books_uploaded);
      const updatesApplied = Boolean(data.updates_applied);

      Modal.info({
        title: dryRun ? 'User/Book Counter Backfill (Dry Run)' : 'User/Book Counter Backfill',
        width: 560,
        content: (
          <div>
            <p><strong>Total users:</strong> {totalUsers.toLocaleString()}</p>
            <p><strong>Total books uploaded:</strong> {totalBooksUploaded.toLocaleString()}</p>
            <p><strong>Updates applied:</strong> {updatesApplied ? 'Yes' : 'No (dry run)'}</p>
            {Array.isArray(data.errors) && data.errors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong>Errors ({data.errors.length})</strong>
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  {data.errors.slice(0, 5).map((errMsg: string, idx: number) => (
                    <li key={idx}>{errMsg}</li>
                  ))}
                </ul>
                {data.errors.length > 5 && <div style={{ fontSize: 12 }}>Showing first 5 errors...</div>}
              </div>
            )}
            {!dryRun && data.document_path && (
              <p style={{ marginTop: 12 }}>
                <strong>Updated document:</strong> {data.document_path}
              </p>
            )}
          </div>
        ),
      });

      if (!dryRun && updatesApplied) {
        message.success('User and book counters updated');
      }
    } catch (err) {
      console.error('Usage counters backfill error:', err);
      message.error('Usage counters backfill failed');
    } finally {
      setUsageBackfillLoading(false);
    }
  };

  const handleProcessingStateRepair = async (dryRun: boolean) => {
    const limit = Math.max(1, Math.min(Math.floor(lockStateBackfillLimit || 1), 5000));
    setLockStateBackfillLoading(true);
    try {
      const response = await adminAxios.post<ProcessingStateBackfillResult>(
        `${API_BASE}/admin/backfill-failed-processing-states`,
        {
          dry_run: dryRun,
          scan_limit: limit,
        }
      );
      const data = response.data ?? ({} as ProcessingStateBackfillResult);
      setLockStateBackfillResult(data);
      const formatNumber = (value: number | undefined) =>
        Number(value || 0).toLocaleString();
      Modal.info({
        title: dryRun
          ? 'Repair Failed Processing States (Dry Run)'
          : 'Repair Failed Processing States',
        width: 640,
        content: (
          <div>
            <p><strong>Scanned:</strong> {formatNumber(data.scanned)}</p>
            <p><strong>Candidates:</strong> {formatNumber(data.candidates)}</p>
            <p><strong>Updated:</strong> {formatNumber(data.updated)}</p>
            <p><strong>Already marked failed:</strong> {formatNumber(data.already_failed_state)}</p>
            <p><strong>Skipped (terminal state):</strong> {formatNumber(data.skipped_terminal_state)}</p>
            {Array.isArray(data.examples) && data.examples.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong>Example locks:</strong>
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  {data.examples.slice(0, 5).map((example, idx) => (
                    <li key={`${example.document_id}-${idx}`}>
                      <code>{example.lock_guid || example.document_id}</code>
                      {example.previous_processing_state
                        ? ` (state: ${example.previous_processing_state})`
                        : ''}
                      {example.failure_time ? ` · failure: ${example.failure_time}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(data.errors) && data.errors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong>Errors ({data.errors.length}):</strong>
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  {data.errors.slice(0, 5).map((errMsg, idx) => (
                    <li key={idx}>{errMsg}</li>
                  ))}
                </ul>
                {data.errors.length > 5 && (
                  <div style={{ fontSize: 12 }}>Showing first five errors…</div>
                )}
              </div>
            )}
          </div>
        ),
      });
      if (!dryRun && Number(data.updated || 0) > 0) {
        message.success(`Updated ${formatNumber(data.updated)} lock(s).`);
      }
    } catch (err) {
      console.error('Processing state repair failed:', err);
      message.error('Failed to repair lock processing states');
    } finally {
      setLockStateBackfillLoading(false);
    }
  };

  const runSubscriptionExpiryRecomputeAll = async (dryRun: boolean) => {
    const maxUsers = Math.max(1, Math.min(Math.floor(subscriptionExpiryRecomputeLimit || 1), 5000));
    const cursor = subscriptionExpiryRecomputeCursor.trim();
    setSubscriptionExpiryRecomputeLoading(true);
    try {
      const response = await adminAxios.post<SubscriptionBatchExpiryRecomputeAllResult>(
        `${API_BASE}/admin/subscription-credit-batches/recompute-expiry-all`,
        {
          dry_run: dryRun,
          allow_shorten: false,
          large_change_days: 183,
          max_users: maxUsers,
          cursor: cursor || undefined,
          max_examples: 20,
          max_large_change_examples: 20,
        }
      );
      const result = response.data ?? ({} as SubscriptionBatchExpiryRecomputeAllResult);
      setSubscriptionExpiryRecomputeResult(result);
      if (result.hit_limit && result.next_cursor) {
        setSubscriptionExpiryRecomputeCursor(result.next_cursor);
      }
      if (dryRun) {
        const skippedShorter = Number(result.skipped_shorter || 0).toLocaleString();
        const largeChanges = Number(result.large_change_count || 0).toLocaleString();
        message.success(
          `Dry run complete. ${Number(result.candidates || 0).toLocaleString()} candidate update(s); skipped shortening ${skippedShorter}; large changes ${largeChanges}.`
        );
      } else {
        message.success(
          `Applied ${Number(result.updated || 0).toLocaleString()} batch expiry update(s).`
        );
      }
    } catch (error: any) {
      const detail =
        (typeof error?.response?.data?.detail === 'string' && error.response.data.detail) ||
        (typeof error?.message === 'string' && error.message) ||
        'Failed to recompute subscription batch expiries';
      message.error(detail);
    } finally {
      setSubscriptionExpiryRecomputeLoading(false);
    }
  };

  const handleApplySubscriptionExpiryRecomputeAll = () => {
    const canApply =
      Boolean(subscriptionExpiryRecomputeResult?.dry_run) &&
      Number(subscriptionExpiryRecomputeResult?.candidates || 0) > 0;
    if (!canApply) {
      message.info('Run a dry run first to preview candidate updates.');
      return;
    }
    Modal.confirm({
      title: `Apply ${Number(subscriptionExpiryRecomputeResult?.candidates || 0).toLocaleString()} batch expiry update(s)?`,
      content:
        'This legacy repair updates stored batch expiry timestamps only. Subscription credits remain protected while a subscription is active and enter a 60-day grace window after cancellation.',
      okText: 'Apply Updates',
      okButtonProps: { danger: true },
      onOk: async () => {
        await runSubscriptionExpiryRecomputeAll(false);
      },
    });
  };

  const handleUncategorizedUsageBackfill = async (dryRun: boolean) => {
    const days = Math.max(1, Math.min(365, Math.floor(uncategorizedUsageDays || 1)));
    const limit = Math.max(1, Math.min(200000, Math.floor(uncategorizedUsageLimit || 1)));
    const provider = uncategorizedUsageProvider.trim();
    const model = uncategorizedUsageModel.trim();

    setUncategorizedUsageBackfillLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        days,
        dry_run: dryRun,
        limit,
      };
      if (provider) params.provider = provider;
      if (model) params.model = model;

      const response = await adminAxios.post<UncategorizedUsageBackfillResponse>(
        `${API_BASE}/admin/usage-aggregates/uncategorized/backfill`,
        null,
        { params }
      );
      const data = response.data ?? ({} as UncategorizedUsageBackfillResponse);
      setUncategorizedUsageBackfillResult(data);

      const scanned = Number(data.scanned || 0).toLocaleString();
      const reclassified = Number(data.reclassified || 0).toLocaleString();
      const applied = Number(data.applied || 0).toLocaleString();
      if (dryRun) {
        message.success(
          `Dry run complete. Scanned ${scanned} records; reclassifiable ${reclassified}.`
        );
      } else {
        message.success(`Backfill complete. Applied ${applied} updates.`);
      }
    } catch (error: any) {
      const detail =
        (typeof error?.response?.data?.detail === 'string' && error.response.data.detail) ||
        (typeof error?.response?.data?.error === 'string' && error.response.data.error) ||
        (typeof error?.message === 'string' && error.message) ||
        'Unknown error';
      message.error(`Uncategorized backfill failed: ${detail}`);
    } finally {
      setUncategorizedUsageBackfillLoading(false);
    }
  };

  const handleExportDocsToDocx = async () => {
    setDocsExportLoading(true);
    try {
      const response = await adminAxios.get<Blob>(`${API_BASE}/admin/docs/export-docx`, {
        responseType: 'blob',
      });
      const contentTypeHeader = response.headers['content-type'];
      const contentType =
        typeof contentTypeHeader === 'string' && contentTypeHeader
          ? contentTypeHeader
          : 'application/zip';
      const blob =
        response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const disposition = response.headers['content-disposition'] as string | undefined;
      const filename = extractFilenameFromDisposition(disposition) || 'docs-docx.zip';
      const tempLink = document.createElement('a');
      tempLink.href = downloadUrl;
      tempLink.download = filename;
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(downloadUrl);
      const convertedCount = response.headers['x-docx-file-count'];
      const countLabel = convertedCount ? `${convertedCount} file${convertedCount === '1' ? '' : 's'}` : 'docs';
      message.success(`Exported ${countLabel} to DOCX`);
    } catch (err: any) {
      console.error('Failed to export docs to DOCX', err);
      let detail: string | undefined;
      const responseData = err?.response?.data;
      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          if (text) {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed.detail === 'string') {
              detail = parsed.detail;
            } else {
              detail = text;
            }
          }
        } catch {
          // ignore parsing errors
        }
      } else if (typeof err?.response?.data?.detail === 'string') {
        detail = err.response.data.detail;
      }
      message.error(detail ? `Export failed: ${detail}` : 'Failed to export docs to DOCX');
    } finally {
      setDocsExportLoading(false);
    }
  };

  const fetchAiLogs = useCallback(async () => {
    if (!isAdmin || aiLogsUnavailable) {
      return;
    }
    const trimmedBookId = aiLogBookId.trim();
    if (!trimmedBookId) {
      message.warning('Enter a Book GUID to fetch AI logs.');
      return;
    }
    setAiLogsLoading(true);
    setAiLogsError(null);
    try {
      const params: Record<string, any> = {
        bookId: trimmedBookId,
        stage: aiLogStage,
        limit: aiLogLimit,
      };
      const trimmedChapter = aiLogChapterGuid.trim();
      if (trimmedChapter) {
        params.chapterGuid = trimmedChapter;
      }
      if (aiLogModelId) {
        params.modelId = aiLogModelId;
      }
      if (aiLogSince) {
        params.min_timestamp = aiLogSince.toISOString();
      }
      const { data } = await adminAxios.get<{ entries: AILogEntry[]; count: number }>(
        `${API_BASE}/api/admin/ai-logs`,
        { params }
      );
      const normalizedEntries = (data.entries || []).map((entry) => ({
        ...entry,
        replacements: entry.replacements || [],
      }));
      setAiLogs(normalizedEntries);
      setAiLogsResultCount(data.count ?? data.entries?.length ?? 0);
      if (!data.entries?.length) {
        message.info('No AI logs found for the provided filters.');
      }
    } catch (error: any) {
      console.error('Failed to load AI logs', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to load AI logs';
      const messageText = typeof detail === 'string' ? detail : 'Failed to load AI logs';
      setAiLogsError(messageText);
      if (error?.response?.status === 503) {
        setAiLogsUnavailable(true);
      }
    } finally {
      setAiLogsLoading(false);
    }
  }, [
    aiLogBookId,
    aiLogChapterGuid,
    aiLogLimit,
    aiLogModelId,
    aiLogSince,
    aiLogStage,
    isAdmin,
  ]);

  const resetAiLogFilters = useCallback(() => {
    setAiLogBookId('');
    setAiLogChapterGuid('');
    setAiLogModelId(undefined);
    setAiLogLimit(50);
    setAiLogStage('validator');
    setAiLogSince(null);
    setAiLogs([]);
    setAiLogsResultCount(0);
    setAiLogsError(null);
    setAiLogsUnavailable(false);
  }, []);

  const aiLogColumns: ColumnsType<AILogEntry> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 220,
      render: (value: string) => formatAiLogTimestamp(value),
    },
    {
      title: 'Stage / Model',
      key: 'stage',
      width: 240,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.stage || '—'}</Text>
          <Text type="secondary">{record.status || 'unknown'}</Text>
          <Text type="secondary">
            {(record.provider || '—') + ' · ' + (record.model || '—')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Languages',
      key: 'languages',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            {record.sourceLanguage || '—'} → {record.targetLanguage || '—'}
          </Text>
          {record.task ? <Text type="secondary">{record.task}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Snippet',
      dataIndex: 'snippet',
      key: 'snippet',
      render: (value: string) =>
        value ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{truncateForDisplay(value, 300)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Reply',
      dataIndex: 'reply',
      key: 'reply',
      render: (value: string) =>
        value ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{truncateForDisplay(value, 300)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Replacements',
      dataIndex: 'replacements',
      key: 'replacements',
      width: 260,
      render: (value: AILogReplacement[] = []) =>
        value.length ? (
          <Space direction="vertical" size={0}>
            {value.slice(0, 3).map((rep, index) => (
              <Text key={`${rep.from}-${rep.to}-${index}`}>
                {truncateForDisplay(rep.from, 80)} → {truncateForDisplay(rep.to, 80)}
              </Text>
            ))}
            {value.length > 3 ? (
              <Text type="secondary">+{value.length - 3} more</Text>
            ) : null}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Cost (per 1M tokens)',
      dataIndex: 'cost',
      key: 'cost',
      width: 220,
      render: (value: AILogCostInfo) => formatCostInfo(value),
    },
  ];

  const transactionQueueColumns: ColumnsType<PendingTransactionLogEntry> = useMemo(
    () => [
      {
        title: 'Entry ID',
        dataIndex: 'id',
        key: 'id',
        width: 220,
        ellipsis: true,
      },
      {
        title: 'Queued',
        dataIndex: 'queuedAtISO',
        key: 'queuedAtISO',
        width: 200,
        render: (value: string | null) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—'),
      },
      {
        title: 'Attempts',
        dataIndex: 'attempts',
        key: 'attempts',
        width: 100,
        render: (value: number | undefined) => Number(value ?? 0),
      },
      {
        title: 'Last Error',
        dataIndex: 'lastError',
        key: 'lastError',
        width: 260,
        render: (value: string | undefined) =>
          value ? (
            <Tooltip title={value}>
              <Text type="danger">{truncateForDisplay(value, 80)}</Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Entry',
        key: 'entry',
        render: (_value, record) => {
          const baseSummary = [
            sentenceCase(record.entry?.type),
            typeof record.entry?.credits === 'number' ? `${record.entry.credits} credits` : null,
            record.entry?.description,
          ]
            .filter(Boolean)
            .join(' • ');
          const batchId = record.entry?.batchId || record.entry?.batch_id;
          return (
            <Space direction="vertical" size={0} style={{ maxWidth: 360 }}>
              <Text>{baseSummary || '—'}</Text>
              {batchId ? <Text type="secondary">Batch: {batchId}</Text> : null}
            </Space>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 210,
        render: (_value, record) => (
          <Space wrap>
            <Button size="small" onClick={() => handleViewTransactionQueueEntry(record)}>
              View
            </Button>
            <Button
              size="small"
              type="primary"
              loading={transactionQueueFlushing}
              onClick={() => void flushTransactionQueueEntries([record.id])}
            >
              Flush
            </Button>
          </Space>
        ),
      },
    ],
    [flushTransactionQueueEntries, handleViewTransactionQueueEntry, transactionQueueFlushing]
  );

  const deleteTargetUserId = userInfo?.userId || '';
  const deleteTargetEmail = userInfo?.email || '';
  const deleteConfirmIdMatches = deleteTargetUserId
    ? deleteUserConfirmId.trim() === deleteTargetUserId
    : false;
  const deleteConfirmEmailMatches = deleteTargetEmail
    ? deleteUserConfirmEmail.trim().toLowerCase() === deleteTargetEmail.toLowerCase()
    : true;
  const deleteConfirmReady = deleteConfirmIdMatches && deleteConfirmEmailMatches;

  return (
    <div className={`admin-management-tab admin-management-tab--${mode}`}>
      <div className="admin-management-header">
        <div>
          <span className="admin-management-kicker">
            {mode === 'support' ? 'Support workspace' : 'Admin workspace'}
          </span>
          <h3>{mode === 'support' ? 'Support Operations' : 'Management And Operations'}</h3>
        </div>
        <div className="admin-management-header__meta">
          <Tag>{availableTabs.length} workflows</Tag>
          <Tag>{mode}</Tag>
        </div>
      </div>

      {!(hideTabsWhenSingle && availableTabs.length <= 1) && (
        <Tabs
          className="admin-management-tabs"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TabKey)}
        >
          {availableTabs.map((tab) => (
            <TabPane tab={tab.label} key={tab.key} />
          ))}
        </Tabs>
      )}

      {activeTab === 'user' && (
        <Spin spinning={isGlobalLoading}>
          <Space className="admin-management-section-stack" direction="vertical" size="large">
            <Card title="Lookup User">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Row gutter={[16, 16]} justify="center">
                  <Col xs={24} md={12} lg={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong>User Email</Text>
                      <Input
                        placeholder="Enter User Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onPressEnter={() => {
                          void fetchUserInfo();
                        }}
                      />
                      <Button type="primary" onClick={fetchUserInfo}>
                        Lookup User
                      </Button>
                    </Space>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong>User ID</Text>
                      <Input
                        placeholder="Enter User ID"
                        value={userIdSearch}
                        onChange={(e) => setUserIdSearch(e.target.value)}
                        onPressEnter={() => {
                          void fetchUserInfoById();
                        }}
                      />
                      <Button type="primary" onClick={fetchUserInfoById}>
                        Lookup User
                      </Button>
                    </Space>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong>Affiliate Code</Text>
                      <Input
                        placeholder="Enter Affiliate Code"
                        value={affiliateCodeSearch}
                        onChange={(e) => setAffiliateCodeSearch(sanitizeReferralCodeInput(e.target.value))}
                        maxLength={15}
                        onPressEnter={() => {
                          void fetchUserInfoByAffiliate();
                        }}
                      />
                      <Space>
                        <Button type="primary" onClick={fetchUserInfoByAffiliate}>
                          Lookup User
                        </Button>
                        <Button onClick={handleReferralCodeLookup} loading={referralCodeSearchLoading}>
                          Lookup Metadata
                        </Button>
                      </Space>
                    </Space>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong>Stripe Customer ID</Text>
                      <Input
                        placeholder="Enter Stripe Customer ID"
                        value={stripeCustomerIdSearch}
                        onChange={(e) => setStripeCustomerIdSearch(e.target.value)}
                        onPressEnter={() => {
                          void fetchUserInfoByStripeCustomer();
                        }}
                      />
                      <Button type="primary" onClick={fetchUserInfoByStripeCustomer}>
                        Lookup User
                      </Button>
                    </Space>
                  </Col>
                </Row>
                {referralCodeSearchLoading ? (
                  <Spin size="small" />
                ) : referralCodeSearchError ? (
                  <Alert type="error" showIcon message={referralCodeSearchError} />
                ) : referralCodeSearchResult ? (
                  <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="Referral Code">{referralCodeSearchResult.code}</Descriptions.Item>
                    <Descriptions.Item label="Owner User ID">{referralCodeSearchResult.ownerUserId || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Owner Email">{referralCodeSearchResult.ownerEmail || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Custom Code">{referralCodeSearchResult.isCustom ? 'Yes' : 'No'}</Descriptions.Item>
                    <Descriptions.Item label="Created">{referralCodeSearchResult.createdAt || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Updated">{referralCodeSearchResult.updatedAt || '—'}</Descriptions.Item>
                  </Descriptions>
                ) : null}
              </Space>
            </Card>
            {userInfo ? (
              <Card
                title={`User Detail — ${userInfo.email || userInfo.userId}`}
              >
                <Tabs
                  activeKey={userDetailTab}
                  onChange={(key) => setUserDetailTab(key as UserDetailTabKey)}
                >
                  <TabPane tab="Overview" key="overview">
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="User ID">{userInfo.userId || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Email">{userInfo.email || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Plan Name">{formatStoredPlanName(userInfo.planName, subscriptionPlanDetails)}</Descriptions.Item>
                        <Descriptions.Item label="Subscription Plan">
                          {subscriptionPlanLoading ? <Spin size="small" /> : subscriptionPlanDetails?.planName || 'No active subscription'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Subscription Level">
                          {subscriptionPlanLoading ? <Spin size="small" /> : formatSubscriptionLevel(subscriptionPlanDetails)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Subscription Price">
                          {subscriptionPlanLoading
                            ? <Spin size="small" />
                            : formatSubscriptionPrice(subscriptionPlanDetails?.price, subscriptionPlanDetails?.currency)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Billing Interval">
                          {subscriptionPlanLoading
                            ? <Spin size="small" />
                            : formatSubscriptionInterval(subscriptionPlanDetails?.billingInterval, subscriptionPlanDetails?.intervalMonths)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Credits Per Interval">
                          {subscriptionPlanLoading ? <Spin size="small" /> : formatCreditsPerInterval(subscriptionPlanDetails)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Monthly Equivalent Credits">
                          {subscriptionPlanLoading
                            ? <Spin size="small" />
                            : typeof subscriptionPlanDetails?.monthlyEquivalentCredits === 'number'
                            ? subscriptionPlanDetails.monthlyEquivalentCredits.toLocaleString()
                            : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Rollover Period">
                          {subscriptionPlanLoading ? <Spin size="small" /> : formatRolloverPeriod(subscriptionPlanDetails)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Stripe Customer IDs">
                          {renderStripeCustomerIDs(userInfo.stripeCustomerID)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Stripe Product ID">{userInfo.stripeProductID || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Stripe Subscription ID">{userInfo.stripeSubscriptionID || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Affiliate Code">{userInfo.affiliateCode || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Affiliate Code ID">{userInfo.affiliateCodeID || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Beta Access">{userInfo.betaAccess ? 'Yes' : 'No'}</Descriptions.Item>
                        <Descriptions.Item label="Monthly Credits">{userInfo.monthlyCredits ?? 0}</Descriptions.Item>
                        <Descriptions.Item label="Legacy Rollover Credits">{userInfo.rolloverCredits ?? 0}</Descriptions.Item>
                        <Descriptions.Item label="Forever Credits">{userInfo.foreverCredits ?? 0}</Descriptions.Item>
                        <Descriptions.Item label="Total Credits">
                          {(userInfo.monthlyCredits ?? 0) +
                            (userInfo.rolloverCredits ?? 0) +
                            (userInfo.foreverCredits ?? 0)}
                        </Descriptions.Item>
                      </Descriptions>
                      {subscriptionPlanError ? (
                        <Alert type="warning" showIcon message={subscriptionPlanError} />
                      ) : null}
                      {creditStats ? (
                        <Descriptions size="small" column={1} bordered>
                          <Descriptions.Item label="Pending Credits (holds)">
                            {Number(creditStats.pending_credits ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Available Balance">
                            {Number((creditStats.available_balance ?? creditStats.credit_balance) ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Credit Balance">
                            {Number(creditStats.credit_balance ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Monthly Allocation">
                            {Number(creditStats.monthly_credits_allocated ?? 0).toLocaleString()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Next Expiring Batch">
                            {nextExpiringBatch?.expiresAtISO
                              ? `${dayjs(nextExpiringBatch.expiresAtISO).format('MMM D, YYYY h:mm A')} · ${(nextExpiringBatch.remainingCredits ?? 0).toLocaleString()} credits`
                              : '—'}
                          </Descriptions.Item>
                        </Descriptions>
                      ) : null}
                      <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Pause Status">
                          {formatPauseStatusLabel(userInfo.pauseStatus)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Started">
                          {formatEpochSeconds(userInfo.pauseStatus?.pauseStartedAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Resumes">
                          {formatEpochSeconds(userInfo.pauseStatus?.pauseResumesAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Length">
                          {formatPauseLengthLabel(userInfo.pauseStatus)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Requested Skip Count">
                          {formatOptionalNumber(userInfo.pauseStatus?.pauseRequestedSkipCount)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Skips Used (Rolling 12 mo)">
                          {formatOptionalNumber(userInfo.pauseStatus?.pauseSkipsUsedInWindow)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Skips Remaining (Rolling 12 mo)">
                          {formatOptionalNumber(userInfo.pauseStatus?.pauseSkipsRemainingInWindow)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pause Skips Used (Total)">
                          {formatOptionalNumber(userInfo.pauseStatus?.pauseSkipsUsedTotal)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Next Pause Skip Available">
                          {formatEpochSeconds(userInfo.pauseStatus?.pauseNextSkipAvailableAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last Resumed">
                          {formatEpochSeconds(userInfo.pauseStatus?.pauseLastResumedAt)}
                        </Descriptions.Item>
                      </Descriptions>
                      <Space wrap>
                        <Button onClick={fetchTransactions}>View Transactions</Button>
                        <Button onClick={fetchBooks}>View Books</Button>
                        <Button
                          type="primary"
                          loading={isCopyingAccountRules}
                          disabled={isCopyingAccountRules || !userInfo.userId}
                          onClick={copyAccountTranslationRulesToAdmin}
                        >
                          Copy Translation Rules
                        </Button>
                        <Button onClick={handleAddCredits}>Manage Credits</Button>
                        <Button onClick={openWebhookTestModal} disabled={!userInfo.userId}>
                          Test Subscription Webhook
                        </Button>
                        <Button onClick={() => fetchHolds()}>Manage Credit Holds</Button>
                        {isAdmin && (
                          <Button
                            danger
                            onClick={() => {
                              setDeleteUserConfirmId('');
                              setDeleteUserConfirmEmail('');
                              setDeleteUserPreview(null);
                              setIsDeleteUserModalOpen(true);
                            }}
                            disabled={!userInfo.userId}
                          >
                            Delete User Data
                          </Button>
                        )}
                      </Space>
                      <Divider />
                      <Space direction="vertical" size="small" style={{ maxWidth: 420 }}>
                        <Text strong>Verify Email</Text>
                        <Text type="secondary">Mark an address as verified for this user.</Text>
                        <Space wrap>
                          <Input
                            placeholder="Enter email to mark verified"
                            value={verifyEmailInput}
                            onChange={(e) => setVerifyEmailInput(e.target.value)}
                            style={{ width: 320 }}
                          />
                          <Button type="primary" onClick={handleVerifyEmail} loading={verifyEmailLoading}>
                            Mark Verified
                          </Button>
                        </Space>
                      </Space>
                    </Space>
                  </TabPane>
                  <TabPane tab="Credits" key="credits">
                    <Spin spinning={subscriptionBatchesLoading}>
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Text strong>Subscription Credit Batches</Text>
                          <Text type="secondary">
                            Adjust or create subscription credit batches. These changes immediately affect the user's available credits.
                          </Text>
                          <Space wrap>
                            <Tag color="blue">
                              Active Credits: {subscriptionBatchSummary.total_active_credits.toLocaleString()}
                            </Tag>
                            <Tag color="geekblue">
                              Total Granted: {subscriptionBatchSummary.total_granted_credits.toLocaleString()}
                            </Tag>
                            <Tag color="volcano">
                              Expired Logged: {Number(subscriptionBatchSummary.total_expired_credits ?? 0).toLocaleString()}
                            </Tag>
                            {nextExpiringBatch?.expiresAtISO && (
                              <Tag color="red">
                                Next Expiring: {dayjs(nextExpiringBatch.expiresAtISO).format('MMM D, YYYY h:mm A')} ({(nextExpiringBatch.remainingCredits ?? 0).toLocaleString()} credits)
                              </Tag>
                            )}
                            <Button onClick={() => userInfo?.userId && loadSubscriptionBatches(userInfo.userId)}>
                              Refresh Batches
                            </Button>
                            <Button
                              onClick={async () => {
                                if (!userInfo?.userId) {
                                  return;
                                }
                                const snapshot = await loadCreditSnapshot(userInfo.userId);
                                await loadSubscriptionPlanDetails(userInfo, snapshot);
                              }}
                            >
                              Refresh Snapshot
                            </Button>
                            <Button onClick={() => setIsCreateBatchModalVisible(true)} type="primary">
                              Create Batch
                            </Button>
                          </Space>
                        </Space>
                        <Table<SubscriptionCreditBatch>
                          dataSource={subscriptionBatches}
                          columns={subscriptionBatchColumns}
                          pagination={false}
                          rowKey={(record) => record.key || record.batchId || Math.random().toString(36)}
                        />
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Text strong>Expired Subscription Credit Batches</Text>
                          <Text type="secondary">
                            Historical batch-expiry transactions are shown here even after the batch has been removed from the active balance.
                          </Text>
                          <Table<SubscriptionCreditBatch>
                            dataSource={expiredSubscriptionBatches}
                            columns={expiredSubscriptionBatchColumns}
                            pagination={false}
                            rowKey={(record) => record.key || record.batchId || record.expiredAtISO || Math.random().toString(36)}
                          />
                        </Space>
                        <Divider />
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div>
                            <Text strong>Forever Credit Adjustments</Text>
                          </div>
                          <Text type="secondary">
                            Monthly and rollover credits are read-only. Use this section to add or deduct forever credits that are not tied to subscription batches.
                          </Text>
                          <Descriptions size="small" bordered column={1}>
                            <Descriptions.Item label="Monthly Credits">{userInfo?.monthlyCredits ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Legacy Rollover Credits">{userInfo?.rolloverCredits ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Forever Credits">{userInfo?.foreverCredits ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Total Legacy Credits">
                              {(userInfo?.monthlyCredits || 0) +
                                (userInfo?.rolloverCredits || 0) +
                                (userInfo?.foreverCredits || 0)}
                            </Descriptions.Item>
                          </Descriptions>
                        </Space>
                        <div style={{ marginTop: 16, marginBottom: 16 }}>
                          <label htmlFor="creditAction">Forever Credit Action:</label>
                          <Select
                            id="creditAction"
                            value={creditAction}
                            onChange={(value) => setCreditAction(value)}
                            style={{ width: '100%' }}
                          >
                            <Select.Option value="add">Add</Select.Option>
                            <Select.Option value="deduct">Deduct</Select.Option>
                          </Select>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <label htmlFor="creditsToAdd">Number of Credits:</label>
                          <InputNumber
                            id="creditsToAdd"
                            min={1}
                            value={creditsToAdd}
                            onChange={(value) => setCreditsToAdd(value)}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <label htmlFor="creditReason">Credit Reason:</label>
                          <AutoComplete
                            id="creditReason"
                            value={creditReason}
                            options={foreverCreditReasonOptions}
                            onSelect={(value) => setCreditReason(value)}
                            onChange={(value) => setCreditReason(value || '')}
                            style={{ width: '100%' }}
                            filterOption={(inputValue, option) => {
                              const optionValue =
                                typeof option?.value === 'string'
                                  ? option.value
                                  : option?.value != null
                                  ? String(option.value)
                                  : '';
                              return optionValue.toLowerCase().includes(inputValue.toLowerCase());
                            }}
                          >
                            <Input placeholder="Select or enter a reason" />
                          </AutoComplete>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Button type="primary" onClick={submitCreditTransaction} loading={isGlobalLoading}>
                            Update Forever Credits
                          </Button>
                        </div>
                      </Space>
                    </Spin>
                  </TabPane>
                  <TabPane tab="Holds" key="holds">
                    <Spin spinning={holdsLoading}>
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Space wrap>
                          <Button onClick={() => fetchHolds(undefined)} style={{ marginRight: 8 }}>
                            All
                          </Button>
                          <Button onClick={() => fetchHolds('held')} style={{ marginRight: 8 }}>
                            Held
                          </Button>
                          <Button onClick={() => fetchHolds('partial_committed')} style={{ marginRight: 8 }}>
                            Partial Committed
                          </Button>
                          <Button onClick={() => fetchHolds('committed')} style={{ marginRight: 8 }}>
                            Committed
                          </Button>
                          <Button onClick={() => fetchHolds('released')} style={{ marginRight: 8 }}>
                            Released
                          </Button>
                          <Button onClick={() => fetchHolds('expired')}>
                            Expired
                          </Button>
                          <Button
                            type="primary"
                            danger
                            onClick={releaseAllHolds}
                            disabled={!hasReleasableHolds || holdsLoading}
                            style={{ marginLeft: 16 }}
                          >
                            Release All Holds
                          </Button>
                          <Button onClick={() => fetchHolds(holdsStatusFilter)} disabled={holdsLoading} style={{ marginLeft: 8 }}>
                            Refresh
                          </Button>
                        </Space>
                        <Table
                          dataSource={holds}
                          rowKey={(r: any) => r.id}
                          scroll={{ x: 900, y: 360 }}
                          columns={[
                            {
                              title: 'ID',
                              dataIndex: 'id',
                              key: 'id',
                              width: 220,
                              ellipsis: true,
                              sorter: (a: any, b: any) => compareStrings(a.id, b.id),
                            },
                            {
                              title: 'Amount',
                              dataIndex: 'amount',
                              key: 'amount',
                              width: 100,
                              sorter: (a: any, b: any) =>
                                Number(a.amount ?? 0) - Number(b.amount ?? 0),
                            },
                            {
                              title: 'Remaining',
                              dataIndex: 'remaining_amount',
                              key: 'remaining_amount',
                              width: 120,
                              sorter: (a: any, b: any) =>
                                Number(a.remaining_amount ?? a.remainingAmount ?? 0) -
                                Number(b.remaining_amount ?? b.remainingAmount ?? 0),
                            },
                            {
                              title: 'Status',
                              dataIndex: 'status',
                              key: 'status',
                              width: 140,
                              sorter: (a: any, b: any) => compareStrings(a.status, b.status),
                            },
                            {
                              title: 'Job Type',
                              dataIndex: 'jobType',
                              key: 'jobType',
                              width: 140,
                              ellipsis: true,
                              sorter: (a: any, b: any) => compareStrings(a.jobType, b.jobType),
                            },
                            {
                              title: 'Job Key',
                              dataIndex: 'jobKey',
                              key: 'jobKey',
                              width: 220,
                              ellipsis: true,
                              sorter: (a: any, b: any) => compareStrings(a.jobKey, b.jobKey),
                              render: (v: any) => (
                                <Tooltip title={v}>
                                  <span
                                    style={{
                                      maxWidth: 200,
                                      display: 'inline-block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {v}
                                  </span>
                                </Tooltip>
                              ),
                            },
                            {
                              title: 'Created',
                              dataIndex: 'createdAt',
                              key: 'createdAt',
                              width: 180,
                              sorter: (a: any, b: any) =>
                                getTimestampValue(a.createdAt ?? a.created_at) -
                                getTimestampValue(b.createdAt ?? b.created_at),
                              render: (v: any) =>
                                v ? new Date(v._seconds ? v._seconds * 1000 : v).toLocaleString() : '',
                            },
                            {
                              title: 'Expires',
                              dataIndex: 'expiresAt',
                              key: 'expiresAt',
                              width: 180,
                              sorter: (a: any, b: any) =>
                                getTimestampValue(a.expiresAt ?? a.expires_at) -
                                getTimestampValue(b.expiresAt ?? b.expires_at),
                              render: (v: any) =>
                                v ? new Date(v._seconds ? v._seconds * 1000 : v).toLocaleString() : '',
                            },
                            {
                              title: 'Actions',
                              key: 'actions',
                              fixed: 'right' as any,
                              width: 240,
                              render: (_: any, rec: any) => (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    onClick={() => releaseHold(rec.id)}
                                    disabled={rec.status !== 'held' && rec.status !== 'partial_committed'}
                                  >
                                    Release
                                  </Button>
                                  <Button
                                    size="small"
                                    danger
                                    onClick={() => commitHold(rec.id)}
                                    disabled={rec.status !== 'held' && rec.status !== 'partial_committed'}
                                  >
                                    Commit
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => navigator.clipboard && navigator.clipboard.writeText(rec.id)}
                                  >
                                    Copy ID
                                  </Button>
                                </div>
                              ),
                            },
                          ]}
                          pagination={{
                            current: holdsPage,
                            pageSize: holdsPageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                          }}
                          onChange={handleHoldsTableChange}
                        />
                      </Space>
                    </Spin>
                  </TabPane>
                  <TabPane tab="Referrals" key="referrals">
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      {referralAdminLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                          <Spin size="small" />
                        </div>
                      ) : referralAdminError ? (
                        <Alert type="error" showIcon message={referralAdminError} />
                      ) : referralAdminInfo ? (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Descriptions size="small" column={1} bordered>
                            <Descriptions.Item label="Current code">
                              {referralAdminInfo.affiliateCode || '—'}
                              {referralAdminInfo.affiliateCodeIsCustom ? ' (custom)' : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Code updated">
                              {referralAdminInfo.affiliateCodeUpdatedAt || referralAdminInfo.affiliateCodeCreatedAt || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Applied code">
                              {referralAdminInfo.appliedCode || '—'}
                              {referralAdminInfo.appliedCodeOwnerEmail ? ` (owner: ${referralAdminInfo.appliedCodeOwnerEmail})` : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Credits status">
                              {referralAdminInfo.referralCreditsStatus || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Reward result">
                              {referralAdminInfo.referralRewardResult || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Reward checked">
                              {referralAdminInfo.referralRewardChecked ? 'Yes' : 'No'}
                              {referralAdminInfo.referralRewardCheckedAt ? ` (${referralAdminInfo.referralRewardCheckedAt})` : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last reward">
                              {referralAdminInfo.lastReferralRewardAt || referralAdminInfo.referralRewardAt || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Invoice ID">
                              {referralAdminInfo.referralInvoiceId || '—'}
                            </Descriptions.Item>
                          </Descriptions>
                          <Space wrap style={{ marginTop: 8 }}>
                            <Button
                              onClick={() => handleRegenerateReferralCode('random')}
                              loading={referralActionLoading}
                            >
                              Regenerate Code
                            </Button>
                            <Button
                              onClick={() => handleRegenerateReferralCode('custom')}
                              loading={referralActionLoading}
                            >
                              Regenerate (Custom)
                            </Button>
                            <Button
                              onClick={handleClearReferralRelationship}
                              disabled={!referralAdminInfo.appliedCode}
                              loading={referralActionLoading}
                            >
                              Clear Applied Code
                            </Button>
                            <Button
                              onClick={() => userInfo?.userId && loadReferralAdminStatus(userInfo.userId)}
                              disabled={referralAdminLoading}
                            >
                              Refresh
                            </Button>
                          </Space>
                        </Space>
                      ) : (
                        <Text type="secondary">Referral data unavailable.</Text>
                      )}
                    </Space>
                  </TabPane>
                </Tabs>
              </Card>
            ) : (
              <Alert type="info" showIcon message="Load a user to manage details." />
            )}

          </Space>
        </Spin>
      )}

      {activeTab === 'content' && (
        <Spin spinning={isGlobalLoading}>
          <Space className="admin-management-section-stack" direction="vertical" size="large">
            <Card title="Copy Book to Admin">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Search
                  placeholder="Enter Book GUID"
                  enterButton="Copy to Admin"
                  size="large"
                  value={bookGuidInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setBookGuidInput(normalizeBookGuid(e.target.value))
                  }
                  onSearch={(value: string) => {
                    const trimmedValue = normalizeBookGuid(value);
                    setBookGuidInput(trimmedValue);
                    handleCopyBookToAdmin(trimmedValue);
                  }}
                />
                <Space size="small" align="center">
                  <Switch checked={copySeriesData} onChange={setCopySeriesData} />
                  <Text>Copy series info and series Always/Never translate rules</Text>
                </Space>
                <Text type="secondary">
                  Keeps series-level glossary behavior visible in the copied admin book for debugging.
                </Text>
              </Space>
            </Card>

            {isAdmin && (
              <Card title="Repair Translated Chapter Formatting">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Search
                    placeholder="Enter Book GUID"
                    enterButton={formatRepairDryRun ? 'Run Dry Run' : 'Repair All Translations'}
                    size="large"
                    value={formatRepairBookGuidInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormatRepairBookGuidInput(normalizeBookGuid(e.target.value))
                    }
                    onSearch={(value: string) => {
                      const trimmedValue = normalizeBookGuid(value);
                      setFormatRepairBookGuidInput(trimmedValue);
                      handleRepairTranslatedFormatting(trimmedValue);
                    }}
                  />
                  <Space size="small" align="center">
                    <Switch checked={formatRepairDryRun} onChange={setFormatRepairDryRun} />
                    <Text>Dry run only (scan and report, no writes)</Text>
                  </Space>
                  <Text type="secondary">
                    Repairs malformed translated HTML across all translation languages for this book and stores backup fields before writing.
                  </Text>
                  {formatRepairResult && (
                    <div
                      style={{
                        background: 'var(--surface-muted)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 6,
                        padding: 12,
                        color: 'var(--text-main)',
                      }}
                    >
                      <Text>
                        Scanned: {Number(formatRepairResult.documents_scanned || 0).toLocaleString()} · Changed:{' '}
                        {Number(formatRepairResult.documents_changed || 0).toLocaleString()} · Failed:{' '}
                        {Number(formatRepairResult.failed_documents || 0).toLocaleString()}
                      </Text>
                      <pre
                        style={{
                          marginTop: 8,
                          maxHeight: 260,
                          overflow: 'auto',
                          background: 'var(--surface-base)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-main)',
                          borderRadius: 6,
                          padding: 10,
                        }}
                      >
                        {JSON.stringify(formatRepairResult, null, 2)}
                      </pre>
                    </div>
                  )}
                </Space>
              </Card>
            )}

            <Card title="Search Books by Title">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Search
                  placeholder="Enter book title or partial title"
                  value={bookTitleSearchTerm}
                  allowClear
                  loading={bookTitleSearchLoading}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const nextValue = e.target.value;
                    setBookTitleSearchTerm(nextValue);
                    if (nextValue.trim() === '') {
                      titleSearchRequestId.current += 1;
                      setBookTitleSearchResults([]);
                      setBookTitleSearchLoading(false);
                    }
                  }}
                  onSearch={(value: string) => handleBookTitleSearch(value)}
                  enterButton="Search"
                  size="large"
                />
                <Table
                  size="small"
                  columns={titleSearchColumns}
                  dataSource={bookTitleSearchResults}
                  rowKey="bookGuid"
                  pagination={false}
                  loading={bookTitleSearchLoading}
                />
              </Space>
            </Card>

          </Space>
        </Spin>
      )}

      {isAdmin && activeTab === 'ai-logs' && (
        <Spin spinning={aiLogsLoading}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="AI Validator & Proofreader Logs">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap>
                  <Input
                    placeholder="Book GUID"
                    value={aiLogBookId}
                    onChange={(e) => setAiLogBookId(e.target.value)}
                    style={{ minWidth: 220 }}
                  />
                  <Input
                    placeholder="Chapter GUID (optional)"
                    value={aiLogChapterGuid}
                    onChange={(e) => setAiLogChapterGuid(e.target.value)}
                    style={{ minWidth: 220 }}
                  />
                  <Select
                    value={aiLogStage}
                    onChange={(value) => setAiLogStage(value as 'validator' | 'proofreader')}
                    style={{ minWidth: 160 }}
                  >
                    <Option value="validator">Validator</Option>
                    <Option value="proofreader">Proofreader</Option>
                  </Select>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Filter by model"
                    value={aiLogModelId}
                    options={aiModelSelectOptions}
                    style={{ minWidth: 240 }}
                    onChange={(value) => setAiLogModelId(value as string | undefined)}
                  />
                  <DatePicker
                    showTime
                    allowClear
                    value={aiLogSince}
                    onChange={(value) => setAiLogSince(value)}
                    placeholder="Since..."
                  />
                  <InputNumber
                    min={1}
                    max={200}
                    value={aiLogLimit}
                    onChange={(value) =>
                      setAiLogLimit(typeof value === 'number' ? value : 50)
                    }
                    style={{ width: 120 }}
                  />
                  <Button type="primary" onClick={fetchAiLogs} disabled={aiLogsUnavailable}>
                    Fetch Logs
                  </Button>
                  <Button onClick={resetAiLogFilters} disabled={aiLogsUnavailable}>
                    Reset
                  </Button>
                </Space>
                {aiLogsError ? (
                  <Alert type="error" showIcon message={aiLogsError} />
                ) : null}
                {aiLogsUnavailable ? (
                  <Alert
                    type="info"
                    showIcon
                    message="AI logs are currently unavailable (Cloud Logging client not configured)."
                  />
                ) : null}
                <Space
                  direction="horizontal"
                  size="small"
                  style={{ justifyContent: 'space-between', width: '100%' }}
                >
                  <Text type="secondary">
                    Showing {formatCount(aiLogs.length)} of {formatCount(aiLogsResultCount)}{' '}
                    entries.
                  </Text>
                  <Text type="secondary">
                    Stage: {aiLogStage === 'validator' ? 'Validator' : 'Proofreader'}
                  </Text>
                </Space>
                <Table<AILogEntry>
                  columns={aiLogColumns}
                  dataSource={aiLogs}
                  rowKey={(record, index) =>
                    `${record.timestamp || record.bookId || 'row'}-${index}`
                  }
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1200 }}
                />
              </Space>
            </Card>
          </Space>
        </Spin>
      )}

      {activeTab === 'operations' && (
        <Spin spinning={isGlobalLoading}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {visibleOperationPanels.adminTools && (
              <Card title="Administration Tools">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space wrap>
                    <Button type="primary" onClick={handleDeleteOldTranslationLocks}>
                      Delete Old Translation Locks
                    </Button>
                    <Button type="primary" onClick={handleResetAIModelCache}>
                      Reset AI Model Cache
                    </Button>
                    <Button onClick={handleExportDocsToDocx} loading={docsExportLoading}>
                      Export Docs as DOCX
                    </Button>
                  </Space>
                  <Space wrap>
                    <Button type="default" onClick={() => fetchHolds()}>
                      View Credit Holds
                    </Button>
                    <Button type="default" danger onClick={expireStaleHolds}>
                      Expire Stale Holds (48h)
                    </Button>
                    <Button type="default" danger onClick={releaseAllCreditHoldsGlobal}>
                      Release All Holds (Global)
                    </Button>
                  </Space>
                </Space>
              </Card>
            )}

      {visibleOperationPanels.lockSupport && (
      <Card title="Lookup Translation Lock by Support Code" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Input
            placeholder="Enter support code"
            value={lockLookupCode}
            onChange={(e) => handleLockCodeChange(e.target.value)}
            onPressEnter={handleLookupTranslationLock}
            style={{ width: 260 }}
          />
          <Button
            type="primary"
            onClick={handleLookupTranslationLock}
            loading={lockLookupLoading}
            disabled={lockLookupCode.trim().length === 0 || lockLookupLoading}
          >
            Lookup
          </Button>
          <Button
            icon={<ClearOutlined />}
            onClick={resetLockLookup}
            disabled={lockLookupLoading || (!lockLookupCode && !lockLookupResult && !lockLookupError)}
          >
            Clear
          </Button>
        </div>
        <Spin spinning={lockLookupLoading}>
          {lockLookupResult ? (
            <div style={{ marginTop: 16 }}>
              <Descriptions
                size="small"
                bordered
                column={{ xs: 1, sm: 1, md: 2, lg: 2 }}
              >
                <Descriptions.Item label="Support Code">{formatLockValue(lockLookupResult.supportCode)}</Descriptions.Item>
                <Descriptions.Item label="Lock GUID">{formatLockValue(lockLookupResult.lockGuid)}</Descriptions.Item>
                <Descriptions.Item label="Status">{formatLockValue(lockLookupResult.status)}</Descriptions.Item>
                <Descriptions.Item label="Type">{formatLockValue(lockLookupResult.type)}</Descriptions.Item>
                <Descriptions.Item label="Cleared">{lockLookupResult.cleared ? 'Yes' : 'No'}</Descriptions.Item>

                <Descriptions.Item label="User ID">{formatLockValue(lockLookupResult.userId)}</Descriptions.Item>
                {lockLookupResult.userEmail && (
                  <Descriptions.Item label="User Email">{formatLockValue(lockLookupResult.userEmail)}</Descriptions.Item>
                )}

                <Descriptions.Item label="Book ID">{formatLockValue(lockLookupResult.bookId)}</Descriptions.Item>
                {lockLookupResult.bookTitle && (
                  <Descriptions.Item label="Book Title">{formatLockValue(lockLookupResult.bookTitle)}</Descriptions.Item>
                )}

                {lockLookupResult.chapterName && (
                  <Descriptions.Item label="Chapter Name">{formatLockValue(lockLookupResult.chapterName)}</Descriptions.Item>
                )}
                <Descriptions.Item label="Chapter GUID">{formatLockValue(lockLookupResult.chapterGuid)}</Descriptions.Item>

                <Descriptions.Item label="Source Lang">{formatLockValue(lockLookupResult.sourceLang)}</Descriptions.Item>
                <Descriptions.Item label="Target Lang">{formatLockValue(lockLookupResult.targetLang)}</Descriptions.Item>

                <Descriptions.Item label="Timestamp">{formatLockDateTime(lockLookupResult.timestamp)}</Descriptions.Item>
                <Descriptions.Item label="Failure Time">{formatLockDateTime(lockLookupResult.failureTime)}</Descriptions.Item>
                <Descriptions.Item label="Completion Time">{formatLockDateTime(lockLookupResult.completionTime)}</Descriptions.Item>
                <Descriptions.Item label="Expires At">{formatLockDateTime(lockLookupResult.expiresAt)}</Descriptions.Item>

                {lockLookupResult.creditHoldAmount !== undefined && (
                  <Descriptions.Item label="Credit Hold Amount">{formatLockValue(lockLookupResult.creditHoldAmount)}</Descriptions.Item>
                )}
                {lockLookupResult.word_count !== undefined && (
                  <Descriptions.Item label="Word Count">{formatLockValue(lockLookupResult.word_count)}</Descriptions.Item>
                )}

                {lockLookupResult.documentId && (
                  <Descriptions.Item label="Document ID" span={2}>{lockLookupResult.documentId}</Descriptions.Item>
                )}

                {lockLookupResult.error && (
                  <Descriptions.Item label="Friendly Error" span={2}>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{lockLookupResult.error}</div>
                  </Descriptions.Item>
                )}
                {lockLookupResult.errorDetails && (
                  <Descriptions.Item label="Error Details" span={2}>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{lockLookupResult.errorDetails}</pre>
                  </Descriptions.Item>
                )}
                {lockLookupResult.supportEvent && (
                  <>
                    <Descriptions.Item label="Support Event Code">
                      {formatLockValue(lockLookupResult.supportEvent.code)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Support Event Type">
                      {formatLockValue(lockLookupResult.supportEvent.eventType)}
                    </Descriptions.Item>
                    {lockLookupResult.supportEvent.message && (
                      <Descriptions.Item label="Support Message" span={2}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {formatLockValue(lockLookupResult.supportEvent.message)}
                        </div>
                      </Descriptions.Item>
                    )}
                    {lockLookupResult.supportEvent.metadata && (
                      <Descriptions.Item label="Support Metadata" span={2}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {JSON.stringify(lockLookupResult.supportEvent.metadata, null, 2)}
                        </pre>
                      </Descriptions.Item>
                    )}
                    {lockLookupResult.supportEvent.stacktrace && (
                      <Descriptions.Item label="Support Stacktrace" span={2}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {lockLookupResult.supportEvent.stacktrace}
                        </pre>
                      </Descriptions.Item>
                    )}
                  </>
                )}
              </Descriptions>
              <Space style={{ marginTop: 12 }} wrap>
                {lockLookupResult &&
                  !isTerminalLockStatus(lockLookupResult.status) && (
                    <Button
                      danger
                      onClick={() => openLockExpireModal(lockLookupResult)}
                      loading={lockExpireSubmitting}
                    >
                      Expire Lock
                    </Button>
                  )}
                {lockLookupResult.bookId && (
                  <Button
                    onClick={() => handleCopyBookToAdmin(lockLookupResult.bookId || '')}
                    loading={isGlobalLoading}
                  >
                    Copy Book to Admin
                  </Button>
                )}
                {lockLookupResult.userId && (
                  <Button
                    onClick={handleCopyTranslationRulesFromLock}
                    loading={isCopyingAccountRules}
                  >
                    Copy Translation Rules to Admin
                  </Button>
                )}
              </Space>
            </div>
          ) : lockLookupError ? (
            <div style={{ marginTop: 16, color: '#d4380d' }}>{lockLookupError}</div>
          ) : (
            <div style={{ marginTop: 16, color: 'var(--text-secondary)' }}>
              Enter a support code from a failed job to view the underlying lock details.
            </div>
          )}
        </Spin>
      </Card>
      )}


      {visibleOperationPanels.lockInspector ? <LockInspector apiBase={API_BASE} /> : null}
      {visibleOperationPanels.manifestInspector ? <ManifestInspector apiBase={API_BASE} /> : null}

      {visibleOperationPanels.lockSupport && (
      <Card title="Lookup Support Event" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Input
            placeholder="Enter support code"
            value={supportLookupCode}
            onChange={(e) => handleSupportCodeChange(e.target.value)}
            onPressEnter={handleLookupSupportEvent}
            style={{ width: 260 }}
          />
          <Button
            type="primary"
            onClick={handleLookupSupportEvent}
            loading={supportLookupLoading}
            disabled={supportLookupCode.trim().length === 0 || supportLookupLoading}
          >
            Lookup
          </Button>
          <Button
            icon={<ClearOutlined />}
            onClick={resetSupportLookup}
            disabled={
              supportLookupLoading ||
              (!supportLookupCode && !supportLookupResult && !supportLookupError)
            }
          >
            Clear
          </Button>
        </div>
        <Spin spinning={supportLookupLoading}>
          {supportLookupResult ? (
            <div style={{ marginTop: 16 }}>
              <Descriptions
                size="small"
                bordered
                column={{ xs: 1, sm: 1, md: 2, lg: 2 }}
              >
                <Descriptions.Item label="Support Code">
                  {formatLockValue(supportLookupResult.code)}
                </Descriptions.Item>
                <Descriptions.Item label="Event Type">
                  {formatLockValue(supportLookupResult.eventType)}
                </Descriptions.Item>
                <Descriptions.Item label="Message" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {formatLockValue(supportLookupResult.message)}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="User ID">
                  {formatLockValue(supportLookupResult.userId)}
                </Descriptions.Item>
                <Descriptions.Item label="Book ID">
                  {formatLockValue(supportLookupResult.bookId)}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {formatLockDateTime(supportLookupResult.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Expires">
                  {formatLockDateTime(supportLookupResult.expiresAt)}
                </Descriptions.Item>
                {supportLookupResult.metadata && (
                  <Descriptions.Item label="Metadata" span={2}>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                      {JSON.stringify(supportLookupResult.metadata, null, 2)}
                    </pre>
                  </Descriptions.Item>
                )}
                {supportLookupResult.stacktrace && (
                  <Descriptions.Item label="Stacktrace" span={2}>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                      {supportLookupResult.stacktrace}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          ) : supportLookupError ? (
            <div style={{ marginTop: 16, color: '#d4380d' }}>
              {supportLookupError}
            </div>
          ) : (
            <div style={{ marginTop: 16, color: 'var(--text-secondary)' }}>
              Enter a support code from an error dialog to view the recorded context.
            </div>
          )}
        </Spin>
      </Card>
      )}

            {visibleOperationPanels.aiModelRoles && (
            <Card title="AI Model Role Assignments" style={{ marginTop: 16 }}>
              <Spin spinning={aiModelUsageLoading}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Text type="secondary">
                    Choose which configured AI model each pipeline task should use. Roles are grouped by task category.
                    Leave a role empty to fall back to the service default.
                  </Text>
                  {normalizeNullableString(forcedPretranslationModelId) ? (
                    <Alert
                      type="info"
                      showIcon
                      message={`Pretranslation default override from AdminDashboard.tsx: ${normalizeNullableString(
                        forcedPretranslationModelId
                      )}`}
                    />
                  ) : null}
                  {[
                    normalizeNullableString(forcedTestProofreadModel1Id),
                    normalizeNullableString(forcedTestProofreadModel2Id),
                    normalizeNullableString(forcedTestProofreadModel3Id),
                  ].some(Boolean) ? (
                    <Alert
                      type="info"
                      showIcon
                      message={`Experimental post-translation test-model defaults from AdminDashboard.tsx: #1=${normalizeNullableString(
                        forcedTestProofreadModel1Id
                      ) || 'unset'}, #2=${normalizeNullableString(
                        forcedTestProofreadModel2Id
                      ) || 'unset'}, #3=${normalizeNullableString(
                        forcedTestProofreadModel3Id
                      ) || 'unset'}`}
                    />
                  ) : null}
                  {normalizeNullableString(forcedTranslationTenseGuardModelId) ? (
                    <Alert
                      type="info"
                      showIcon
                      message={`Chunk tense-guard default override from AdminDashboard.tsx: ${normalizeNullableString(
                        forcedTranslationTenseGuardModelId
                      )}`}
                    />
                  ) : null}
                  {normalizeNullableString(forcedProofreadChangeAuditModelId) ? (
                    <Alert
                      type="info"
                      showIcon
                      message={`Proofread change-audit default override from AdminDashboard.tsx: ${normalizeNullableString(
                        forcedProofreadChangeAuditModelId
                      )}`}
                    />
                  ) : null}
                  {aiModelUsageError ? (
                    <Alert type="error" showIcon message={aiModelUsageError} />
                  ) : null}
                  <Card
                    size="small"
                    title="Recommended Translation Canary"
                    bodyStyle={{ paddingTop: 12 }}
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Text type="secondary">
                        Route a book-stable percentage of Recommended translations to a candidate model.
                        The normal model remains{' '}
                        <strong>{aiModelAssignments['translation.preferred'] || 'the service default'}</strong>.
                        Set the percentage to 0 for the kill switch.
                      </Text>
                      {translationCanary.percentage > 0 ? (
                        <Alert
                          type="warning"
                          showIcon
                          message={`${translationCanary.percentage}% canary enabled`}
                          description={`Books in the canary cohort use ${translationCanary.modelId || 'no model selected'}; the decision is stored on each translation job.`}
                        />
                      ) : (
                        <Alert type="info" showIcon message="Canary disabled (0%)" />
                      )}
                      <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} md={10}>
                          <strong>Canary model</strong>
                        </Col>
                        <Col xs={24} md={14}>
                          <Select
                            aria-label="Recommended translation canary model"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select candidate model"
                            style={{ width: '100%' }}
                            value={translationCanary.modelId || undefined}
                            options={translationCanaryModelOptions}
                            onChange={(value) => {
                              setTranslationCanary((current) => ({
                                ...current,
                                modelId: (value as string | undefined) || null,
                              }));
                              setAiModelUsageDirty(true);
                            }}
                            disabled={aiModelUsageSaving}
                          />
                        </Col>
                      </Row>
                      <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} md={10}>
                          <strong>Traffic percentage</strong>
                        </Col>
                        <Col xs={24} md={14}>
                          <InputNumber
                            aria-label="Recommended translation canary percentage"
                            min={0}
                            max={100}
                            precision={2}
                            addonAfter="%"
                            value={translationCanary.percentage}
                            onChange={(value) => {
                              setTranslationCanary((current) => ({
                                ...current,
                                percentage: typeof value === 'number' ? value : 0,
                              }));
                              setAiModelUsageDirty(true);
                            }}
                            disabled={aiModelUsageSaving}
                          />
                        </Col>
                      </Row>
                    </Space>
                  </Card>
                  {aiModelUsageRoleSections.length ? (
                    aiModelUsageRoleSections.map((section) => (
                      <Card
                        key={section.key}
                        size="small"
                        title={section.title}
                        bodyStyle={{ paddingTop: 12 }}
                      >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          <Text type="secondary">{section.description}</Text>
                          {section.roles.map((role) => {
                            const currentValue = aiModelAssignments[role.id];
                            const roleOptions =
                              currentValue && !aiModelOptionSet.has(currentValue)
                                ? [
                                    ...aiModelSelectOptions,
                                    {
                                      value: currentValue,
                                      label: `${currentValue} (unknown model)`,
                                    },
                                  ]
                                : aiModelSelectOptions;
                            return (
                              <Row key={role.id} gutter={[12, 12]} align="middle">
                                <Col xs={24} md={10}>
                                  <div>
                                    <strong>{getAiRoleDisplayLabel(role)}</strong>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{role.id}</div>
                                  </div>
                                </Col>
                                <Col xs={24} md={14}>
                                  <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Use default assignment"
                                    style={{ width: '100%' }}
                                    value={currentValue || undefined}
                                    options={roleOptions}
                                    onChange={(value) =>
                                      handleAiModelAssignmentChange(role.id, value as string | undefined)
                                    }
                                    disabled={aiModelUsageSaving}
                                  />
                                </Col>
                              </Row>
                            );
                          })}
                        </Space>
                      </Card>
                    ))
                  ) : (
                    <Text type="secondary">
                      {aiModelUsageInitialized
                        ? 'No role metadata available.'
                        : 'Select the Operations tab to load role assignments.'}
                    </Text>
                  )}
                  <Space wrap>
                    <Button onClick={() => loadAiModelUsageData()} disabled={aiModelUsageLoading}>
                      Reload Roles
                    </Button>
                    <Button
                      type="primary"
                      onClick={saveAiModelUsageAssignments}
                      loading={aiModelUsageSaving}
                      disabled={!aiModelUsageDirty || aiModelUsageSaving}
                    >
                      Save Changes
                    </Button>
                  </Space>
                  {aiModelUsageUpdatedLabel && (
                    <Text type="secondary">
                      Last updated {aiModelUsageUpdatedLabel}
                      {aiModelUsageMetadata?.updatedBy ? ` · ${aiModelUsageMetadata.updatedBy}` : ''}
                    </Text>
                  )}
                </Space>
              </Spin>
            </Card>
            )}

          </Space>
        </Spin>
      )}

      {activeTab === 'monitoring' && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="Pending Credit Transaction Queue">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">
                Inspect queued ledger entries for a user and manually flush them when automatic retries fail.
              </Text>
              <Space wrap>
                <Input
                  placeholder="User ID"
                  value={transactionQueueUserId}
                  onChange={(e) => setTransactionQueueUserId(e.target.value)}
                  style={{ minWidth: 240 }}
                />
                <InputNumber
                  min={1}
                  max={200}
                  value={transactionQueueLimit}
                  onChange={(value) =>
                    setTransactionQueueLimit(typeof value === 'number' && value > 0 ? value : 25)
                  }
                  style={{ width: 140 }}
                  placeholder="Limit"
                />
                <Button type="primary" onClick={() => void fetchTransactionQueue()} loading={transactionQueueLoading}>
                  Load Queue
                </Button>
                <Button onClick={clearTransactionQueueView} disabled={transactionQueueLoading && !transactionQueueEntries.length}>
                  Clear
                </Button>
              </Space>
              {transactionQueueError ? (
                <Alert type="error" showIcon message={transactionQueueError} />
              ) : null}
              <Table<PendingTransactionLogEntry>
                size="small"
                columns={transactionQueueColumns}
                dataSource={transactionQueueEntries}
                loading={transactionQueueLoading}
                rowKey={(record) => record.id}
                pagination={{ pageSize: 10 }}
                rowSelection={{
                  selectedRowKeys: transactionQueueSelectedIds,
                  onChange: (keys) => setTransactionQueueSelectedIds(keys as string[]),
                }}
                scroll={{ x: 960 }}
              />
              <Space wrap>
                <Button
                  type="primary"
                  disabled={!transactionQueueSelectedIds.length}
                  loading={transactionQueueFlushing}
                  onClick={() => void flushTransactionQueueEntries(transactionQueueSelectedIds)}
                >
                  Flush Selected
                </Button>
                <Button
                  disabled={!transactionQueueEntries.length}
                  loading={transactionQueueFlushing}
                  onClick={() => void flushTransactionQueueEntries(undefined, { limit: transactionQueueLimit })}
                >
                  Flush Oldest {transactionQueueLimit}
                </Button>
                <Button
                  onClick={() => void fetchTransactionQueue()}
                  disabled={!transactionQueueUserId.trim()}
                  loading={transactionQueueLoading}
                >
                  Refresh
                </Button>
              </Space>
            </Space>
          </Card>

          <Card title="Recompute Legacy Subscription Batch Expiry (All Users)">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text type="secondary">
                Legacy repair for stored subscription batch expiry timestamps. Active subscription credits are protected while subscribed; cancellation starts a 60-day grace window.
                Run dry run first, then apply.
              </Text>
              <Space wrap>
                <Space align="center">
                  <Text>Max users</Text>
                  <InputNumber
                    min={1}
                    max={5000}
                    value={subscriptionExpiryRecomputeLimit}
                    onChange={(value) => {
                      const numericValue =
                        typeof value === 'number' ? value : Number(value ?? 0);
                      if (!Number.isFinite(numericValue) || numericValue <= 0) {
                        return;
                      }
                      setSubscriptionExpiryRecomputeLimit(
                        Math.min(5000, Math.max(1, Math.floor(numericValue)))
                      );
                    }}
                  />
                </Space>
                <Input
                  placeholder="Start after user ID (optional)"
                  value={subscriptionExpiryRecomputeCursor}
                  onChange={(e) => setSubscriptionExpiryRecomputeCursor(e.target.value)}
                  style={{ width: 320 }}
                />
              </Space>
              <Space wrap>
                <Button
                  onClick={() => runSubscriptionExpiryRecomputeAll(true)}
                  loading={subscriptionExpiryRecomputeLoading}
                >
                  Dry Run
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={handleApplySubscriptionExpiryRecomputeAll}
                  loading={subscriptionExpiryRecomputeLoading}
                  disabled={
                    !subscriptionExpiryRecomputeResult?.dry_run ||
                    Number(subscriptionExpiryRecomputeResult?.candidates || 0) <= 0
                  }
                >
                  Apply Updates
                </Button>
              </Space>
              {subscriptionExpiryRecomputeResult && (
                <Alert
                  type={
                    Number(subscriptionExpiryRecomputeResult.updated || 0) > 0
                      ? 'success'
                      : Number(subscriptionExpiryRecomputeResult.candidates || 0) > 0
                      ? 'warning'
                      : 'info'
                  }
                  showIcon
                  message={
                    subscriptionExpiryRecomputeResult.dry_run
                      ? `Dry run scanned ${Number(subscriptionExpiryRecomputeResult.scanned_users || 0).toLocaleString()} user(s); candidate updates ${Number(subscriptionExpiryRecomputeResult.candidates || 0).toLocaleString()}`
                      : `Applied ${Number(subscriptionExpiryRecomputeResult.updated || 0).toLocaleString()} update(s) across ${Number(subscriptionExpiryRecomputeResult.users_with_candidates || 0).toLocaleString()} user(s)`
                  }
                  description={
                    <div>
                      <div>
                        Users with batches:{' '}
                        {Number(subscriptionExpiryRecomputeResult.users_with_batches || 0).toLocaleString()}
                      </div>
                      <div>
                        Users with candidates:{' '}
                        {Number(subscriptionExpiryRecomputeResult.users_with_candidates || 0).toLocaleString()}
                      </div>
                      <div>
                        Batches scanned:{' '}
                        {Number(subscriptionExpiryRecomputeResult.scanned_batches || 0).toLocaleString()}
                      </div>
                      <div>
                        Skipped (would shorten expiry):{' '}
                        {Number(subscriptionExpiryRecomputeResult.skipped_shorter || 0).toLocaleString()}
                      </div>
                      <div>
                        Large changes (&gt;{' '}
                        {Number(subscriptionExpiryRecomputeResult.large_change_days_threshold || 183).toLocaleString()} days):{' '}
                        {Number(subscriptionExpiryRecomputeResult.large_change_count || 0).toLocaleString()}
                      </div>
                      {subscriptionExpiryRecomputeResult.hit_limit ? (
                        <div style={{ marginTop: 8 }}>
                          <Tag color="orange">
                            Hit max users limit. Continue from cursor {subscriptionExpiryRecomputeResult.next_cursor || '—'}
                          </Tag>
                        </div>
                      ) : null}
                      {Array.isArray(subscriptionExpiryRecomputeResult.examples) &&
                        subscriptionExpiryRecomputeResult.examples.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Examples:</strong>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {subscriptionExpiryRecomputeResult.examples.slice(0, 5).map((example, idx) => (
                                <li key={`${example.user_id || 'user'}-${example.batch_id}-${idx}`}>
                                  <div>
                                    <code>{example.user_id || 'unknown-user'}</code>
                                    {' · '}
                                    <code>{example.batch_id}</code>
                                    {`: ${example.current_expires_at || '—'} -> ${example.proposed_expires_at}`}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#666' }}>
                                    Plan:{' '}
                                    {example.resolved_plan_name ||
                                      example.resolved_plan_id ||
                                      example.plan_id ||
                                      'unknown'}
                                    {' · '}
                                    Issued: {example.granted_at || '—'}
                                    {' · '}
                                    Calc: {example.expiry_formula_detail || example.expiry_formula || '—'}
                                    {' · '}
                                    Delta:{' '}
                                    {typeof example.delta_days === 'number'
                                      ? `${example.delta_days > 0 ? '+' : ''}${example.delta_days} days (${example.change_direction || 'change'})`
                                      : '—'}
                                    {example.would_shorten ? ' · would shorten' : ''}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      {Array.isArray(subscriptionExpiryRecomputeResult.large_change_examples) &&
                        subscriptionExpiryRecomputeResult.large_change_examples.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>
                              Large Change Examples (&gt;
                              {Number(subscriptionExpiryRecomputeResult.large_change_days_threshold || 183).toLocaleString()}
                              {' '}
                              days):
                            </strong>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {subscriptionExpiryRecomputeResult.large_change_examples.slice(0, 5).map((example, idx) => (
                                <li key={`large-${example.user_id || 'user'}-${example.batch_id}-${idx}`}>
                                  <div>
                                    <code>{example.user_id || 'unknown-user'}</code>
                                    {' · '}
                                    <code>{example.batch_id}</code>
                                    {`: ${example.current_expires_at || '—'} -> ${example.proposed_expires_at}`}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#666' }}>
                                    Plan:{' '}
                                    {example.resolved_plan_name ||
                                      example.resolved_plan_id ||
                                      example.plan_id ||
                                      'unknown'}
                                    {' · '}
                                    Issued: {example.granted_at || '—'}
                                    {' · '}
                                    Calc: {example.expiry_formula_detail || example.expiry_formula || '—'}
                                    {' · '}
                                    Delta:{' '}
                                    {typeof example.delta_days === 'number'
                                      ? `${example.delta_days > 0 ? '+' : ''}${example.delta_days} days (${example.change_direction || 'change'})`
                                      : '—'}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      {Array.isArray(subscriptionExpiryRecomputeResult.errors) &&
                        subscriptionExpiryRecomputeResult.errors.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Errors ({subscriptionExpiryRecomputeResult.errors.length}):</strong>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {subscriptionExpiryRecomputeResult.errors.slice(0, 3).map((errMsg, idx) => (
                                <li key={idx}>{errMsg}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  }
                />
              )}
            </Space>
          </Card>

          <Card title="Repair Failed Processing States">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text type="secondary">
                Scan failed translation locks whose processing state is still marked as active and force
                them into a failed state so the Processing Status panel stays accurate.
              </Text>
              <Space align="center" wrap>
                <span>Scan limit:</span>
                <InputNumber
                  min={50}
                  max={5000}
                  step={50}
                  value={lockStateBackfillLimit}
                  onChange={(value) => {
                    const numericValue =
                      typeof value === 'number' ? value : Number(value ?? 0);
                    if (!Number.isFinite(numericValue) || numericValue <= 0) {
                      return;
                    }
                    const safeValue = Math.min(5000, Math.max(50, Math.floor(numericValue)));
                    setLockStateBackfillLimit(safeValue);
                  }}
                />
              </Space>
              <Space wrap>
                <Button
                  onClick={() => handleProcessingStateRepair(true)}
                  loading={lockStateBackfillLoading}
                >
                  Dry Run
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={() => handleProcessingStateRepair(false)}
                  loading={lockStateBackfillLoading}
                >
                  Apply Fix
                </Button>
              </Space>
              {lockStateBackfillResult && (
                <Alert
                  type={Number(lockStateBackfillResult.updated || 0) > 0 ? 'success' : 'info'}
                  showIcon
                  message={`Scanned ${Number(lockStateBackfillResult.scanned || 0).toLocaleString()} lock(s); candidates ${Number(
                    lockStateBackfillResult.candidates || 0
                  ).toLocaleString()}`}
                  description={
                    <div>
                      <div>
                        Updated:{' '}
                        {Number(lockStateBackfillResult.updated || 0).toLocaleString()}
                      </div>
                      <div>
                        Already failed state:{' '}
                        {Number(lockStateBackfillResult.already_failed_state || 0).toLocaleString()}
                      </div>
                      <div>
                        Skipped (terminal state):{' '}
                        {Number(lockStateBackfillResult.skipped_terminal_state || 0).toLocaleString()}
                      </div>
                      {Array.isArray(lockStateBackfillResult.examples) &&
                        lockStateBackfillResult.examples.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Examples:</strong>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {lockStateBackfillResult.examples.slice(0, 3).map((example, idx) => (
                                <li key={`${example.document_id}-${idx}`}>
                                  <code>{example.lock_guid || example.document_id}</code>
                                  {example.previous_processing_state
                                    ? ` · previous state: ${example.previous_processing_state}`
                                    : ''}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      {Array.isArray(lockStateBackfillResult.errors) &&
                        lockStateBackfillResult.errors.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Errors ({lockStateBackfillResult.errors.length}):</strong>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {lockStateBackfillResult.errors.slice(0, 3).map((errMsg, idx) => (
                                <li key={idx}>{errMsg}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  }
                />
              )}
            </Space>
          </Card>

          <Card title="Backfill Uncategorized Usage">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text type="secondary">
                Reclassify historical <code>model_usage</code> rows with <code>usage_category=uncategorized</code> using stage/call metadata.
              </Text>
              <Space wrap>
                <Space align="center">
                  <Text>Days</Text>
                  <InputNumber
                    min={1}
                    max={365}
                    value={uncategorizedUsageDays}
                    onChange={(value) =>
                      setUncategorizedUsageDays(
                        typeof value === 'number' && Number.isFinite(value) ? value : 365
                      )
                    }
                  />
                </Space>
                <Space align="center">
                  <Text>Limit</Text>
                  <InputNumber
                    min={1}
                    max={200000}
                    step={1000}
                    value={uncategorizedUsageLimit}
                    onChange={(value) =>
                      setUncategorizedUsageLimit(
                        typeof value === 'number' && Number.isFinite(value) ? value : 20000
                      )
                    }
                  />
                </Space>
                <Input
                  placeholder="Provider (optional)"
                  value={uncategorizedUsageProvider}
                  onChange={(e) => setUncategorizedUsageProvider(e.target.value)}
                  style={{ width: 200 }}
                />
                <Input
                  placeholder="Model (optional)"
                  value={uncategorizedUsageModel}
                  onChange={(e) => setUncategorizedUsageModel(e.target.value)}
                  style={{ width: 260 }}
                />
              </Space>
              <Space wrap>
                <Button
                  onClick={() => handleUncategorizedUsageBackfill(true)}
                  loading={uncategorizedUsageBackfillLoading}
                >
                  Dry Run
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={() => handleUncategorizedUsageBackfill(false)}
                  loading={uncategorizedUsageBackfillLoading}
                >
                  Apply Backfill
                </Button>
              </Space>
              {uncategorizedUsageBackfillResult && (
                <Alert
                  type={Number(uncategorizedUsageBackfillResult.applied || 0) > 0 ? 'success' : 'info'}
                  showIcon
                  message={`Scanned ${Number(
                    uncategorizedUsageBackfillResult.scanned || 0
                  ).toLocaleString()} record(s); candidates ${Number(
                    uncategorizedUsageBackfillResult.uncategorized_candidates || 0
                  ).toLocaleString()}`}
                  description={
                    <div>
                      <div>
                        Reclassifiable:{' '}
                        {Number(uncategorizedUsageBackfillResult.reclassified || 0).toLocaleString()}
                      </div>
                      <div>
                        Applied:{' '}
                        {Number(uncategorizedUsageBackfillResult.applied || 0).toLocaleString()}
                      </div>
                      <div>
                        Unchanged:{' '}
                        {Number(uncategorizedUsageBackfillResult.unchanged || 0).toLocaleString()}
                      </div>
                      <div>
                        Errors:{' '}
                        {Number(uncategorizedUsageBackfillResult.errors || 0).toLocaleString()}
                      </div>
                      {uncategorizedUsageBackfillResult.hit_limit ? (
                        <div style={{ marginTop: 8 }}>
                          <Tag color="orange">Hit scan limit; run again to continue.</Tag>
                        </div>
                      ) : null}
                      {Array.isArray(uncategorizedUsageBackfillResult.samples) &&
                        uncategorizedUsageBackfillResult.samples.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Samples:</strong>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {uncategorizedUsageBackfillResult.samples.slice(0, 5).map((sample, idx) => (
                                <li key={`${sample.doc_id || 'sample'}-${idx}`}>
                                  <code>{sample.doc_id || 'missing-doc-id'}</code>
                                  {' · '}
                                  {(sample.process || 'other') + '/' + (sample.subprocess || 'uncategorized')}
                                  {' · '}
                                  {(sample.previous_usage_category || 'uncategorized')} →{' '}
                                  {(sample.suggested_usage_category || 'uncategorized')}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  }
                />
              )}
            </Space>
          </Card>
        </Space>
      )}

      <Modal
        title="Expire Translation Lock"
        visible={lockExpireModalVisible}
        onCancel={closeLockExpireModal}
        onOk={submitLockExpire}
        okButtonProps={{ danger: true }}
        confirmLoading={lockExpireSubmitting}
        okText="Expire lock"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="This will mark the lock as expired, release holds, and refund any charges."
          />
          <div>
            <Text strong>Support Code:</Text> <Text code>{lockExpireTarget?.lockGuid || '—'}</Text>
          </div>
          <Input.TextArea
            rows={4}
            value={lockExpireReason}
            onChange={(e) => setLockExpireReason(e.target.value)}
            placeholder="Reason shown to the user (optional)"
          />
        </Space>
      </Modal>

        <Modal
          title="Simulate Stripe Subscription Webhook"
          visible={isWebhookModalVisible}
          onCancel={closeWebhookTestModal}
          footer={[
            <Button key="cancel" onClick={closeWebhookTestModal} disabled={webhookLoading}>
              Cancel
            </Button>,
            <Button
              key="run"
              type="primary"
              onClick={runWebhookSimulation}
              loading={webhookLoading}
              disabled={!userInfo?.userId}
            >
              Run Simulation
            </Button>,
          ]}
          width={600}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Select scenario</Text>
              <Radio.Group
                style={{ marginTop: 8 }}
                onChange={(event) => setWebhookScenario(event.target.value)}
                value={webhookScenario}
              >
                <Radio.Button value="subscription_create">Subscription Start</Radio.Button>
                <Radio.Button value="subscription_renewal">Renewal</Radio.Button>
                <Radio.Button value="subscription_update">Plan Change</Radio.Button>
                <Radio.Button value="subscription_cancel">Cancel Subscription</Radio.Button>
              </Radio.Group>
            </div>
            <div>
              <Text strong>Choose plan</Text>
              <Select
                allowClear
                showSearch
                placeholder="Defaults to the user’s current plan"
                value={webhookPlanId || undefined}
                onChange={(value) => handleWebhookPlanChange(value)}
                optionFilterProp="label"
                optionLabelProp="label"
                loading={webhookPlansLoading}
                disabled={webhookPlansLoading || (!webhookPlanRecords.length && !webhookPlansError)}
                style={{ width: '100%', marginTop: 8 }}
              >
                {webhookPlanOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.searchLabel}>
                    <div>
                      <div>{option.title}</div>
                      {option.subtitle ? (
                        <div style={{ fontSize: 12, color: '#666' }}>{option.subtitle}</div>
                      ) : null}
                      {option.tags.length > 0 ? (
                        <div style={{ marginTop: 4 }}>
                          <Space size={4} wrap>
                            {option.tags.map((tag) => (
                              <Tag key={`${option.value}-${tag.label}`} color={tag.color}>
                                {tag.label}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      ) : null}
                    </div>
                  </Select.Option>
                ))}
              </Select>
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {webhookPlanId
                  ? 'The simulated payload will use this plan’s Stripe IDs.'
                  : 'Leave blank to reuse the user’s current plan.'}
              </Text>
              {webhookPlansError ? (
                <Alert type="warning" showIcon style={{ marginTop: 8 }} message={webhookPlansError} />
              ) : null}
              {!webhookPlansLoading && !webhookPlansError && !webhookPlanRecords.length ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="No subscription plans were returned from the API."
                />
              ) : null}
            </div>
            {webhookScenario !== 'subscription_cancel' && (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="Override amount (USD, optional)"
                  value={typeof webhookAmount === 'number' ? webhookAmount : undefined}
                  onChange={(value) => setWebhookAmount(typeof value === 'number' ? value : null)}
                />
                <Input
                  placeholder="Currency override (e.g. usd) – optional"
                  value={webhookCurrency}
                  onChange={(event) => setWebhookCurrency(event.target.value)}
                />
              </Space>
            )}
            <Input
              placeholder="Custom invoice ID (optional)"
              value={webhookInvoiceId}
              onChange={(event) => setWebhookInvoiceId(event.target.value)}
            />
            <Input
              placeholder="Apply referral code before simulation (optional)"
              value={webhookReferralCode}
              onChange={(event) => setWebhookReferralCode(event.target.value)}
            />
            <Alert
              type="info"
              showIcon
              message="This reuses the live Stripe webhook handler. Pick a plan to mimic upgrades or downgrades without touching real Stripe data."
            />
            {webhookError && <Alert type="error" showIcon message={webhookError} />}
            {webhookResult && (
              <div>
                <Alert
                  type="success"
                  showIcon
                  message="Webhook processed"
                  description={`Event ${webhookResult.event_id} (${webhookResult.event_type}) executed.`}
                  style={{ marginBottom: 12 }}
                />
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Scenario">{webhookResult.scenario}</Descriptions.Item>
                  {selectedWebhookPlan ? (
                    <Descriptions.Item label="Simulated Plan">
                      {selectedWebhookPlan.planName}
                    </Descriptions.Item>
                  ) : null}
                  <Descriptions.Item label="Plan ID">
                    {webhookResult.plan_id || selectedWebhookPlan?.id || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Invoice ID">{webhookResult.invoice_id || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Subscription ID">{webhookResult.subscription_id || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Customer ID">{webhookResult.customer_id || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Referral Code Applied">
                    {webhookResult.referral_code_applied || '—'}
                  </Descriptions.Item>
                </Descriptions>
                <Divider style={{ margin: '16px 0 12px' }} />
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Credit Snapshot</Text>
                    {creditStats ? (
                      <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                        <Descriptions.Item label="Credit Balance">
                          {Number(creditStats.credit_balance ?? 0).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Available Balance">
                          {Number(
                            (typeof creditStats.available_balance === 'number'
                              ? creditStats.available_balance
                              : creditStats.credit_balance) ?? 0
                          ).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pending Holds">
                          {Number(creditStats.pending_credits ?? 0).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Monthly Allocation">
                          {Number(creditStats.monthly_credits_allocated ?? 0).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Next Expiring Batch">
                          {nextExpiringBatch?.expiresAtISO
                            ? `${dayjs(nextExpiringBatch.expiresAtISO).format('MMM D, YYYY h:mm A')} · ${(nextExpiringBatch.remainingCredits ?? 0).toLocaleString()} credits`
                            : '—'}
                        </Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        Credit snapshot is unavailable.
                      </Text>
                    )}
                  </div>
                  <div>
                    <Text strong>Recent Credit Batches</Text>
                    {subscriptionBatchesLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                        <Spin size="small" />
                      </div>
                    ) : recentSubscriptionBatches.length ? (
                      <Table<SubscriptionCreditBatch>
                        columns={webhookSummaryBatchColumns}
                        dataSource={recentSubscriptionBatches}
                        pagination={false}
                        size="small"
                        rowKey={(record) =>
                          record.key ||
                          record.batchId ||
                          record.expiresAtISO ||
                          record.grantedAtISO ||
                          record.planId ||
                          'recent-batch'
                        }
                        style={{ marginTop: 8 }}
                      />
                    ) : (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        No subscription batches returned.
                      </Text>
                    )}
                  </div>
                  <div>
                    <Text strong>Referral Outcome</Text>
                    {referralAdminLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                        <Spin size="small" />
                      </div>
                    ) : referralAdminError ? (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={referralAdminError}
                      />
                    ) : referralAdminInfo ? (
                      <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                        <Descriptions.Item label="Applied Code">
                          {referralAdminInfo.appliedCode || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Code Owner">
                          {referralAdminInfo.appliedCodeOwnerEmail ||
                            referralAdminInfo.appliedCodeOwnerName ||
                            referralAdminInfo.appliedCodeOwnerId ||
                            '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Credits Status">
                          {referralAdminInfo.referralCreditsStatus || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Reward Result">
                          {referralAdminInfo.referralRewardResult || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Reward Checked">
                          {referralAdminInfo.referralRewardChecked ? 'Yes' : 'No'}
                          {referralAdminInfo.referralRewardCheckedAt
                            ? ` (${referralAdminInfo.referralRewardCheckedAt})`
                            : ''}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last Reward Recorded">
                          {referralAdminInfo.lastReferralRewardAt ||
                            referralAdminInfo.referralRewardAt ||
                            '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Invoice ID">
                          {referralAdminInfo.referralInvoiceId || '—'}
                        </Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        No referral relationship recorded.
                      </Text>
                    )}
                  </div>
                </Space>
                {webhookResult.webhook_response && (
                  <div style={{ marginTop: 12 }}>
                    <Text strong>Webhook response</Text>
                    <pre
                      style={{
                        background: 'var(--ant-color-bg-elevated, #1f1f1f)',
                        color: 'var(--ant-color-text, #f5f5f5)',
                        padding: 12,
                        borderRadius: 4,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(webhookResult.webhook_response, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </Space>
        </Modal>
      <Modal
          title="User Books"
          visible={isBookModalVisible}
          onOk={() => setIsBookModalVisible(false)}
          onCancel={() => {
            setIsBookModalVisible(false);
            setSeriesFilter('');
            setBookNameFilter('');
          }}
          width={1200}
          footer={[
            <Button key="close" onClick={() => setIsBookModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <Spin spinning={isGlobalLoading} size="large">
            <Space style={{ marginBottom: 16 }} wrap>
              <Input
                placeholder="Filter by series name"
                value={seriesFilter}
                onChange={(e) => setSeriesFilter(e.target.value)}
                style={{ width: 240 }}
                allowClear
              />
              <Input
                placeholder="Search by book title"
                value={bookNameFilter}
                onChange={(e) => setBookNameFilter(e.target.value)}
                style={{ width: 240 }}
                allowClear
              />
            </Space>
            <Table dataSource={filteredBooks} columns={columns} rowKey="bookID" />
          </Spin>
        </Modal>

      <Modal
          title={`User Transactions - ${userInfo?.email || 'User'}`}
          visible={isTransactionsModalVisible}
          onOk={() => setIsTransactionsModalVisible(false)}
          onCancel={() => setIsTransactionsModalVisible(false)}
          width={1400}
          footer={[
            <Button key="exportFull" onClick={exportFullToCSV} disabled={transactionLoading}>
              Export Full CSV
            </Button>,
            <Button key="close" onClick={() => setIsTransactionsModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <Spin spinning={transactionLoading} size="large">
            <div
              style={{
                marginBottom: '1em',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <RangePicker onChange={handleDateRangeChange} value={dateRange} disabled={transactionLoading} />
              <Select
                placeholder="Filter by Book"
                allowClear
                style={{ width: 220 }}
                value={bookFilter}
                onChange={handleBookFilterChange}
                disabled={transactionLoading}
              >
                {bookOptions.map((opt) => (
                  <Option key={opt.guid} value={opt.guid}>
                    {opt.name}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Language"
                allowClear
                style={{ width: 180 }}
                value={languageFilter}
                onChange={handleLanguageFilterChange}
                disabled={transactionLoading}
              >
                {languageOptions.map((l) => (
                  <Option key={l} value={l}>
                    {l}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Description"
                allowClear
                style={{ width: 250 }}
                value={descriptionFilter}
                onChange={handleDescriptionFilterChange}
                disabled={transactionLoading}
              >
                {descriptionOptions.map((d) => (
                  <Option key={d} value={d}>
                    {d}
                  </Option>
                ))}
              </Select>
              <Button
                icon={<ClearOutlined />}
                onClick={clearAllFilters}
                disabled={transactionLoading || !hasActiveFilters}
                type="default"
              >
                Clear All Filters
              </Button>
            </div>

            <Table
              dataSource={transactions}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (text: string) => new Date(text).toLocaleString(),
                },
                { title: 'Description', dataIndex: 'description', key: 'description' },
                { title: 'Book', dataIndex: 'book', key: 'book' },
                { title: 'Chapter', dataIndex: 'chapter', key: 'chapter' },
                { title: 'Language', dataIndex: 'language', key: 'language' },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  render: (_: string, record: Transaction) => {
                    if (record.type === 'hold') {
                      const label = getTransactionTypeLabel(record);
                      const resolved = HOLD_POSITIVE_STATUSES.has((record.hold_status || 'held').toLowerCase());
                      const tag = (
                        <Tag color={resolved ? '#52c41a' : '#faad14'}>
                          {label}
                        </Tag>
                      );
                      const tooltip = buildHoldTooltip(record);
                      return tooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag;
                    }
                    if (record.type === 'deduction') {
                      return <Tag color="red">Deduction</Tag>;
                    }
                    if (record.type === 'refund' || record.type === 'addition') {
                      return <Tag color="#52c41a">{getTransactionTypeLabel(record)}</Tag>;
                    }
                    return <Tag color="blue">{getTransactionTypeLabel(record)}</Tag>;
                  },
                },
                {
                  title: 'Deducted From',
                  key: 'deducted_from',
                  render: (_: string, record: Transaction) => renderDeductionSourceCell(record),
                },
                {
                  title: 'Credits',
                  dataIndex: 'credits',
                  key: 'credits',
                  render: (_: any, record: Transaction) => {
                    const { value, color } = getCreditDeltaDisplay(record);
                    return (
                      <span style={{ color }}>
                        {value > 0 ? '+' : ''}
                        {value}
                      </span>
                    );
                  },
                },
                { title: 'Credits Before', dataIndex: 'credits_before', key: 'credits_before' },
                { title: 'Credits After', dataIndex: 'credits_after', key: 'credits_after' },
              ]}
              rowKey="id"
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: handlePaginationChange,
                showTotal: (tot, range) => `${range[0]}-${range[1]} of ${tot} transactions`,
              }}
            />

            {transactions.length > 0 && (
              <div style={{ textAlign: 'right', marginTop: '1em' }}>
                <strong>
                  Total Credits on Current Page:
                  <span
                    style={{ color: totalCreditsOnPage < 0 ? 'red' : 'green', marginLeft: '8px' }}
                  >
                    {totalCreditsOnPage > 0 ? '+' : ''}
                    {Math.abs(totalCreditsOnPage)}
                  </span>
                </strong>
              </div>
            )}
          </Spin>
        </Modal>

      <Modal
        title="Delete User Data"
        visible={isDeleteUserModalOpen}
        onCancel={resetDeleteUserModal}
        footer={[
          <Button
            key="preview"
            onClick={handlePreviewDeleteUserData}
            disabled={!deleteConfirmReady || deleteUserSubmitting}
            loading={deleteUserPreviewLoading}
          >
            Preview
          </Button>,
          <Button key="cancel" onClick={resetDeleteUserModal} disabled={deleteUserSubmitting}>
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            onClick={handleDeleteUserData}
            disabled={!deleteConfirmReady}
            loading={deleteUserSubmitting}
          >
            Delete User Data
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="This permanently deletes all data for this user."
            description="Includes uploaded books, translations, audiobooks, exports, usage logs, credit history, and storage files."
          />
          <div>
            <Text strong>Confirm User ID</Text>
            <Input
              placeholder={deleteTargetUserId ? `Type ${deleteTargetUserId}` : 'Select a user first'}
              value={deleteUserConfirmId}
              onChange={(e) => setDeleteUserConfirmId(e.target.value)}
            />
          </div>
          <div>
            <Text strong>Confirm Email</Text>
            <Input
              placeholder={deleteTargetEmail ? `Type ${deleteTargetEmail}` : 'Email required'}
              value={deleteUserConfirmEmail}
              onChange={(e) => setDeleteUserConfirmEmail(e.target.value)}
            />
          </div>
          {deleteUserPreview && (
            <div>
              <Text strong>Preview Summary</Text>
              <pre
                style={{
                  background: 'var(--ant-color-bg-elevated, #1f1f1f)',
                  color: 'var(--ant-color-text, #f5f5f5)',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 260,
                  overflow: 'auto',
                  marginTop: 8,
                }}
              >
                {JSON.stringify(deleteUserPreview, null, 2)}
              </pre>
            </div>
          )}
        </Space>
      </Modal>


      <Modal
          title={selectedBatch ? `Adjust Batch ${selectedBatch.batchId || ''}` : 'Adjust Batch'}
          visible={isBatchAdjustmentModalVisible}
          onCancel={() => setIsBatchAdjustmentModalVisible(false)}
          onOk={submitBatchAdjustment}
          confirmLoading={batchOperationLoading}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <strong>Batch ID:</strong> {selectedBatch?.batchId || '�'}
            </div>
            <div>
              <strong>Remaining:</strong> {(selectedBatch?.remainingCredits ?? 0).toLocaleString()}
            </div>
            <div>
              <strong>Expires:</strong> {selectedBatch?.expiresAtISO ? dayjs(selectedBatch.expiresAtISO).format('MMM D, YYYY h:mm A') : '�'}
            </div>
            <Radio.Group value={batchAdjustmentAction} onChange={(e) => setBatchAdjustmentAction(e.target.value)}>
              <Radio.Button value="add">Add Credits</Radio.Button>
              <Radio.Button value="subtract">Subtract Credits</Radio.Button>
            </Radio.Group>
            <InputNumber
              min={1}
              value={batchAdjustmentAmount}
              onChange={(value) => setBatchAdjustmentAmount(value)}
              style={{ width: '100%' }}
              placeholder="Credits"
            />
            <DatePicker
              showTime
              value={batchAdjustmentNewExpiry}
              onChange={(value) => setBatchAdjustmentNewExpiry(value)}
              style={{ width: '100%' }}
              placeholder="Optional: set new expiration"
            />
            <AutoComplete
              value={batchAdjustmentReason}
              options={foreverCreditReasonOptions}
              onSelect={(value) => setBatchAdjustmentReason(value)}
              onChange={(value) => setBatchAdjustmentReason(value || '')}
              style={{ width: '100%' }}
              filterOption={(inputValue, option) => {
                const optionValue =
                  typeof option?.value === 'string'
                    ? option.value
                    : option?.value != null
                    ? String(option.value)
                    : '';
                return optionValue.toLowerCase().includes(inputValue.toLowerCase());
              }}
            >
              <Input placeholder="Reason" />
            </AutoComplete>
          </Space>
        </Modal>

      <Modal
          title="Create Subscription Batch"
          visible={isCreateBatchModalVisible}
          onCancel={() => setIsCreateBatchModalVisible(false)}
          onOk={submitCreateBatch}
          confirmLoading={batchOperationLoading}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <InputNumber
              min={1}
              value={newBatchAmount}
              onChange={(value) => setNewBatchAmount(value)}
              style={{ width: '100%' }}
              placeholder="Credits to grant"
            />
            <DatePicker
              showTime
              value={newBatchExpiresAt}
              onChange={(value) => setNewBatchExpiresAt(value)}
              style={{ width: '100%' }}
              placeholder="Expiration date"
            />
            <AutoComplete
              value={newBatchReason}
              options={foreverCreditReasonOptions}
              onSelect={(value) => setNewBatchReason(value)}
              onChange={(value) => setNewBatchReason(value || '')}
              style={{ width: '100%' }}
              filterOption={(inputValue, option) => {
                const optionValue =
                  typeof option?.value === 'string'
                    ? option.value
                    : option?.value != null
                    ? String(option.value)
                    : '';
                return optionValue.toLowerCase().includes(inputValue.toLowerCase());
              }}
            >
              <Input placeholder="Reason" />
            </AutoComplete>
            <Input
              placeholder="Plan ID (optional)"
              value={newBatchPlanId}
              onChange={(e) => setNewBatchPlanId(e.target.value)}
            />
            <Input
              placeholder="Source (optional)"
              value={newBatchSource}
              onChange={(e) => setNewBatchSource(e.target.value)}
            />
          </Space>
        </Modal>

        {/* Cover Key Migration Preview Modal */}
        <Modal
          title="Cover Key Migration Preview"
          visible={isCoverMigrationModalVisible}
          onCancel={() => setIsCoverMigrationModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsCoverMigrationModalVisible(false)}>
              Close
            </Button>,
          ]}
          width={900}
        >
          <Spin spinning={coverMigrationLoading}>
            {coverMigrationResult ? (
              <div>
                <p><strong>Processed:</strong> {coverMigrationResult.processed}</p>
                <p><strong>Documents with changes:</strong> {coverMigrationResult.changed}</p>
                {coverMigrationResult.docs && coverMigrationResult.docs.length > 0 ? (
                  <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #eee', padding: 8 }}>
                    {coverMigrationResult.docs.slice(0, 100).map((d, idx) => (
                      <div key={idx} style={{ marginBottom: 12 }}>
                        <div><strong>Doc:</strong> {d.id}</div>
                        {d.set_keys.length > 0 && (
                          <div style={{ fontSize: 12 }}>
                            <strong>Set keys:</strong> {d.set_keys.join(', ')}
                          </div>
                        )}
                        {d.delete_keys.length > 0 && (
                          <div style={{ fontSize: 12 }}>
                            <strong>Delete keys:</strong> {d.delete_keys.join(', ')}
                          </div>
                        )}
                        <hr />
                      </div>
                    ))}
                    {coverMigrationResult.docs.length > 100 && (
                      <div style={{ marginTop: 8 }}>
                        Showing first 100 documents with changes...
                      </div>
                    )}
                  </div>
                ) : (
                  <p>No documents need changes.</p>
                )}
              </div>
            ) : (
              <p>Run the preview to see results.</p>
            )}
          </Spin>
        </Modal>
    </div>
  );
};

export default ManagementTab;
